/**
 * CompanyDetail.js
 * View company details and manage users
 */

import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../AuthContext';
import { Modal } from '../components/SharedComponents';
import { API_BASE } from '../config';
import { CompanyLogo, companyLogos } from '../assets/companyLogos';
import colors from '../utils/adminColors';
import OpportunityStatusLine from '../components/OpportunityStatusLine';
import { SYNTHESIS_STATUS, normalizeSynthesisStatus, SYNTHESIS_COPY } from '../utils/synthesisStatus';
import { setKillSwitch } from '../api/chatCapture';
import AdminCompanyUploads from '../components/AdminCompanyUploads';

/**
 * One-line summary of a POST /api/admin/ppi/fetch-data/{id} response.
 *
 * Exported so it can be tested directly (Lane 962). Before this lane it was an
 * inline arrow that read `0 series fetched - 0 data points` whenever the
 * freshness gate skipped everything: `series_fetched` and `data_points_cached`
 * both passed the `!= null` check with the value 0, `series_skipped_fresh` was
 * absent from the early return so its clause never rendered, and because `parts`
 * was already non-empty the `|| d.message` fallback -- the only thing carrying a
 * REASON -- could never fire. An operator therefore read a green success with
 * two zeros and no explanation, which is why a CORRECT skip was written up as
 * "a complete no-op".
 */
export function summarizePpiFetch(d) {
  const parts = [];
  // When nothing was fetched and nothing is pending, the two zeros are noise
  // that reads like a broken button; the reason IS the content of that outcome.
  const nothingFetched = d.series_fetched === 0 && !(d.series_remaining > 0);
  if (!nothingFetched) {
    if (d.series_fetched != null) parts.push(`${d.series_fetched} series fetched`);
    if (d.data_points_cached != null) parts.push(`${d.data_points_cached} data points`);
  }
  if (d.forced) parts.push('freshness gate overridden');
  if (d.series_skipped_fresh) parts.push(`${d.series_skipped_fresh} already fresh`);
  if (d.series_remaining > 0) parts.push(`${d.series_remaining} remaining. Click again`);
  if (d.frontier_note) parts.push(d.frontier_note);
  return parts.join(' · ') || d.message || 'Done';
}

/**
 * The backend's own sentence for a PPI pipeline step, rendered BENEATH the
 * fragment line instead of as a fallback TO it. Returns null when there is
 * nothing extra to show.
 *
 * Lane 979 -- Finding F1 of the Lane 976 copy register. Every PPI step renderer
 * ends `parts.join(' · ') || d.message`, which makes the server's `message` a
 * FALLBACK: it can only paint when the fragment list comes out empty, and on
 * the live branches it never does. So a sentence the backend composed on
 * purpose was thrown away one line later. Two measured consequences, both
 * OPERATOR-facing, both live on PROD:
 *
 *   - step 2, `fetch-data`: the 30-day freshness gate skips every series while
 *     the cache sits behind the newest month BLS has published, so the backend
 *     composes "... Re-run with Force refresh to fetch anyway."
 *     (`backend/routes/ppi.py:1319-1323`). The operator read the diagnosis and
 *     never the cure.
 *   - step 3, `compute-deviations`: nothing is left to do, so the backend
 *     composes "All materials already computed within the last 24 hours."
 *     (`backend/routes/ppi.py:1549`). `deviations_computed` is 0 and 0 passes
 *     the `!= null` check, so the operator read a bare `0 deviations` -- the
 *     same shape as the `0 series fetched · 0 data points` defect Lane 962
 *     fixed on step 2 and left standing on step 3.
 *
 * DISPLAY ONLY. Every string rendered here is the backend's, verbatim; this
 * function composes no operator wording of its own. It is deliberately generic
 * across the three steps rather than special-cased to the frontier branch, so
 * the compose-then-discard class cannot return on a step or a branch nobody
 * thought about. `generate-mappings` sends no `message` at all
 * (`backend/routes/ppi.py:814-826`), so step 1 renders no detail line.
 */
export function ppiStepDetail(data, summary) {
  const message = data && data.message;
  // Equal means the fragment list WAS empty, `|| d.message` already fired, and
  // the summary line IS the message -- printing it twice helps nobody.
  if (!message || message === summary) return null;
  return message;
}

function CompanyDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { token, user: currentUser, loginWithToken } = useAuth();
  
  const [company, setCompany] = useState(null);
  // Lane 1416: "Open workspace" support session (shown only when the backend
  // says admin Google sign-in and support sessions are on).
  const [supportEnabled, setSupportEnabled] = useState(false);
  const [openingWorkspace, setOpeningWorkspace] = useState(false);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [message, setMessage] = useState(null);
  
  // Edit company modal
  const [showEditModal, setShowEditModal] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [saving, setSaving] = useState(false);
  
  // Add user modal
  const [showUserModal, setShowUserModal] = useState(false);
  const [userForm, setUserForm] = useState({
    email: '',
    password: '',
    full_name: '',
    role: 'user',
    department: '',
    job_title: '',
  });
  const [creatingUser, setCreatingUser] = useState(false);
  const [credentialResult, setCredentialResult] = useState(null);
  const [openActionMenu, setOpenActionMenu] = useState(null);
  const [actionMenuPos, setActionMenuPos] = useState({ top: 0, left: 0 });
  const [confirmDeleteUser, setConfirmDeleteUser] = useState(null);

  // D.6 Chat capture kill-switch toggle — single-state design:
  //   null                                                  → modal closed, pill idle
  //   { direction: 'enable'|'disable', status: 'confirming' } → modal open, buttons live
  //   { direction: 'enable'|'disable', status: 'saving' }     → modal open, PATCH in flight
  //     (pill visually dimmed, Cancel/ESC/backdrop no-op)
  const [chatCaptureAction, setChatCaptureAction] = useState(null);
  const pillRef = useRef(null);
  const chatCaptureWasOpenRef = useRef(false);

  // Core Data state
  const [coreDataStats, setCoreDataStats] = useState({
    materials: { count: 0, last_uploaded_at: null },
    approved_suppliers: { count: 0, last_uploaded_at: null },
    purchase_orders: { count: 0, last_uploaded_at: null },
    forecasts: { count: 0, last_uploaded_at: null },
    contracts: { count: 0, last_uploaded_at: null },
    standard_costs: { count: 0, last_uploaded_at: null },
    taxonomy: { count: 0, last_uploaded_at: null },
  });
  const [uploadingDataType, setUploadingDataType] = useState(null);
  const [uploadResult, setUploadResult] = useState(null);
  // Opportunities briefing run-status for the PO-upload card (Lane 37 step 3 §4).
  // null when the module flag is OFF (endpoint 404s) or no run has occurred → the
  // indicator renders nothing (upload screen unchanged).
  const [oppStatus, setOppStatus] = useState(null);

  // Company Knowledge state
  const [knowledgeDocs, setKnowledgeDocs] = useState([]);
  const [knowledgeLoading, setKnowledgeLoading] = useState(false);
  const [showKnowledgeUpload, setShowKnowledgeUpload] = useState(false);
  const [knowledgeUploading, setKnowledgeUploading] = useState(false);
  const [knowledgeForm, setKnowledgeForm] = useState({
    file: null, title: '', source: '', document_date: '', summary: '', relevance_tags: '',
  });

  // Knowledge Coverage state (WS13C — field coverage + claims)
  const [coverageData, setCoverageData] = useState(null);
  const [coverageLoading, setCoverageLoading] = useState(false);
  const [coverageError, setCoverageError] = useState(null);

  // Interview Activity state
  const [interviewSessions, setInterviewSessions] = useState([]);
  const [interviewsLoading, setInterviewsLoading] = useState(false);
  const [expandedSessionId, setExpandedSessionId] = useState(null);
  const [sessionDetail, setSessionDetail] = useState(null);
  const [sessionDetailLoading, setSessionDetailLoading] = useState(false);
  const [deletingSessionId, setDeletingSessionId] = useState(null);
  const [synthesizingEntity, setSynthesizingEntity] = useState(null);
  const [synthesisResult, setSynthesisResult] = useState(null);
  const [synthesisHistory, setSynthesisHistory] = useState([]);

  // Interview Invites state
  const [invites, setInvites] = useState([]);
  const [invitesLoading, setInvitesLoading] = useState(false);
  const [showCreateInvite, setShowCreateInvite] = useState(false);
  const [createInviteForm, setCreateInviteForm] = useState({ interviewee_name: '', interviewee_email: '', role_hint: '', notes: '', expiration_days: 14 });
  const [creatingInvite, setCreatingInvite] = useState(false);
  const [newInviteResult, setNewInviteResult] = useState(null); // { invite, passcode, link_path }
  const [revokingInviteId, setRevokingInviteId] = useState(null);
  // Invite lifecycle: config, extend modal, delete inline-confirm
  const [inviteConfig, setInviteConfig] = useState({ default_expiry_days: 14, max_expiry_days: 90 });
  const [extendingInvite, setExtendingInvite] = useState(null); // invite object when modal open
  const [extendingDateValue, setExtendingDateValue] = useState(''); // YYYY-MM-DD
  const [extendingInFlight, setExtendingInFlight] = useState(false);
  const [confirmingDeleteId, setConfirmingDeleteId] = useState(null);
  const [deletingInviteId, setDeletingInviteId] = useState(null);

  // Top spend data (from enhanced core-data-stats)
  const [topMaterials, setTopMaterials] = useState([]);
  const [topSuppliers, setTopSuppliers] = useState([]);

  // Company Profile state (A1)
  const [profileData, setProfileData] = useState({});
  const [profileLoading, setProfileLoading] = useState(false);
  const [profileSaving, setProfileSaving] = useState(false);
  const [profileForm, setProfileForm] = useState({});

  // Supplier Groups state (A2)
  const [supplierGroups, setSupplierGroups] = useState([]);
  const [groupsLoading, setGroupsLoading] = useState(false);
  const [expandedGroupId, setExpandedGroupId] = useState(null);
  const [showCreateGroup, setShowCreateGroup] = useState(false);
  const [createGroupForm, setCreateGroupForm] = useState({ group_name: '', description: '' });
  const [creatingGroup, setCreatingGroup] = useState(false);
  const [allSuppliers, setAllSuppliers] = useState([]);
  const [groupSearchTerm, setGroupSearchTerm] = useState('');
  const [assigningSupplier, setAssigningSupplier] = useState(null);

  // Supplier Research state
  const [researchSuppliers, setResearchSuppliers] = useState([]);
  const [researchLoading, setResearchLoading] = useState(false);
  const [researchSummary, setResearchSummary] = useState({ total: 0, researched: 0 });
  const [researchPage, setResearchPage] = useState(1);
  const [researchTotalPages, setResearchTotalPages] = useState(1);
  const [researchingId, setResearchingId] = useState(null);
  const [expandedResearchId, setExpandedResearchId] = useState(null);
  const [expandedResearchData, setExpandedResearchData] = useState(null);
  const [expandedResearchLoading, setExpandedResearchLoading] = useState(false);

  // PPI pipeline state (per-step)
  const [ppiSteps, setPpiSteps] = useState({ 1: {}, 2: {}, 3: {} });
  // Step 2 only: override the 30-day freshness gate. Default OFF -- a normal
  // press must stay the gated one (Lane 962).
  const [ppiForceFetch, setPpiForceFetch] = useState(false);

  // PPI Profiles tab state
  const [ppiSuppliers, setPpiSuppliers] = useState([]);
  const [ppiSummary, setPpiSummary] = useState({ total_with_spend: 0, mapped_count: 0 });
  const [ppiLoading, setPpiLoading] = useState(false);
  const [ppiSearch, setPpiSearch] = useState('');
  const [ppiShowAll, setPpiShowAll] = useState(false);
  const [ppiMappingStatus, setPpiMappingStatus] = useState({}); // {supplierName: {loading, error, done}}
  const [ppiBulk, setPpiBulk] = useState(null); // null | {total, completed, current, failed: [], cancelled}
  const [ppiExpandedSupplier, setPpiExpandedSupplier] = useState(null);
  const [ppiExpandedProfile, setPpiExpandedProfile] = useState(null);
  const [ppiExpandedLoading, setPpiExpandedLoading] = useState(false);
  const [showPpiBulkConfirm, setShowPpiBulkConfirm] = useState(null); // null | 'top20' | 'all'
  const ppiBulkAbortRef = useRef(null);

  // Batch research state
  const [batchStatus, setBatchStatus] = useState(null); // null | {status, total, completed, current_supplier, ...}
  const [batchStarting, setBatchStarting] = useState(false);
  const [showBatchConfirm, setShowBatchConfirm] = useState(false);
  const [cleanupRunning, setCleanupRunning] = useState(false);

  // Demand Context state
  const [demandContexts, setDemandContexts] = useState([]);
  const [demandContextSummary, setDemandContextSummary] = useState({ total_contexts: 0, total_suppliers: 0, total_materials: 0, total_annual_value: 0 });
  const [dcLoading, setDcLoading] = useState(false);
  const [showCreateDc, setShowCreateDc] = useState(false);
  const [dcForm, setDcForm] = useState({ name: '', context_type: '', annual_value: '', status: '', strategic_tier: '', margin_notes: '', strategic_notes: '', key_contacts: '' });
  const [creatingDc, setCreatingDc] = useState(false);
  const [expandedDcId, setExpandedDcId] = useState(null);
  const [expandedDcData, setExpandedDcData] = useState(null);
  const [expandedDcLoading, setExpandedDcLoading] = useState(false);
  const [editingDcId, setEditingDcId] = useState(null);
  const [editDcForm, setEditDcForm] = useState({});
  const [savingDc, setSavingDc] = useState(false);
  const [dcSupplierSearch, setDcSupplierSearch] = useState('');
  const [dcMaterialSearch, setDcMaterialSearch] = useState('');
  const [dcLinkingSupplier, setDcLinkingSupplier] = useState(false);
  const [dcLinkingMaterial, setDcLinkingMaterial] = useState(false);
  const [dcExtracting, setDcExtracting] = useState(false);
  const [dcExtractResult, setDcExtractResult] = useState(null);
  const [dcKnowledgeDocs, setDcKnowledgeDocs] = useState([]);
  const [dcApprovingId, setDcApprovingId] = useState(null);
  const [dcApprovingAll, setDcApprovingAll] = useState(false);
  const [dcIntelNote, setDcIntelNote] = useState('');
  const [dcIntelTags, setDcIntelTags] = useState('');
  const [dcAddingIntel, setDcAddingIntel] = useState(false);
  const [dcDeletingAll, setDcDeletingAll] = useState(false);

  // Intelligence Audit state
  const [auditSummary, setAuditSummary] = useState(null);
  const [auditGaps, setAuditGaps] = useState({ gaps: [], total: 0, page: 1, total_pages: 0 });
  const [auditStatus, setAuditStatus] = useState(null);
  const [auditLoading, setAuditLoading] = useState(false);
  const [auditStarting, setAuditStarting] = useState(false);
  const [auditError, setAuditError] = useState(null);
  const [gapFilters, setGapFilters] = useState({ entity_type: '', priority: '', status: '', gap_category: '', search: '' });
  const [gapPage, setGapPage] = useState(1);
  const [expandedGapId, setExpandedGapId] = useState(null);
  const [updatingGapId, setUpdatingGapId] = useState(null);
  const [allGapsForHeatMap, setAllGapsForHeatMap] = useState([]);
  const [heatMapVisible, setHeatMapVisible] = useState(true);
  const [heatMapSelectedCell, setHeatMapSelectedCell] = useState(null);

  const [newsSourceFilter, setNewsSourceFilter] = useState('news');

  // Compute which document IDs have already been used for extraction (for re-extract labels)
  const extractedDocIds = new Set(
    demandContexts.filter(dc => dc.source_document_id).map(dc => dc.source_document_id)
  );

  useEffect(() => {
    fetchCompanyDetails();
    fetchCoreDataStats();
    fetchOppStatus();
  }, [id, token]);

  useEffect(() => {
    fetch(`${API_BASE}/api/admin/oauth/config`)
      .then((r) => (r.ok ? r.json() : null))
      .then((data) => setSupportEnabled(Boolean(data?.google_enabled)))
      .catch(() => {});
  }, []);

  // Close action menu on click outside (matches AdminUsers.js pattern)
  useEffect(() => {
    const handleClick = () => setOpenActionMenu(null);
    if (openActionMenu) {
      document.addEventListener('click', handleClick);
      return () => document.removeEventListener('click', handleClick);
    }
  }, [openActionMenu]);

  const getAuthHeaders = () => ({
    'Authorization': `Bearer ${token}`,
    'Content-Type': 'application/json'
  });

  const fetchCompanyDetails = async () => {
    try {
      const response = await fetch(`${API_BASE}/api/admin/companies/${id}`, {
        headers: getAuthHeaders()
      });
      
      if (response.ok) {
        const data = await response.json();
        setCompany(data.company);
        setUsers(data.recent_users || []);
        setEditForm({
          company_name: data.company.company_name || '',
          industry: data.company.industry || '',
          contact_email: data.company.contact_email || '',
          contact_phone: data.company.contact_phone || '',
          address: data.company.address || '',
          status: data.company.status || 'active',
          settings: data.company.settings || {},
        });
      } else {
        setMessage({ type: 'error', text: 'Company not found' });
      }
    } catch (error) {
      console.error('Error fetching company:', error);
      setMessage({ type: 'error', text: 'Failed to load company details' });
    } finally {
      setLoading(false);
    }
  };

  const fetchUsers = async () => {
    try {
      const response = await fetch(`${API_BASE}/api/admin/companies/${id}/users`, {
        headers: getAuthHeaders()
      });

      if (response.ok) {
        const data = await response.json();
        setUsers(data.users || []);
      }
    } catch (error) {
      console.error('Error fetching users:', error);
    }
  };

  const fetchProfile = async () => {
    setProfileLoading(true);
    try {
      const response = await fetch(`${API_BASE}/api/admin/companies/${id}/profile`, {
        headers: getAuthHeaders()
      });
      if (response.ok) {
        const data = await response.json();
        setProfileData(data);
        setProfileForm({
          company_type: data.company_type || '',
          industry: data.industry || '',
          annual_revenue_range: data.annual_revenue_range || '',
          employee_count_range: data.employee_count_range || '',
          primary_procurement_model: data.primary_procurement_model || '',
          headquarters_country: data.headquarters_country || '',
          number_of_sites: data.number_of_sites != null ? String(data.number_of_sites) : '',
          company_profile_summary: data.company_profile_summary || '',
          ai_context_notes: data.ai_context_notes || '',
        });
      }
    } catch (error) {
      console.error('Error fetching profile:', error);
    } finally {
      setProfileLoading(false);
    }
  };

  const handleSaveProfile = async () => {
    setProfileSaving(true);
    try {
      const response = await fetch(`${API_BASE}/api/admin/companies/${id}/profile`, {
        method: 'PUT',
        headers: getAuthHeaders(),
        body: JSON.stringify(profileForm),
      });
      if (response.ok) {
        const data = await response.json();
        setProfileData(data.profile || {});
        setMessage({ type: 'success', text: 'Company profile saved successfully' });
      } else {
        const err = await response.json();
        setMessage({ type: 'error', text: err.detail || 'Failed to save profile' });
      }
    } catch (error) {
      setMessage({ type: 'error', text: 'Failed to save profile' });
    } finally {
      setProfileSaving(false);
    }
  };

  // ── Supplier Groups Functions (A2) ──
  const fetchSupplierGroups = async () => {
    setGroupsLoading(true);
    try {
      const response = await fetch(`${API_BASE}/api/admin/supplier-groups?company_id=${id}`, {
        headers: getAuthHeaders()
      });
      if (response.ok) {
        const data = await response.json();
        setSupplierGroups(data.groups || []);
      }
    } catch (error) {
      console.error('Error fetching supplier groups:', error);
    } finally {
      setGroupsLoading(false);
    }
  };

  const fetchAllSuppliers = async () => {
    try {
      const response = await fetch(`${API_BASE}/api/admin/suppliers-list`, {
        headers: getAuthHeaders()
      });
      if (response.ok) {
        const data = await response.json();
        setAllSuppliers(data.suppliers || []);
      }
    } catch (error) {
      console.error('Error fetching suppliers list:', error);
    }
  };

  const handleCreateGroup = async () => {
    if (!createGroupForm.group_name.trim()) {
      setMessage({ type: 'error', text: 'Group name is required' });
      return;
    }
    setCreatingGroup(true);
    try {
      const response = await fetch(`${API_BASE}/api/admin/supplier-groups`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          company_id: parseInt(id),
          group_name: createGroupForm.group_name.trim(),
          description: createGroupForm.description.trim() || null,
        }),
      });
      if (response.ok) {
        setMessage({ type: 'success', text: `Group "${createGroupForm.group_name}" created` });
        setShowCreateGroup(false);
        setCreateGroupForm({ group_name: '', description: '' });
        fetchSupplierGroups();
      } else {
        const err = await response.json();
        setMessage({ type: 'error', text: err.detail || 'Failed to create group' });
      }
    } catch (error) {
      setMessage({ type: 'error', text: 'Failed to create group' });
    } finally {
      setCreatingGroup(false);
    }
  };

  const handleDeleteGroup = async (groupId, groupName) => {
    if (!window.confirm(`Delete group "${groupName}"? All members will be unassigned.`)) return;
    try {
      const response = await fetch(`${API_BASE}/api/admin/supplier-groups/${groupId}`, {
        method: 'DELETE',
        headers: getAuthHeaders(),
      });
      if (response.ok) {
        setMessage({ type: 'success', text: `Group "${groupName}" deleted` });
        fetchSupplierGroups();
      } else {
        const err = await response.json();
        setMessage({ type: 'error', text: err.detail || 'Failed to delete group' });
      }
    } catch (error) {
      setMessage({ type: 'error', text: 'Failed to delete group' });
    }
  };

  const handleAssignSupplier = async (supplierId, groupId) => {
    setAssigningSupplier(supplierId);
    try {
      const response = await fetch(`${API_BASE}/api/admin/suppliers/${supplierId}/group`, {
        method: 'PUT',
        headers: getAuthHeaders(),
        body: JSON.stringify({ group_id: groupId, company_id: parseInt(id) }),
      });
      if (response.ok) {
        fetchSupplierGroups();
        setGroupSearchTerm('');
      } else {
        const err = await response.json();
        setMessage({ type: 'error', text: err.detail || 'Failed to assign supplier' });
      }
    } catch (error) {
      setMessage({ type: 'error', text: 'Failed to assign supplier' });
    } finally {
      setAssigningSupplier(null);
    }
  };

  const handleUnassignSupplier = async (supplierId) => {
    setAssigningSupplier(supplierId);
    try {
      const response = await fetch(`${API_BASE}/api/admin/suppliers/${supplierId}/group`, {
        method: 'PUT',
        headers: getAuthHeaders(),
        body: JSON.stringify({ group_id: null, company_id: parseInt(id) }),
      });
      if (response.ok) {
        fetchSupplierGroups();
      } else {
        const err = await response.json();
        setMessage({ type: 'error', text: err.detail || 'Failed to remove supplier' });
      }
    } catch (error) {
      setMessage({ type: 'error', text: 'Failed to remove supplier' });
    } finally {
      setAssigningSupplier(null);
    }
  };

  // Get supplier IDs already assigned to ANY group (for filtering search results)
  const assignedSupplierIds = new Set(
    supplierGroups.flatMap(g => (g.members || []).map(m => m.supplier_id))
  );

  // Filter suppliers for search — exclude already-assigned suppliers
  const filteredSearchSuppliers = groupSearchTerm.length >= 2
    ? allSuppliers.filter(s =>
        s.supplier_name.toLowerCase().includes(groupSearchTerm.toLowerCase()) &&
        !assignedSupplierIds.has(s.supplier_id)
      ).slice(0, 8)
    : [];

  // ── Intelligence Audit Functions ──
  const fetchAuditData = async (pg = gapPage) => {
    setAuditLoading(true);
    setAuditError(null);
    try {
      const params = new URLSearchParams();
      if (gapFilters.entity_type) params.set('entity_type', gapFilters.entity_type);
      if (gapFilters.priority) params.set('priority', gapFilters.priority);
      if (gapFilters.status) params.set('status', gapFilters.status);
      if (gapFilters.gap_category) params.set('gap_category', gapFilters.gap_category);
      if (gapFilters.search) params.set('search', gapFilters.search);
      params.set('page', pg);
      params.set('page_size', 50);
      const [summaryRes, gapsRes] = await Promise.all([
        fetch(`${API_BASE}/api/admin/intelligence-audit/summary/${id}`, { headers: getAuthHeaders() }),
        fetch(`${API_BASE}/api/admin/intelligence-audit/gaps/${id}?${params}`, { headers: getAuthHeaders() }),
      ]);
      if (summaryRes.ok) setAuditSummary(await summaryRes.json());
      if (gapsRes.ok) setAuditGaps(await gapsRes.json());
      // Fetch all gaps for heat map (unfiltered)
      if (allGapsForHeatMap.length === 0) {
        try {
          const allRes = await fetch(`${API_BASE}/api/admin/intelligence-audit/gaps/${id}?page=1&page_size=9999`, { headers: getAuthHeaders() });
          if (allRes.ok) {
            const allData = await allRes.json();
            setAllGapsForHeatMap(allData.gaps || []);
          }
        } catch {} // Non-critical — heat map just won't render
      }
    } catch (err) {
      setAuditError(err.message);
    } finally {
      setAuditLoading(false);
    }
  };

  const startAudit = async (auditType = 'full') => {
    setAuditStarting(true);
    setAuditError(null);
    try {
      const res = await fetch(`${API_BASE}/api/admin/intelligence-audit/start`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ company_id: parseInt(id), audit_type: auditType }),
      });
      if (res.ok) {
        const data = await res.json();
        setAuditStatus({ status: 'running', total: data.total_entities, completed: 0, current_entity: null, phase: 'starting' });
      } else {
        const err = await res.json();
        setAuditError(err.detail || 'Failed to start audit');
      }
    } catch (err) {
      setAuditError(err.message);
    } finally {
      setAuditStarting(false);
    }
  };

  const updateGapStatus = async (gapId, newStatus) => {
    setUpdatingGapId(gapId);
    try {
      const res = await fetch(`${API_BASE}/api/admin/intelligence-audit/gaps/${gapId}/update-status`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ status: newStatus, company_id: parseInt(id) }),
      });
      if (res.ok) fetchAuditData();
    } catch (err) {
      setAuditError(err.message);
    } finally {
      setUpdatingGapId(null);
    }
  };

  // Poll audit status when running
  useEffect(() => {
    if (activeTab !== 'intelligence' || !auditStatus || auditStatus.status !== 'running') return;
    const interval = setInterval(async () => {
      try {
        const res = await fetch(`${API_BASE}/api/admin/intelligence-audit/status/${id}`, { headers: getAuthHeaders() });
        if (res.ok) {
          const data = await res.json();
          setAuditStatus(data);
          if (data.status === 'completed' || data.status === 'failed') {
            clearInterval(interval);
            fetchAuditData();
          }
        }
      } catch (err) { /* polling error — will retry */ }
    }, 3000);
    return () => clearInterval(interval);
  }, [activeTab, auditStatus?.status]); // eslint-disable-line react-hooks/exhaustive-deps

  // Refetch gaps when filters or page change
  useEffect(() => {
    if (activeTab === 'intelligence' && auditSummary) fetchAuditData(gapPage);
  }, [gapFilters, gapPage]); // eslint-disable-line react-hooks/exhaustive-deps

  const formatGapCategory = (cat) => (cat || '').split('_').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
  const priorityColors = { critical: '#DC2626', high: '#D97706', medium: '#2251FF', low: '#8A94A6' };
  const statusColors = { open: '#D97706', partially_answered: '#2251FF', closed: '#059669' };

  // ── Demand Context Functions ──
  const fetchDemandContexts = async () => {
    setDcLoading(true);
    try {
      const response = await fetch(`${API_BASE}/api/admin/companies/${id}/demand-contexts`, {
        headers: getAuthHeaders()
      });
      if (response.ok) {
        const data = await response.json();
        setDemandContexts(data.contexts || []);
        setDemandContextSummary(data.summary || { total_contexts: 0, total_suppliers: 0, total_materials: 0, total_annual_value: 0 });
      }
    } catch (error) {
      console.error('Error fetching demand contexts:', error);
    } finally {
      setDcLoading(false);
    }
  };

  const fetchDemandContextDetail = async (contextId) => {
    setExpandedDcLoading(true);
    try {
      const response = await fetch(`${API_BASE}/api/admin/companies/${id}/demand-contexts/${contextId}`, {
        headers: getAuthHeaders()
      });
      if (response.ok) {
        const data = await response.json();
        setExpandedDcData(data.context);
      }
    } catch (error) {
      console.error('Error fetching demand context detail:', error);
    } finally {
      setExpandedDcLoading(false);
    }
  };

  const handleCreateDc = async () => {
    if (!dcForm.name.trim()) {
      setMessage({ type: 'error', text: 'Name is required' });
      return;
    }
    setCreatingDc(true);
    try {
      const body = { ...dcForm };
      if (body.annual_value) body.annual_value = parseFloat(body.annual_value) || null;
      else body.annual_value = null;
      const response = await fetch(`${API_BASE}/api/admin/companies/${id}/demand-contexts`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(body),
      });
      if (response.ok) {
        setMessage({ type: 'success', text: `Demand context "${dcForm.name}" created` });
        setShowCreateDc(false);
        setDcForm({ name: '', context_type: '', annual_value: '', status: '', strategic_tier: '', margin_notes: '', strategic_notes: '', key_contacts: '' });
        fetchDemandContexts();
      } else {
        const err = await response.json();
        setMessage({ type: 'error', text: err.detail || 'Failed to create demand context' });
      }
    } catch (error) {
      setMessage({ type: 'error', text: 'Failed to create demand context' });
    } finally {
      setCreatingDc(false);
    }
  };

  const handleUpdateDc = async (contextId) => {
    setSavingDc(true);
    try {
      const body = { ...editDcForm };
      if (body.annual_value !== undefined && body.annual_value !== null && body.annual_value !== '') {
        body.annual_value = parseFloat(body.annual_value) || null;
      } else {
        body.annual_value = null;
      }
      const response = await fetch(`${API_BASE}/api/admin/companies/${id}/demand-contexts/${contextId}`, {
        method: 'PUT',
        headers: getAuthHeaders(),
        body: JSON.stringify(body),
      });
      if (response.ok) {
        setMessage({ type: 'success', text: 'Demand context updated' });
        setEditingDcId(null);
        fetchDemandContexts();
        fetchDemandContextDetail(contextId);
      } else {
        const err = await response.json();
        setMessage({ type: 'error', text: err.detail || 'Failed to update' });
      }
    } catch (error) {
      setMessage({ type: 'error', text: 'Failed to update demand context' });
    } finally {
      setSavingDc(false);
    }
  };

  const handleDeleteDc = async (contextId, name) => {
    if (!window.confirm(`Delete demand context "${name}"? All linked suppliers and materials will be unlinked.`)) return;
    try {
      const response = await fetch(`${API_BASE}/api/admin/companies/${id}/demand-contexts/${contextId}`, {
        method: 'DELETE',
        headers: getAuthHeaders(),
      });
      if (response.ok) {
        setMessage({ type: 'success', text: `"${name}" deleted` });
        setExpandedDcId(null);
        setExpandedDcData(null);
        fetchDemandContexts();
      } else {
        const err = await response.json();
        setMessage({ type: 'error', text: err.detail || 'Failed to delete' });
      }
    } catch (error) {
      setMessage({ type: 'error', text: 'Failed to delete demand context' });
    }
  };

  const handleLinkDcSupplier = async (contextId, supplierId) => {
    setDcLinkingSupplier(true);
    try {
      const response = await fetch(`${API_BASE}/api/admin/demand-contexts/${contextId}/suppliers`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ supplier_id: supplierId }),
      });
      if (response.ok) {
        setDcSupplierSearch('');
        fetchDemandContextDetail(contextId);
        fetchDemandContexts();
      } else {
        const err = await response.json();
        setMessage({ type: 'error', text: err.detail || 'Failed to link supplier' });
      }
    } catch (error) {
      setMessage({ type: 'error', text: 'Failed to link supplier' });
    } finally {
      setDcLinkingSupplier(false);
    }
  };

  const handleUnlinkDcSupplier = async (contextId, supplierId) => {
    try {
      const response = await fetch(`${API_BASE}/api/admin/demand-contexts/${contextId}/suppliers/${supplierId}`, {
        method: 'DELETE',
        headers: getAuthHeaders(),
      });
      if (response.ok) {
        fetchDemandContextDetail(contextId);
        fetchDemandContexts();
      } else {
        const err = await response.json();
        setMessage({ type: 'error', text: err.detail || 'Failed to unlink supplier' });
      }
    } catch (error) {
      setMessage({ type: 'error', text: 'Failed to unlink supplier' });
    }
  };

  const handleLinkDcMaterial = async (contextId, materialNumber) => {
    setDcLinkingMaterial(true);
    try {
      const response = await fetch(`${API_BASE}/api/admin/demand-contexts/${contextId}/materials`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ material_number: materialNumber }),
      });
      if (response.ok) {
        setDcMaterialSearch('');
        fetchDemandContextDetail(contextId);
        fetchDemandContexts();
      } else {
        const err = await response.json();
        setMessage({ type: 'error', text: err.detail || 'Failed to link material' });
      }
    } catch (error) {
      setMessage({ type: 'error', text: 'Failed to link material' });
    } finally {
      setDcLinkingMaterial(false);
    }
  };

  const handleUnlinkDcMaterial = async (contextId, materialNumber) => {
    try {
      const response = await fetch(`${API_BASE}/api/admin/demand-contexts/${contextId}/materials/${encodeURIComponent(materialNumber)}`, {
        method: 'DELETE',
        headers: getAuthHeaders(),
      });
      if (response.ok) {
        fetchDemandContextDetail(contextId);
        fetchDemandContexts();
      } else {
        const err = await response.json();
        setMessage({ type: 'error', text: err.detail || 'Failed to unlink material' });
      }
    } catch (error) {
      setMessage({ type: 'error', text: 'Failed to unlink material' });
    }
  };

  // Supplier search results for demand context linking (uses allSuppliers from supplier groups)
  const dcFilteredSuppliers = dcSupplierSearch.length >= 2
    ? allSuppliers.filter(s =>
        s.supplier_name.toLowerCase().includes(dcSupplierSearch.toLowerCase()) &&
        !(expandedDcData?.suppliers || []).some(linked => linked.supplier_id === s.supplier_id)
      ).slice(0, 8)
    : [];

  const fetchDcKnowledgeDocs = async () => {
    try {
      // Fetch knowledge docs scoped to this company (company_id match OR global docs).
      // For admins, get_company_id_or_admin reads company_id from query params.
      const response = await fetch(`${API_BASE}/api/knowledge/documents?company_id=${id}`, {
        headers: getAuthHeaders()
      });
      if (response.ok) {
        const data = await response.json();
        const docs = Array.isArray(data) ? data : data.documents || [];
        setDcKnowledgeDocs(docs);
      }
    } catch (error) {
      console.error('Error fetching knowledge docs for extraction:', error);
    }
  };

  const handleExtractDc = async (documentId, docTitle) => {
    if (!window.confirm(`This will use AI credits to analyze "${docTitle}". Continue?`)) return;
    setDcExtracting(true);
    setDcExtractResult(null);
    try {
      const response = await fetch(`${API_BASE}/api/admin/companies/${id}/demand-contexts/extract`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ document_id: documentId }),
      });
      const data = await response.json();
      if (response.ok && data.status === 'started') {
        // Start polling for status
        const pollInterval = setInterval(async () => {
          try {
            const statusRes = await fetch(`${API_BASE}/api/admin/companies/${id}/demand-contexts/extract-status`, {
              headers: getAuthHeaders(),
            });
            const statusData = await statusRes.json();
            if (statusData.status === 'complete') {
              clearInterval(pollInterval);
              setDcExtracting(false);
              setDcExtractResult(statusData);
              setMessage({ type: 'success', text: `Extracted ${statusData.created_count} demand contexts${statusData.skipped_count > 0 ? ` (${statusData.skipped_count} skipped, already exist)` : ''}. Review and approve below.` });
              fetchDemandContexts();
            } else if (statusData.status === 'error') {
              clearInterval(pollInterval);
              setDcExtracting(false);
              // Truncation is a distinct, well-understood condition with a clear
              // remediation -- render as amber 'warning' instead of red 'error'
              // so the operator immediately distinguishes "model ran out of room"
              // from "something blew up unexpectedly". output_tokens is templated
              // dynamically from statusData so the message reflects whatever the
              // current ceiling produced, not a hardcoded value.
              const isTruncation = statusData.error_kind === 'truncation';
              setMessage({
                type: isTruncation ? 'warning' : 'error',
                text: isTruncation
                  ? `Extraction was cut off -- the AI hit its output budget (${statusData.output_tokens} tokens) before finishing. No data was inserted. Retry after the document or token ceiling is adjusted.`
                  : (statusData.error_message || 'Extraction failed'),
              });
            }
            // status === 'running' → keep polling
          } catch {
            clearInterval(pollInterval);
            setDcExtracting(false);
            setMessage({ type: 'error', text: 'Lost connection while checking extraction status' });
          }
        }, 5000);
      } else if (response.status === 409) {
        setMessage({ type: 'error', text: data.detail || 'Extraction already in progress' });
        setDcExtracting(false);
      } else {
        setMessage({ type: 'error', text: data.detail || 'Failed to start extraction' });
        setDcExtracting(false);
      }
    } catch (error) {
      setMessage({ type: 'error', text: 'Extraction failed. Check your connection' });
      setDcExtracting(false);
    }
  };

  const handleApproveDc = async (contextId, name) => {
    setDcApprovingId(contextId);
    try {
      const response = await fetch(`${API_BASE}/api/admin/companies/${id}/demand-contexts/${contextId}/approve`, {
        method: 'PUT',
        headers: getAuthHeaders(),
      });
      if (response.ok) {
        setMessage({ type: 'success', text: `"${name}" approved` });
        fetchDemandContexts();
        if (expandedDcId === contextId) fetchDemandContextDetail(contextId);
      } else {
        const err = await response.json();
        setMessage({ type: 'error', text: err.detail || 'Failed to approve' });
      }
    } catch (error) {
      setMessage({ type: 'error', text: 'Failed to approve' });
    } finally {
      setDcApprovingId(null);
    }
  };

  const handleApproveAllDc = async () => {
    if (!window.confirm('Approve all pending demand contexts?')) return;
    setDcApprovingAll(true);
    try {
      const response = await fetch(`${API_BASE}/api/admin/companies/${id}/demand-contexts/approve-all`, {
        method: 'PUT',
        headers: getAuthHeaders(),
      });
      if (response.ok) {
        const data = await response.json();
        setMessage({ type: 'success', text: `${data.approved_count} demand contexts approved` });
        fetchDemandContexts();
      } else {
        const err = await response.json();
        const detail = typeof err.detail === 'string' ? err.detail : 'Failed to approve';
        setMessage({ type: 'error', text: detail });
      }
    } catch (error) {
      setMessage({ type: 'error', text: 'Failed to approve all' });
    } finally {
      setDcApprovingAll(false);
    }
  };

  const handleAddDcIntelligence = async (contextId) => {
    if (!dcIntelNote.trim()) return;
    setDcAddingIntel(true);
    try {
      const response = await fetch(`${API_BASE}/api/admin/demand-contexts/${contextId}/intelligence`, {
        method: 'POST',
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ content: dcIntelNote, tags: dcIntelTags }),
      });
      if (response.ok) {
        setDcIntelNote('');
        setDcIntelTags('');
        fetchDemandContextDetail(contextId);
      } else {
        const err = await response.json();
        setMessage({ type: 'error', text: err.detail || 'Failed to add intelligence entry' });
      }
    } catch (error) {
      setMessage({ type: 'error', text: 'Failed to add intelligence entry' });
    } finally {
      setDcAddingIntel(false);
    }
  };

  const handleDeleteDcIntelligence = async (contextId, entryId) => {
    try {
      const response = await fetch(`${API_BASE}/api/admin/demand-contexts/${contextId}/intelligence/${entryId}`, {
        method: 'DELETE',
        headers: getAuthHeaders(),
      });
      if (response.ok) {
        fetchDemandContextDetail(contextId);
      }
    } catch (error) {
      setMessage({ type: 'error', text: 'Failed to delete intelligence entry' });
    }
  };

  const handleDeleteAllDc = async () => {
    if (!window.confirm(`Delete ALL demand contexts for this company? This removes all contexts, intelligence entries, and linked suppliers/materials. This cannot be undone.`)) return;
    setDcDeletingAll(true);
    try {
      const response = await fetch(`${API_BASE}/api/admin/companies/${id}/demand-contexts/delete-all`, {
        method: 'DELETE',
        headers: getAuthHeaders(),
      });
      if (response.ok) {
        const data = await response.json();
        setMessage({ type: 'success', text: data.message || 'All demand contexts deleted' });
        fetchDemandContexts();
        setExpandedDcId(null);
        setExpandedDcData(null);
      } else {
        const err = await response.json();
        setMessage({ type: 'error', text: err.detail || 'Failed to delete' });
      }
    } catch (error) {
      setMessage({ type: 'error', text: 'Failed to delete all demand contexts' });
    } finally {
      setDcDeletingAll(false);
    }
  };

  const pendingDcCount = demandContexts.filter(dc => dc.record_status === 'pending_review').length;

  // ── Supplier Research Functions ──
  const fetchResearchStatus = async (pg = 1) => {
    setResearchLoading(true);
    try {
      const response = await fetch(`${API_BASE}/api/admin/companies/${id}/suppliers/research-status?page=${pg}&page_size=50`, {
        headers: getAuthHeaders(),
      });
      if (response.ok) {
        const data = await response.json();
        setResearchSuppliers(data.suppliers || []);
        setResearchSummary(data.summary || { total: 0, researched: 0 });
        setResearchPage(data.pagination?.page || 1);
        setResearchTotalPages(data.pagination?.total_pages || 1);
      }
    } catch (error) {
      setMessage({ type: 'error', text: 'Failed to load supplier research status' });
    } finally {
      setResearchLoading(false);
    }
  };

  // ── PPI Profiles tab data ──
  const fetchPpiStatus = async () => {
    setPpiLoading(true);
    try {
      const response = await fetch(`${API_BASE}/api/admin/companies/${id}/suppliers/ppi-status`, {
        headers: getAuthHeaders(),
      });
      if (response.ok) {
        const data = await response.json();
        setPpiSuppliers(data.suppliers || []);
        setPpiSummary(data.summary || { total_with_spend: 0, mapped_count: 0 });
      }
    } catch (error) {
      setMessage({ type: 'error', text: 'Failed to load PPI status' });
    } finally {
      setPpiLoading(false);
    }
  };

  const handleMapPpi = async (supplierName) => {
    setPpiMappingStatus(prev => ({ ...prev, [supplierName]: { loading: true } }));
    try {
      const res = await fetch(`${API_BASE}/api/admin/suppliers/${encodeURIComponent(supplierName)}/map-ppi-profile?company_id=${id}`, {
        method: 'POST',
        headers: getAuthHeaders(),
      });
      const data = await res.json();
      if (res.ok && data.success) {
        setPpiMappingStatus(prev => ({ ...prev, [supplierName]: { done: true } }));
        // Check before updating list so we don't read stale closure
        const wasMapped = ppiSuppliers.find(s => s.supplier_name === supplierName)?.series_count > 0;
        setPpiSuppliers(prev => prev.map(s =>
          s.supplier_name === supplierName
            ? { ...s, series_count: data.profile?.length || 1, mapped_at: new Date().toISOString() }
            : s
        ));
        if (!wasMapped) {
          setPpiSummary(prev => ({ ...prev, mapped_count: prev.mapped_count + 1 }));
        }
      } else {
        setPpiMappingStatus(prev => ({ ...prev, [supplierName]: { error: data.detail || data.error || 'Failed' } }));
      }
    } catch (err) {
      setPpiMappingStatus(prev => ({ ...prev, [supplierName]: { error: err.message } }));
    }
  };

  const handleBulkMapPpi = async (mode) => {
    setShowPpiBulkConfirm(null);
    const unmapped = ppiSuppliers
      .filter(s => s.annual_spend > 0 && s.series_count === 0)
      .sort((a, b) => b.annual_spend - a.annual_spend);
    const targets = mode === 'top20' ? unmapped.slice(0, 20) : unmapped;
    if (targets.length === 0) {
      setMessage({ type: 'info', text: 'No unmapped suppliers with spend to map.' });
      return;
    }
    const abortController = new AbortController();
    ppiBulkAbortRef.current = abortController;
    const failed = [];
    setPpiBulk({ total: targets.length, completed: 0, current: targets[0].supplier_name, failed: [], cancelled: false });
    for (let i = 0; i < targets.length; i++) {
      if (abortController.signal.aborted) {
        setPpiBulk(prev => prev ? { ...prev, cancelled: true, current: null } : prev);
        break;
      }
      const sname = targets[i].supplier_name;
      setPpiBulk(prev => prev ? { ...prev, current: sname, completed: i } : prev);
      setPpiMappingStatus(prev => ({ ...prev, [sname]: { loading: true } }));
      try {
        const res = await fetch(`${API_BASE}/api/admin/suppliers/${encodeURIComponent(sname)}/map-ppi-profile?company_id=${id}`, {
          method: 'POST',
          headers: getAuthHeaders(),
          signal: abortController.signal,
        });
        const data = await res.json();
        if (res.ok && data.success) {
          setPpiMappingStatus(prev => ({ ...prev, [sname]: { done: true } }));
          setPpiSuppliers(prev => prev.map(s =>
            s.supplier_name === sname
              ? { ...s, series_count: data.profile?.length || 1, mapped_at: new Date().toISOString() }
              : s
          ));
          setPpiSummary(prev => ({ ...prev, mapped_count: prev.mapped_count + 1 }));
        } else {
          failed.push(sname);
          setPpiMappingStatus(prev => ({ ...prev, [sname]: { error: data.detail || 'Failed' } }));
        }
      } catch (err) {
        if (err.name === 'AbortError') {
          setPpiBulk(prev => prev ? { ...prev, cancelled: true, current: null } : prev);
          break;
        }
        failed.push(sname);
        setPpiMappingStatus(prev => ({ ...prev, [sname]: { error: err.message } }));
      }
    }
    ppiBulkAbortRef.current = null;
    setPpiBulk(prev => prev ? { ...prev, completed: prev.cancelled ? prev.completed : targets.length, current: null, failed } : null);
  };

  const handleCancelBulkPpi = () => {
    if (ppiBulkAbortRef.current) {
      ppiBulkAbortRef.current.abort();
    }
  };

  const fetchPpiProfile = async (supplierName) => {
    if (ppiExpandedSupplier === supplierName) {
      setPpiExpandedSupplier(null);
      setPpiExpandedProfile(null);
      return;
    }
    setPpiExpandedSupplier(supplierName);
    setPpiExpandedLoading(true);
    try {
      const res = await fetch(`${API_BASE}/api/admin/suppliers/${encodeURIComponent(supplierName)}/ppi-profile?company_id=${id}`, {
        headers: getAuthHeaders(),
      });
      if (res.ok) {
        const data = await res.json();
        setPpiExpandedProfile(data);
      }
    } catch (err) {
      setPpiExpandedProfile(null);
    } finally {
      setPpiExpandedLoading(false);
    }
  };

  const handleTriggerResearch = async (supplierId) => {
    setResearchingId(supplierId);
    setMessage(null);
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 180000); // 3-minute timeout
    try {
      const response = await fetch(`${API_BASE}/api/admin/suppliers/${supplierId}/research`, {
        method: 'POST',
        headers: getAuthHeaders(),
        signal: controller.signal,
      });
      clearTimeout(timeoutId);
      if (response.ok) {
        const data = await response.json();
        // Update the row inline
        setResearchSuppliers(prev => prev.map(s =>
          s.supplier_id === supplierId ? {
            ...s,
            research_summary: data.research_summary,
            research_date: data.research_date,
            research_confidence: data.research_confidence,
          } : s
        ));
        setResearchSummary(prev => ({
          ...prev,
          researched: prev.researched + (researchSuppliers.find(s => s.supplier_id === supplierId)?.research_date ? 0 : 1),
        }));
        setMessage({ type: 'success', text: `Research complete for ${data.supplier_name}` });
      } else {
        const err = await response.json();
        setMessage({ type: 'error', text: err.detail || 'Research failed' });
      }
    } catch (error) {
      clearTimeout(timeoutId);
      if (error.name === 'AbortError') {
        setMessage({ type: 'error', text: 'Research may still be completing. Refresh the page in a minute to check' });
      } else {
        setMessage({ type: 'error', text: 'Research request failed. Check network connection' });
      }
    } finally {
      setResearchingId(null);
    }
  };

  const handleExpandResearch = async (supplierId) => {
    if (expandedResearchId === supplierId) {
      setExpandedResearchId(null);
      setExpandedResearchData(null);
      return;
    }
    setExpandedResearchId(supplierId);
    setExpandedResearchData(null);
    setExpandedResearchLoading(true);
    try {
      const response = await fetch(`${API_BASE}/api/admin/suppliers/${supplierId}/research`, {
        headers: getAuthHeaders(),
      });
      if (response.ok) {
        const data = await response.json();
        setExpandedResearchData(data.research_data);
      }
    } catch (error) {
      setMessage({ type: 'error', text: 'Failed to load research details' });
    } finally {
      setExpandedResearchLoading(false);
    }
  };

  // ── Batch Research Functions ──
  const fetchBatchStatus = async () => {
    try {
      const response = await fetch(`${API_BASE}/api/admin/companies/${id}/suppliers/research-batch-status`, {
        headers: getAuthHeaders(),
      });
      if (response.ok) {
        const data = await response.json();
        setBatchStatus(data);
        return data;
      }
    } catch (error) {
      // Silently fail — polling shouldn't show errors
    }
    return null;
  };

  const handleStartBatch = async (mode) => {
    setBatchStarting(true);
    setShowBatchConfirm(false);
    setMessage(null);
    try {
      const response = await fetch(`${API_BASE}/api/admin/companies/${id}/suppliers/research-batch`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ mode, force_reresearch: false }),
      });
      if (response.ok) {
        const data = await response.json();
        if (data.status === 'started') {
          setMessage({ type: 'success', text: data.message });
          // Immediately fetch status to start showing progress
          fetchBatchStatus();
        } else if (data.status === 'already_running') {
          setMessage({ type: 'success', text: data.message });
          fetchBatchStatus();
        } else if (data.status === 'nothing_to_do') {
          setMessage({ type: 'success', text: data.message });
        }
      } else {
        const err = await response.json();
        setMessage({ type: 'error', text: err.detail || 'Failed to start batch research' });
      }
    } catch (error) {
      setMessage({ type: 'error', text: 'Failed to start batch research. Check network connection' });
    } finally {
      setBatchStarting(false);
    }
  };

  const handleCleanupResearch = async () => {
    setCleanupRunning(true);
    setMessage(null);
    try {
      const response = await fetch(`${API_BASE}/api/admin/companies/${id}/suppliers/research-cleanup`, {
        method: 'POST',
        headers: getAuthHeaders(),
      });
      if (response.ok) {
        const data = await response.json();
        setMessage({ type: 'success', text: data.message });
        fetchResearchStatus(researchPage);
      } else {
        const err = await response.json();
        setMessage({ type: 'error', text: err.detail || 'Cleanup failed' });
      }
    } catch (error) {
      setMessage({ type: 'error', text: 'Cleanup request failed. Check network connection' });
    } finally {
      setCleanupRunning(false);
    }
  };

  // Poll batch status every 10 seconds while running, and refresh supplier list every 30s
  useEffect(() => {
    if (activeTab !== 'supplier_research') return;

    // Check batch status on tab load
    fetchBatchStatus();

    const statusInterval = setInterval(() => {
      fetchBatchStatus();
    }, 10000);

    return () => clearInterval(statusInterval);
  }, [activeTab, id, token]); // eslint-disable-line react-hooks/exhaustive-deps

  // Refresh supplier list every 30s while a batch is running
  useEffect(() => {
    if (!batchStatus || batchStatus.status !== 'running') return;

    const refreshInterval = setInterval(() => {
      fetchResearchStatus(researchPage);
    }, 30000);

    return () => clearInterval(refreshInterval);
  }, [batchStatus?.status, researchPage]); // eslint-disable-line react-hooks/exhaustive-deps

  const isBatchRunning = batchStatus && batchStatus.status === 'running';
  const unresearchedCount = researchSummary.total - researchSummary.researched;
  const top100Remaining = Math.min(100, unresearchedCount);

  const handleUpdateCompany = async (e) => {
    e.preventDefault();
    setSaving(true);
    
    try {
      const response = await fetch(`${API_BASE}/api/admin/companies/${id}`, {
        method: 'PUT',
        headers: getAuthHeaders(),
        body: JSON.stringify(editForm),
      });

      if (response.ok) {
        const data = await response.json();
        setCompany(data.company);
        setShowEditModal(false);
        setMessage({ type: 'success', text: 'Company updated successfully' });
      } else {
        const error = await response.json();
        setMessage({ type: 'error', text: error.detail || 'Update failed' });
      }
    } catch (error) {
      setMessage({ type: 'error', text: 'Failed to update company' });
    } finally {
      setSaving(false);
    }
  };

  const handleCreateUser = async (e) => {
    e.preventDefault();
    
    if (!userForm.email || !userForm.password || !userForm.full_name) {
      setMessage({ type: 'error', text: 'Email, password, and name are required' });
      return;
    }

    setCreatingUser(true);
    
    try {
      const response = await fetch(`${API_BASE}/api/admin/companies/${id}/users`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(userForm),
      });

      const data = await response.json();

      if (response.ok) {
        setCredentialResult({ email: userForm.email, password: userForm.password });
        setShowUserModal(false);
        resetUserForm();
        fetchUsers();
      } else {
        // Handle validation errors from FastAPI
        let errorMsg = 'Failed to create user';
        if (data.detail) {
          if (typeof data.detail === 'string') {
            errorMsg = data.detail;
          } else if (Array.isArray(data.detail)) {
            // FastAPI validation errors are arrays
            errorMsg = data.detail.map(err => err.msg || err.message || JSON.stringify(err)).join(', ');
          } else if (typeof data.detail === 'object') {
            errorMsg = data.detail.msg || data.detail.message || JSON.stringify(data.detail);
          }
        }
        setMessage({ type: 'error', text: errorMsg });
      }
    } catch (error) {
      setMessage({ type: 'error', text: 'Failed to create user' });
    } finally {
      setCreatingUser(false);
    }
  };

  const toggleUserStatus = async (userId, currentStatus) => {
    const newStatus = currentStatus === 'active' ? 'inactive' : 'active';
    try {
      const response = await fetch(`${API_BASE}/api/admin/users/company/${userId}/status`, {
        method: 'PATCH',
        headers: getAuthHeaders(),
        body: JSON.stringify({ status: newStatus }),
      });
      const data = await response.json();
      if (data.success) {
        setMessage({ type: 'success', text: `User ${newStatus === 'active' ? 'activated' : 'deactivated'}` });
        fetchUsers();
      } else {
        setMessage({ type: 'error', text: data.detail || 'Failed to update status' });
      }
    } catch (error) {
      setMessage({ type: 'error', text: 'Failed to update status' });
    }
  };

  const handleResetPassword = async (userId, userEmail) => {
    try {
      const response = await fetch(`${API_BASE}/api/admin/users/company/${userId}/reset-password`, {
        method: 'POST',
        headers: getAuthHeaders(),
        // Lane 1426: the server makes a random one-time password and returns it once.
        body: JSON.stringify({}),
      });
      const data = await response.json();
      if (data.success && data.new_password) {
        setCredentialResult({ email: data.email || userEmail, password: data.new_password });
      } else {
        setMessage({ type: 'error', text: data.detail || 'Failed to reset password' });
      }
    } catch (error) {
      setMessage({ type: 'error', text: 'Failed to reset password' });
    }
  };

  const handleDeleteUser = async (userId) => {
    try {
      const response = await fetch(`${API_BASE}/api/admin/users/company/${userId}`, {
        method: 'DELETE',
        headers: getAuthHeaders(),
      });
      const data = await response.json();
      if (data.success) {
        setMessage({ type: 'success', text: data.message || 'User permanently deleted' });
        fetchUsers();
      } else {
        setMessage({ type: 'error', text: data.detail || 'Failed to delete user' });
      }
    } catch (error) {
      setMessage({ type: 'error', text: 'Failed to delete user' });
    }
    setConfirmDeleteUser(null);
  };

  // D.6 Chat capture kill-switch handlers.
  //
  //   idle ──click pill──▶ modal:confirming ──Confirm──▶ modal:saving ──┬─200──▶ idle (company updated, success banner)
  //         (guarded by                                                 ├─404──▶ idle (company unchanged, refresh banner)
  //          !chatCaptureAction)                                        └─5xx/net─▶ idle (company unchanged, generic banner)
  //
  //   Cancel / ESC / backdrop from modal:confirming ─▶ idle (no PATCH)
  //   Cancel / ESC / backdrop from modal:saving      ─▶ no-op (buttons disabled)
  //
  // company.chat_capture_enabled is the single source of truth for pill state;
  // we never optimistically flip it, so "revert on error" is automatic (no-op).
  const openChatCaptureModal = (nextValue) => {
    if (chatCaptureAction !== null) return;
    setChatCaptureAction({ direction: nextValue ? 'enable' : 'disable', status: 'confirming' });
  };

  const closeChatCaptureModal = () => {
    if (chatCaptureAction?.status === 'saving') return;
    setChatCaptureAction(null);
  };

  const confirmChatCaptureToggle = async () => {
    if (!chatCaptureAction || chatCaptureAction.status !== 'confirming') return;
    const nextValue = chatCaptureAction.direction === 'enable';
    setChatCaptureAction({ ...chatCaptureAction, status: 'saving' });
    try {
      const result = await setKillSwitch(id, nextValue);
      setCompany((c) => (c ? { ...c, chat_capture_enabled: Boolean(result?.chat_capture_enabled) } : c));
      setMessage({
        type: 'success',
        text: `Chat capture ${nextValue ? 'enabled' : 'disabled'} for ${company?.company_name || 'company'}.`,
      });
    } catch (err) {
      console.error(
        `[ADMIN-CAPTURE-TOGGLE] company=${id} next=${nextValue} status=${err?.status} detail=${err?.detail}`,
      );
      const text = err?.status === 404
        ? 'This company was deleted. Refresh the page.'
        : 'Failed to update chat capture. Check backend logs for details.';
      setMessage({ type: 'error', text });
    } finally {
      setChatCaptureAction(null);
    }
  };

  // ESC-to-close + focus restoration. Focus returns to the pill whenever the
  // modal transitions from any open state back to null — covers Cancel, ESC,
  // backdrop, success, and error paths uniformly. wasOpenRef ensures we don't
  // steal focus on initial mount (modal starts null).
  useEffect(() => {
    if (!chatCaptureAction) {
      if (chatCaptureWasOpenRef.current) {
        chatCaptureWasOpenRef.current = false;
        pillRef.current?.focus();
      }
      return undefined;
    }
    chatCaptureWasOpenRef.current = true;
    const onKey = (e) => {
      if (e.key === 'Escape' && chatCaptureAction.status !== 'saving') {
        setChatCaptureAction(null);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [chatCaptureAction]);

  const resetUserForm = () => {
    setUserForm({
      email: '',
      password: '',
      full_name: '',
      role: 'user',
      department: '',
      job_title: '',
    });
  };

  // ── Core Data Functions ──
  const fetchCoreDataStats = async () => {
    try {
      const conn = await fetch(`${API_BASE}/api/admin/companies/` + id + '/core-data-stats', {
        headers: getAuthHeaders()
      });
      if (conn.ok) {
        const data = await conn.json();
        setCoreDataStats(data.stats || coreDataStats);
        setTopMaterials(data.top_materials || []);
        setTopSuppliers(data.top_suppliers || []);
      }
    } catch (error) {
      // Stats endpoint may not exist yet — use fallback counts
      console.log('Core data stats not available yet, using defaults');
    }
  };

  // Opportunities briefing run-status (Lane 37 step 3 §4). Best-effort + silent:
  // a 404 means the module flag is OFF → clear the indicator (upload screen
  // unchanged); 'none' (no run yet) also renders nothing.
  const fetchOppStatus = async () => {
    try {
      const resp = await fetch(
        `${API_BASE}/api/admin/companies/${id}/opportunities-status`,
        { headers: getAuthHeaders() });
      if (resp.status === 404) { setOppStatus(null); return null; }
      if (resp.ok) {
        const data = await resp.json();
        const next = data && data.state && data.state !== 'none' ? data : null;
        setOppStatus(next);
        return next;
      }
    } catch (error) {
      // Indicator is best-effort; never surface an error over a working upload.
    }
    return null;
  };

  // After a PO upload the Option-A hook runs synchronously, so the first fetch
  // usually already shows the final state; poll a few times defensively in case a
  // generation is somehow still in flight.
  const pollOppStatusAfterUpload = async () => {
    for (let i = 0; i < 4; i++) {
      const data = await fetchOppStatus();
      if (!data || data.state !== 'in_progress') return;
      await new Promise((r) => setTimeout(r, 1500));
    }
  };

  const fetchCoverage = async () => {
    setCoverageLoading(true);
    setCoverageError(null);
    try {
      const response = await fetch(
        `${API_BASE}/api/admin/knowledge-coverage?company_id=${id}`,
        { headers: getAuthHeaders() }
      );
      if (!response.ok) throw new Error(`Failed to load coverage data (${response.status})`);
      const result = await response.json();
      setCoverageData(result);
    } catch (err) {
      console.error('Error fetching knowledge coverage:', err);
      setCoverageError(err.message);
    } finally {
      setCoverageLoading(false);
    }
  };

  const fetchInterviews = async () => {
    setInterviewsLoading(true);
    try {
      const response = await fetch(
        `${API_BASE}/api/admin/companies/${id}/interviews`,
        { headers: getAuthHeaders() }
      );
      if (response.ok) {
        const data = await response.json();
        setInterviewSessions(data.sessions || []);
        setSynthesisHistory(data.synthesis_history || []);
      }
    } catch (error) {
      console.error('Error fetching interviews:', error);
    } finally {
      setInterviewsLoading(false);
    }
  };

  const toggleSessionDetail = async (sessionId) => {
    if (expandedSessionId === sessionId) {
      setExpandedSessionId(null);
      setSessionDetail(null);
      return;
    }
    setExpandedSessionId(sessionId);
    setSessionDetail(null);
    setSessionDetailLoading(true);
    try {
      const res = await fetch(`${API_BASE}/api/admin/interview-sessions/${sessionId}/detail`, { headers: getAuthHeaders() });
      if (res.ok) {
        const data = await res.json();
        setSessionDetail(data);
      }
    } catch (err) {
      console.error('Error fetching session detail:', err);
    } finally {
      setSessionDetailLoading(false);
    }
  };

  const deleteSession = async (sessionId) => {
    if (!window.confirm('This will delete the interview session and all intelligence extracted from it (assertions, synthesis results). This cannot be undone. Continue?')) return;
    setDeletingSessionId(sessionId);
    try {
      const res = await fetch(`${API_BASE}/api/admin/interview-sessions/${sessionId}`, {
        method: 'DELETE',
        headers: getAuthHeaders(),
      });
      if (res.ok) {
        if (expandedSessionId === sessionId) { setExpandedSessionId(null); setSessionDetail(null); }
        fetchInterviews();
      } else {
        const err = await res.json().catch(() => ({}));
        alert(err.detail || 'Failed to delete session');
      }
    } catch (err) {
      alert('Error deleting session: ' + err.message);
    } finally {
      setDeletingSessionId(null);
    }
  };

  // ── Cross-Interview Synthesis ──────────────────────────────────
  const entityGroupedSessions = useMemo(() => {
    const groups = {};
    const completed = interviewSessions.filter(s => s.status === 'completed' && s.focus_areas);
    for (const sess of completed) {
      let areas = [];
      try {
        const raw = typeof sess.focus_areas === 'string' ? JSON.parse(sess.focus_areas) : sess.focus_areas;
        if (Array.isArray(raw)) {
          // Old flat format: [{entity_type, entity_id, entity_name}, ...]
          areas = raw;
        } else if (raw && typeof raw === 'object') {
          // New dict format: {categories: [{id, name}], suppliers: [{id, name}], accounts: [{id, name}]}
          const typeMap = { categories: 'category', suppliers: 'supplier', accounts: 'account' };
          for (const [entityType, items] of Object.entries(raw)) {
            if (!Array.isArray(items)) continue;
            for (const item of items) {
              if (!item.id && !item.entity_id) continue;
              areas.push({
                entity_type: item.entity_type || typeMap[entityType] || entityType,
                entity_id: item.entity_id || item.id,
                entity_name: item.entity_name || item.name || 'Unknown',
              });
            }
          }
        }
      } catch { /* malformed JSON — skip */ }
      for (const area of areas) {
        if (!area.entity_type || !area.entity_id) continue;
        const key = `${area.entity_type}:${area.entity_id}`;
        if (!groups[key]) {
          groups[key] = { entity_type: area.entity_type, entity_id: area.entity_id, entity_name: area.entity_name || 'Unknown', sessions: [] };
        }
        groups[key].sessions.push(sess);
      }
    }
    return Object.values(groups)
      .sort((a, b) => b.sessions.length - a.sessions.length || a.entity_name.localeCompare(b.entity_name));
  }, [interviewSessions]);

  const handleCrossSynthesis = async (entityType, entityId) => {
    setSynthesizingEntity({ entity_type: entityType, entity_id: entityId });
    setSynthesisResult(null);
    try {
      const res = await fetch(`${API_BASE}/api/admin/companies/${id}/cross-synthesis`, {
        method: 'POST',
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ entity_type: entityType, entity_id: entityId }),
      });
      const data = await res.json();
      if (res.ok && data.success) {
        setSynthesisResult({ success: true, message: `Synthesized ${data.sessions_synthesized} interviews for ${data.entity_name}. Fields updated.`, summary: data.synthesis_summary });
        // Update local synthesis history so status shows immediately
        setSynthesisHistory(prev => {
          const filtered = prev.filter(h => !(h.entity_type === entityType && h.entity_id === entityId));
          return [...filtered, {
            entity_type: entityType,
            entity_id: entityId,
            entity_name: data.entity_name,
            sessions_synthesized: data.sessions_synthesized,
            synthesis_summary: data.synthesis_summary,
            created_at: new Date().toISOString(),
          }];
        });
      } else {
        setSynthesisResult({ success: false, message: data.detail || 'Synthesis failed' });
      }
    } catch (err) {
      setSynthesisResult({ success: false, message: err.message || 'Network error' });
    } finally {
      setSynthesizingEntity(null);
    }
  };

  // ── Interview Invites ──────────────────────────────────────────
  const fetchInvites = async () => {
    setInvitesLoading(true);
    try {
      const res = await fetch(`${API_BASE}/api/admin/companies/${id}/interview-invites`, { headers: getAuthHeaders() });
      if (res.ok) {
        const data = await res.json();
        setInvites(data.invites || []);
      }
    } catch (err) {
      console.error('Error fetching invites:', err);
    } finally {
      setInvitesLoading(false);
    }
  };

  const handleCreateInvite = async () => {
    if (!createInviteForm.interviewee_name.trim() || !createInviteForm.interviewee_email.trim()) return;
    setCreatingInvite(true);
    try {
      const res = await fetch(`${API_BASE}/api/admin/companies/${id}/interview-invites`, {
        method: 'POST',
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify(createInviteForm),
      });
      const data = await res.json();
      if (res.ok) {
        setNewInviteResult(data);
        setCreateInviteForm({ interviewee_name: '', interviewee_email: '', role_hint: '', notes: '', expiration_days: 14 });
        fetchInvites();
      } else {
        alert(data.detail || 'Failed to create invite');
      }
    } catch (err) {
      alert('Error creating invite: ' + err.message);
    } finally {
      setCreatingInvite(false);
    }
  };

  const handleRevokeInvite = async (inviteId) => {
    if (!window.confirm('Revoke this invite? The interviewee will no longer be able to start new sessions.')) return;
    setRevokingInviteId(inviteId);
    try {
      const res = await fetch(`${API_BASE}/api/admin/interview-invites/${inviteId}/revoke`, {
        method: 'POST',
        headers: getAuthHeaders(),
      });
      if (res.ok) {
        fetchInvites();
      } else {
        const err = await res.json().catch(() => ({}));
        alert(err.detail || 'Failed to revoke invite');
      }
    } catch (err) {
      alert('Error revoking invite: ' + err.message);
    } finally {
      setRevokingInviteId(null);
    }
  };

  // Format a Date as YYYY-MM-DD using local timezone components.
  // Do NOT use toISOString().slice(0, 10) — that converts to UTC and would
  // emit the next day's date when run after ~5pm in PST.
  const formatDateInput = (date) => {
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
  };

  const todayPlusDays = (days) => {
    const d = new Date();
    d.setDate(d.getDate() + days);
    return formatDateInput(d);
  };

  const openExtendModal = (invite) => {
    setExtendingInvite(invite);
    setExtendingDateValue(todayPlusDays(inviteConfig.default_expiry_days));
  };

  const closeExtendModal = () => {
    setExtendingInvite(null);
    setExtendingDateValue('');
  };

  // Status-conditional Actions cell for an invite row.
  //   - pending / in_progress -> Revoke (existing behavior, unchanged)
  //   - expired                -> Extend + Delete (new this commit)
  //   - completed / revoked    -> empty cell
  // Backend permits Extend on pending/in_progress too, but UX scope per kickoff
  // surfaces it on expired only.
  const renderInviteActions = (invite) => {
    if (invite.status === 'pending' || invite.status === 'in_progress') {
      return (
        <button
          onClick={() => handleRevokeInvite(invite.id)}
          disabled={revokingInviteId === invite.id}
          style={{ background: 'none', border: 'none', color: '#DC2626', fontSize: 'var(--font-size-sm)', cursor: 'pointer', padding: '4px 8px', opacity: revokingInviteId === invite.id ? 0.5 : 1 }}
        >
          {revokingInviteId === invite.id ? 'Revoking...' : 'Revoke'}
        </button>
      );
    }
    if (invite.status === 'expired') {
      return (
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
          <button
            onClick={() => openExtendModal(invite)}
            style={{ background: 'none', border: 'none', color: colors.success, fontSize: 'var(--font-size-sm)', cursor: 'pointer', padding: '4px 8px' }}
          >
            Extend
          </button>
          {renderDeleteInlineConfirm(invite)}
        </span>
      );
    }
    return null;
  };

  // Inline-confirm primitive for the Delete action. Returns either:
  //   - a plain "Delete" button (default state), or
  //   - a "Delete? Yes / No" morph (when this row is being confirmed).
  // Mutually exclusive across rows by construction (single id in state).
  // Step (e) decides which rows render this at all (status='expired' only).
  const renderDeleteInlineConfirm = (invite) => {
    const isConfirming = confirmingDeleteId === invite.id;
    const isInFlight = deletingInviteId === invite.id;

    if (!isConfirming) {
      return (
        <button
          onClick={() => setConfirmingDeleteId(invite.id)}
          style={{ background: 'none', border: 'none', color: '#DC2626', fontSize: 'var(--font-size-sm)', cursor: 'pointer', padding: '4px 8px' }}
        >
          Delete
        </button>
      );
    }

    return (
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
        <span style={{ fontSize: 'var(--font-size-sm)', color: colors.textMuted }}>Delete?</span>
        <button
          onClick={() => handleDeleteInvite(invite.id)}
          disabled={isInFlight}
          style={{ padding: '4px 10px', fontSize: 'var(--font-size-sm)', fontWeight: 600, color: '#fff', backgroundColor: '#DC2626', border: 'none', borderRadius: '4px', cursor: isInFlight ? 'not-allowed' : 'pointer', opacity: isInFlight ? 0.6 : 1 }}
        >
          {isInFlight ? '...' : 'Yes'}
        </button>
        <button
          onClick={() => setConfirmingDeleteId(null)}
          disabled={isInFlight}
          style={{ background: 'none', border: 'none', color: colors.textMuted, fontSize: 'var(--font-size-sm)', cursor: isInFlight ? 'not-allowed' : 'pointer', padding: '4px 6px' }}
        >
          No
        </button>
      </span>
    );
  };

  const fetchInviteConfig = async () => {
    try {
      const res = await fetch(`${API_BASE}/api/admin/invite-config`, { headers: getAuthHeaders() });
      if (res.ok) {
        const cfg = await res.json();
        setInviteConfig({
          default_expiry_days: cfg.default_expiry_days,
          max_expiry_days: cfg.max_expiry_days,
        });
      } else {
        console.warn('Failed to load invite config, using defaults', res.status);
      }
    } catch (err) {
      console.warn('Failed to load invite config, using defaults', err);
    }
  };

  const handleExtendInvite = async () => {
    if (!extendingInvite || !extendingDateValue) return;
    setExtendingInFlight(true);
    try {
      const res = await fetch(`${API_BASE}/api/admin/interview-invites/${extendingInvite.id}/extend`, {
        method: 'PATCH',
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ expires_at: extendingDateValue }),
      });
      if (res.ok) {
        const data = await res.json();
        const updated = data.invite;
        // Optimistic in-place update (avoids a list refetch)
        setInvites(prev => prev.map(inv => (inv.id === updated.id ? { ...inv, ...updated } : inv)));
        setExtendingInvite(null);
        setExtendingDateValue('');
      } else {
        const err = await res.json().catch(() => ({}));
        alert(err.detail || 'Failed to extend invite');
      }
    } catch (err) {
      alert('Error extending invite: ' + err.message);
    } finally {
      setExtendingInFlight(false);
    }
  };

  const handleDeleteInvite = async (inviteId) => {
    setDeletingInviteId(inviteId);
    try {
      const res = await fetch(`${API_BASE}/api/admin/interview-invites/${inviteId}`, {
        method: 'DELETE',
        headers: getAuthHeaders(),
      });
      if (res.status === 204) {
        // Optimistic remove — backend confirmed deletion, no need to refetch
        setInvites(prev => prev.filter(inv => inv.id !== inviteId));
      } else {
        const err = await res.json().catch(() => ({}));
        // TODO(invite-hardening): The 409 "Revoke instead" message vanishes when the alert
        // dismisses. Future polish: surface inline in the Actions cell so the next-step
        // instruction stays visible while the user finds Revoke.
        alert(err.detail || 'Failed to delete invite');
      }
    } catch (err) {
      alert('Error deleting invite: ' + err.message);
    } finally {
      setDeletingInviteId(null);
      setConfirmingDeleteId(null);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text).catch(() => {
      // Fallback for older browsers
      const textarea = document.createElement('textarea');
      textarea.value = text;
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand('copy');
      document.body.removeChild(textarea);
    });
  };

  const handleCoreDataUpload = async (dataType, endpoint, file) => {
    if (!file) return;
    setUploadingDataType(dataType);
    setUploadResult(null);

    const formData = new FormData();
    formData.append('file', file);
    formData.append('company_id', id);  // Multi-tenant: tag upload with this company's ID

    try {
      const response = await fetch(`${API_BASE}${endpoint}`, {
        method: 'POST',
        headers: token ? { 'Authorization': `Bearer ${token}` } : {},
        body: formData,
      });

      const result = await response.json();

      if (response.ok) {
        setUploadResult({ type: 'success', dataType, ...result });
        setMessage({ type: 'success', text: result.message || `${dataType} uploaded successfully` });
        fetchCoreDataStats();
        // A PO upload fires the Opportunities briefing engine (flag-gated) — refresh
        // the run-status indicator on the PO card.
        if (endpoint === '/api/upload/purchase-orders') {
          pollOppStatusAfterUpload();
        }
      } else {
        setUploadResult({ type: 'error', dataType, detail: result.detail });
        setMessage({ type: 'error', text: result.detail || 'Upload failed' });
      }
    } catch (error) {
      setMessage({ type: 'error', text: `Upload failed: ${error.message}` });
    } finally {
      setUploadingDataType(null);
    }
  };

  const handleContractUpload = async (file) => {
    if (!file) return;
    setUploadingDataType('Contracts');
    setUploadResult(null);

    const formData = new FormData();
    formData.append('file', file);
    formData.append('supplier_name', 'Bulk Upload');
    formData.append('contract_name', file.name);
    formData.append('company_id', id);  // Multi-tenant: tag upload with this company's ID

    try {
      const response = await fetch(`${API_BASE}/api/upload/contract-document`, {
        method: 'POST',
        headers: token ? { 'Authorization': `Bearer ${token}` } : {},
        body: formData,
      });

      const result = await response.json();

      if (response.ok) {
        setUploadResult({ type: 'success', dataType: 'Contracts', ...result });
        setMessage({ type: 'success', text: result.message || 'Contract uploaded successfully' });
        fetchCoreDataStats();
      } else {
        setMessage({ type: 'error', text: result.detail || 'Upload failed' });
      }
    } catch (error) {
      setMessage({ type: 'error', text: `Upload failed: ${error.message}` });
    } finally {
      setUploadingDataType(null);
    }
  };

  // ── Company Knowledge Functions ──
  const fetchKnowledgeDocs = async () => {
    setKnowledgeLoading(true);
    try {
      const response = await fetch(`${API_BASE}/api/knowledge/documents?limit=500&category=internal_strategy`, {
        headers: token ? { 'Authorization': `Bearer ${token}` } : {}
      });
      if (response.ok) {
        const docs = await response.json();
        setKnowledgeDocs(docs);
      }
    } catch (error) {
      console.error('Error fetching knowledge docs:', error);
    } finally {
      setKnowledgeLoading(false);
    }
  };

  const handleKnowledgeUpload = async (e) => {
    e.preventDefault();
    if (!knowledgeForm.file || !knowledgeForm.title) {
      setMessage({ type: 'error', text: 'File and title are required' });
      return;
    }

    setKnowledgeUploading(true);
    const formData = new FormData();
    formData.append('file', knowledgeForm.file);
    formData.append('title', knowledgeForm.title);
    formData.append('category', 'internal_strategy');
    if (knowledgeForm.source) formData.append('source', knowledgeForm.source);
    if (knowledgeForm.document_date) formData.append('document_date', knowledgeForm.document_date);
    if (knowledgeForm.summary) formData.append('summary', knowledgeForm.summary);
    if (knowledgeForm.relevance_tags) formData.append('relevance_tags', knowledgeForm.relevance_tags);

    try {
      const response = await fetch(`${API_BASE}/api/knowledge/upload?company_id=${id}`, {
        method: 'POST',
        headers: token ? { 'Authorization': `Bearer ${token}` } : {},
        body: formData,
      });

      if (response.ok) {
        const result = await response.json();
        setMessage({ type: 'success', text: `"${result.title}" uploaded successfully!` });
        setShowKnowledgeUpload(false);
        setKnowledgeForm({ file: null, title: '', source: '', document_date: '', summary: '', relevance_tags: '' });
        fetchKnowledgeDocs();
      } else {
        const error = await response.json();
        setMessage({ type: 'error', text: error.detail || 'Upload failed' });
      }
    } catch (error) {
      setMessage({ type: 'error', text: 'Upload failed: ' + error.message });
    } finally {
      setKnowledgeUploading(false);
    }
  };

  const handleDeleteKnowledgeDoc = async (docId, title) => {
    if (!window.confirm(`Delete "${title}"?`)) return;
    try {
      const response = await fetch(`${API_BASE}/api/knowledge/documents/${docId}`, {
        method: 'DELETE',
        headers: token ? { 'Authorization': `Bearer ${token}` } : {}
      });
      if (response.ok) {
        setMessage({ type: 'success', text: `Deleted "${title}"` });
        fetchKnowledgeDocs();
      }
    } catch (error) {
      setMessage({ type: 'error', text: 'Delete failed' });
    }
  };

  const formatDateShort = (d) => {
    if (!d) return 'Not available';
    return new Date(d).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
  };

  const getStatusBadge = (status) => {
    const styles = {
      active: { bg: `${colors.success}15`, color: colors.success },
      inactive: { bg: `${colors.warning}15`, color: colors.warning },
      deleted: { bg: `${colors.danger}15`, color: colors.danger },
    };
    const style = styles[status] || styles.inactive;
    
    return (
      <span style={{
        padding: '6px 14px',
        borderRadius: '12px',
        fontSize: 'var(--font-size-sm)',
        fontWeight: 'var(--font-weight-semibold)',
        backgroundColor: style.bg,
        color: style.color,
        textTransform: 'capitalize',
      }}>
        {status}
      </span>
    );
  };

  const getRoleBadge = (role) => {
    const styles = {
      company_admin: { bg: `${colors.adminPurple}15`, color: colors.adminPurple, label: 'Admin' },
      manager: { bg: `${colors.accent}15`, color: colors.accent, label: 'Manager' },
      user: { bg: `${colors.cyan}15`, color: colors.cyan, label: 'User' },
      viewer: { bg: `${colors.mediumGray}`, color: colors.textMuted, label: 'Viewer' },
    };
    const style = styles[role] || styles.user;
    
    return (
      <span style={{
        padding: '4px 10px',
        borderRadius: '6px',
        fontSize: 'var(--font-size-sm)',
        fontWeight: 'var(--font-weight-semibold)',
        backgroundColor: style.bg,
        color: style.color,
      }}>
        {style.label}
      </span>
    );
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Never';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const menuItemStyle = {
    display: 'block', width: '100%', padding: '8px 16px',
    background: 'none', border: 'none', textAlign: 'left',
    cursor: 'pointer', fontSize: 'var(--font-size-sm)', fontWeight: 'var(--font-weight-medium)',
  };

  if (loading) {
    return (
      <div style={{ 
        padding: '32px', 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center',
        minHeight: '100vh',
        backgroundColor: colors.lightGray,
      }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: 'var(--font-size-hero)', marginBottom: '16px', display: 'flex', justifyContent: 'center' }}><svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round"><rect x="4" y="2" width="16" height="20" rx="2" ry="2"/><path d="M9 22V12h6v10"/><line x1="9" y1="6" x2="9.01" y2="6"/><line x1="15" y1="6" x2="15.01" y2="6"/></svg></div>
          <div style={{ color: colors.textMuted }}>Loading company...</div>
        </div>
      </div>
    );
  }

  if (!company) {
    return (
      <div style={{ padding: '32px', backgroundColor: colors.lightGray, minHeight: '100vh' }}>
        <div style={{ textAlign: 'center', padding: '60px' }}>
          <div style={{ fontSize: 'var(--font-size-hero)', marginBottom: '16px', display: 'flex', justifyContent: 'center' }}><svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg></div>
          <h2 style={{ color: colors.darkText }}>Company Not Found</h2>
          <button
            onClick={() => navigate('/admin/companies')}
            style={{
              marginTop: '20px',
              padding: '10px 20px',
              backgroundColor: colors.accent,
              color: colors.white,
              border: 'none',
              borderRadius: '8px',
              cursor: 'pointer',
            }}
          >
            Back to Companies
          </button>
        </div>
      </div>
    );
  }

  const openWorkspace = async () => {
    setOpeningWorkspace(true);
    try {
      const response = await fetch(`${API_BASE}/api/admin/companies/${id}/support-session`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok || !data.token || !data.user) {
        setMessage({ type: 'error', text: data.detail || 'Could not open the workspace.' });
        return;
      }
      // The support session replaces the admin session in this browser. A full
      // page load starts the company portal with its own flags from /me.
      loginWithToken(data.token, data.user);
      window.location.assign('/');
    } catch (err) {
      setMessage({ type: 'error', text: 'Could not open the workspace.' });
    } finally {
      setOpeningWorkspace(false);
    }
  };

  return (
    <div style={{ padding: '32px', backgroundColor: colors.lightGray, minHeight: '100vh' }}>
      {/* Breadcrumb */}
      <div style={{ marginBottom: '16px' }}>
        <button
          onClick={() => navigate('/admin/companies')}
          style={{
            background: 'none',
            border: 'none',
            color: colors.accent,
            cursor: 'pointer',
            fontSize: 'var(--font-size-base)',
            padding: 0,
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
          }}
        >
          ← Back to Companies
        </button>
      </div>

      {/* Header */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'flex-start',
        marginBottom: '24px',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '20px' }}>
          {companyLogos.hasOwnProperty(company.company_name) ? (
            <CompanyLogo name={company.company_name} width={64} height={64} style={{ borderRadius: '12px' }} />
          ) : (
            <div style={{
              width: '64px',
              height: '64px',
              borderRadius: '12px',
              backgroundColor: `${colors.accent}15`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: 'var(--font-size-hero)',
              fontWeight: 'var(--font-weight-bold)',
              color: colors.accent,
            }}>
              {company.company_code?.substring(0, 2) || '??'}
            </div>
          )}
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <h1 style={{ fontSize: 'var(--font-size-hero)', fontWeight: 'var(--font-weight-bold)', color: colors.darkText, margin: 0 }}>
                {company.company_name}
              </h1>
              {getStatusBadge(company.status)}
            </div>
            <p style={{ color: colors.textMuted, margin: '4px 0 0 0', fontSize: 'var(--font-size-base)' }}>
              {company.company_code} • {company.industry || 'No industry set'}
            </p>
          </div>
        </div>
        
        <div style={{ display: 'flex', gap: '12px' }}>
        {supportEnabled && company.status === 'active' && (
          <button
            onClick={openWorkspace}
            disabled={openingWorkspace}
            title="Open this company's workspace as Wingman support. Every session is logged."
            style={{
              padding: '10px 20px',
              backgroundColor: colors.accent,
              border: 'none',
              borderRadius: '8px',
              fontSize: 'var(--font-size-base)',
              fontWeight: 'var(--font-weight-medium)',
              color: colors.white,
              cursor: openingWorkspace ? 'not-allowed' : 'pointer',
              opacity: openingWorkspace ? 0.7 : 1,
            }}
          >
            {openingWorkspace ? 'Opening…' : 'Open workspace'}
          </button>
        )}
        <button
          onClick={() => setShowEditModal(true)}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            padding: '10px 20px',
            backgroundColor: colors.white,
            border: `1px solid ${colors.mediumGray}`,
            borderRadius: '8px',
            fontSize: 'var(--font-size-base)',
            fontWeight: 'var(--font-weight-medium)',
            color: colors.darkText,
            cursor: 'pointer',
          }}
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg> Edit Company
        </button>
        </div>
      </div>

      {/* Message Toast -- three states:
           success -> green   (colors.success)
           warning -> amber   (colors.warning) -- e.g. demand-context truncation
           error   -> red     (colors.danger)  -- default / unknown */}
      {message && (() => {
        const toastColor = message.type === 'success' ? colors.success
                         : message.type === 'warning' ? colors.warning
                         : colors.danger;
        return (
        <div style={{
          marginBottom: '16px',
          padding: '12px 16px',
          borderRadius: '8px',
          backgroundColor: `${toastColor}15`,
          borderLeft: `4px solid ${toastColor}`,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}>
          <span style={{ color: toastColor }}>
            {message.text}
          </span>
          <button
            onClick={() => setMessage(null)}
            style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 'var(--font-size-md)', color: colors.textMuted }}
          >
            ×
          </button>
        </div>
        );
      })()}

      {/* Tabs */}
      <div style={{
        display: 'flex',
        gap: '4px',
        marginBottom: '24px',
        borderBottom: `1px solid ${colors.mediumGray}`,
      }}>
        {[
          { key: 'overview', label: 'Overview' },
          { key: 'users', label: 'Users' },
          { key: 'core_data', label: 'Core Data' },
          { key: 'knowledge', label: 'Knowledge' },
          /* Coverage tab hidden — backend endpoints preserved */
          { key: 'interviews', label: 'Interviews' },
          { key: 'interview_invites', label: 'Interview Invites' },
          { key: 'profile', label: 'Profile' },
          { key: 'supplier_groups', label: 'Supplier Groups' },
          { key: 'supplier_research', label: 'Supplier Research' },
          { key: 'ppi_profiles', label: 'PPI Profiles' },
          { key: 'demand_context', label: 'Demand Context' },
          { key: 'intelligence', label: 'Intelligence' },
          { key: 'chat_capture', label: 'Chat Capture' },
          { key: 'uploads', label: 'Uploads' },
        ].map((tab, idx, arr) => (
          <button
            key={tab.key}
            onClick={() => {
              setActiveTab(tab.key);
              if (tab.key === 'users') fetchUsers();
              if (tab.key === 'core_data') fetchCoreDataStats();
              if (tab.key === 'knowledge') fetchKnowledgeDocs();
              if (tab.key === 'interviews') fetchInterviews();
              if (tab.key === 'interview_invites') { fetchInvites(); fetchInviteConfig(); }
              if (tab.key === 'profile') fetchProfile();
              if (tab.key === 'supplier_groups') { fetchSupplierGroups(); fetchAllSuppliers(); }
              if (tab.key === 'supplier_research') fetchResearchStatus();
              if (tab.key === 'ppi_profiles') fetchPpiStatus();
              if (tab.key === 'demand_context') { fetchDemandContexts(); fetchAllSuppliers(); fetchDcKnowledgeDocs(); }
              if (tab.key === 'intelligence') { fetchAuditData(); /* also check if running */ fetch(`${API_BASE}/api/admin/intelligence-audit/status/${id}`, { headers: getAuthHeaders() }).then(r => r.ok ? r.json() : null).then(d => { if (d && d.status === 'running') setAuditStatus(d); }).catch(() => {}); }
            }}
            style={{
              padding: '12px 20px',
              fontSize: 'var(--font-size-base)',
              fontWeight: 'var(--font-weight-medium)',
              color: activeTab === tab.key ? colors.accent : colors.textMuted,
              backgroundColor: 'transparent',
              border: 'none',
              borderBottom: `2px solid ${activeTab === tab.key ? colors.accent : 'transparent'}`,
              borderRight: idx < arr.length - 1 ? `1px solid ${colors.mediumGray}` : 'none',
              cursor: 'pointer',
              marginBottom: '-1px',
            }}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      {/* Lane 1430: the company's uploaded chat files, with Remove. */}
      {activeTab === 'uploads' && <AdminCompanyUploads companyId={id} />}
      {activeTab === 'overview' && (
        <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: '24px' }}>
          {/* Company Info */}
          <div style={{
            backgroundColor: colors.white,
            borderRadius: '12px',
            padding: '24px',
            boxShadow: 'var(--shadow-sm)',
          }}>
            <h2 style={{ fontSize: 'var(--font-size-md)', fontWeight: 'var(--font-weight-semibold)', color: colors.darkText, marginBottom: '20px' }}>
              Company Information
            </h2>
            
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
              <InfoItem label="Company Name" value={company.company_name} />
              <InfoItem label="Company Code" value={company.company_code} />
              <InfoItem label="Industry" value={company.industry || '-'} />
              <InfoItem label="Status" value={getStatusBadge(company.status)} />
              <InfoItem label="Contact Email" value={company.contact_email || '-'} />
              <InfoItem label="Contact Phone" value={company.contact_phone || '-'} />
              <InfoItem label="Created" value={formatDate(company.created_at)} />
              <InfoItem label="Last Updated" value={formatDate(company.updated_at)} />
            </div>

            {company.address && (
              <div style={{ marginTop: '20px', paddingTop: '20px', borderTop: `1px solid ${colors.mediumGray}` }}>
                <InfoItem label="Address" value={company.address} />
              </div>
            )}
          </div>

          {/* Quick Stats */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            <div style={{
              backgroundColor: colors.white,
              borderRadius: '12px',
              padding: '24px',
              boxShadow: 'var(--shadow-sm)',
            }}>
              <div style={{ fontSize: 'var(--font-size-sm)', color: colors.textMuted, marginBottom: '8px' }}>Total Users</div>
              <div style={{ fontSize: 'var(--font-size-hero)', fontWeight: 'var(--font-weight-bold)', color: colors.darkText }}>
                {company.user_count || 0}
              </div>
            </div>

            <button
              onClick={() => { setActiveTab('users'); fetchUsers(); }}
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '8px',
                padding: '16px',
                backgroundColor: colors.accent,
                color: colors.white,
                border: 'none',
                borderRadius: '12px',
                fontSize: 'var(--font-size-base)',
                fontWeight: 'var(--font-weight-semibold)',
                cursor: 'pointer',
              }}
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg> Manage Users
            </button>

            <button
              onClick={() => setShowUserModal(true)}
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '8px',
                padding: '16px',
                backgroundColor: colors.white,
                color: colors.accent,
                border: `1px solid ${colors.accent}`,
                borderRadius: '12px',
                fontSize: 'var(--font-size-base)',
                fontWeight: 'var(--font-weight-semibold)',
                cursor: 'pointer',
              }}
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg> Add User
            </button>

            {/* PPI Pipeline — 3 Steps */}
            <div style={{
              backgroundColor: colors.white,
              borderRadius: '12px',
              padding: '20px',
              boxShadow: 'var(--shadow-sm)',
              marginTop: '8px',
            }}>
              <div style={{ fontSize: 'var(--font-size-sm)', fontWeight: 'var(--font-weight-semibold)', color: colors.darkText, marginBottom: '4px' }}>PPI Market Index</div>
              <div style={{ fontSize: 'var(--font-size-xs)', color: colors.textMuted, marginBottom: '16px' }}>
                Run each step in order. Steps are safe to re-run.
              </div>
              {[
                { step: 1, label: 'Generate Mappings', desc: 'AI maps taxonomy to PPI series', endpoint: 'generate-mappings', summarize: d => {
                  const parts = [];
                  if (d.total_mapped != null) parts.push(`${d.total_mapped} mapped`);
                  if (d.batches_succeeded != null) parts.push(`${d.batches_succeeded}/${d.batches_total} batches`);
                  if (d.confidence_breakdown) {
                    const cb = d.confidence_breakdown;
                    parts.push(`${cb.high || 0} high / ${cb.medium || 0} med / ${cb.low || 0} low`);
                  }
                  return parts.join(' · ') || 'Done';
                }},
                { step: 2, label: 'Fetch PPI Data', desc: 'Download PPI index values from FRED API', endpoint: 'fetch-data', query: () => (ppiForceFetch ? '?force=true' : ''), summarize: summarizePpiFetch },
                { step: 3, label: 'Compute Deviations', desc: 'Compare your prices vs market trend', endpoint: 'compute-deviations', summarize: d => {
                  const parts = [];
                  if (d.deviations_computed != null) parts.push(`${d.deviations_computed} deviations`);
                  if (d.skipped_no_ppi_mapping) parts.push(`${d.skipped_no_ppi_mapping} no mapping`);
                  if (d.skipped_insufficient_data) parts.push(`${d.skipped_insufficient_data} insufficient data`);
                  if (d.materials_remaining > 0) parts.push(`${d.materials_remaining} remaining. Click again`);
                  return parts.join(' · ') || d.message || 'Done';
                }},
              ].map(({ step, label, desc, endpoint, summarize, query }) => {
                const s = ppiSteps[step] || {};
                const isRunning = s.running;
                const prevDone = step === 1 || ppiSteps[step - 1]?.success;
                // Lane 979 / F1: the server's own sentence, which used to be a
                // fallback the fragment line could never let through.
                const detail = s.success ? ppiStepDetail(s.data, s.summary) : null;
                return (
                  <div key={step} style={{ marginBottom: step < 3 ? '12px' : 0 }}>
                    <button
                      onClick={async () => {
                        setPpiSteps(prev => ({ ...prev, [step]: { running: true } }));
                        try {
                          const res = await fetch(`${API_BASE}/api/admin/ppi/${endpoint}/${id}${query ? query() : ''}`, {
                            method: 'POST',
                            headers: getAuthHeaders(),
                          });
                          const ct = res.headers.get('content-type') || '';
                          if (!ct.includes('application/json')) {
                            setPpiSteps(prev => ({ ...prev, [step]: { error: `Server returned ${res.status} (non-JSON), possible timeout` } }));
                          } else {
                            const data = await res.json();
                            if (res.ok && (data.success !== false)) {
                              setPpiSteps(prev => ({ ...prev, [step]: { success: true, summary: summarize(data), data } }));
                            } else {
                              setPpiSteps(prev => ({ ...prev, [step]: { error: data.error || data.detail || 'Failed' } }));
                            }
                          }
                        } catch (err) {
                          setPpiSteps(prev => ({ ...prev, [step]: { error: err.message } }));
                        }
                      }}
                      disabled={isRunning}
                      style={{
                        width: '100%',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        padding: '10px 12px',
                        backgroundColor: isRunning ? colors.mediumGray : prevDone ? '#7C3AED' : `${colors.mediumGray}`,
                        color: colors.white,
                        border: 'none',
                        borderRadius: '8px',
                        fontSize: 'var(--font-size-xs)',
                        fontWeight: 'var(--font-weight-semibold)',
                        cursor: isRunning ? 'not-allowed' : 'pointer',
                        opacity: isRunning ? 0.7 : 1,
                        textAlign: 'left',
                      }}
                    >
                      <span style={{ minWidth: '18px', textAlign: 'center' }}>
                        {s.success ? '\u2713' : isRunning ? '\u2026' : step}
                      </span>
                      <span style={{ flex: 1 }}>
                        <span>{isRunning ? `Running ${label}...` : label}</span>
                        <span style={{ display: 'block', fontSize: '10px', opacity: 0.8, fontWeight: 'normal' }}>{desc}</span>
                      </span>
                    </button>
                    {step === 2 && (
                      <label style={{ display: 'flex', alignItems: 'center', gap: '6px', marginTop: '4px', fontSize: '10px', color: colors.textMuted, cursor: 'pointer' }}>
                        <input
                          type="checkbox"
                          checked={ppiForceFetch}
                          onChange={e => setPpiForceFetch(e.target.checked)}
                          style={{ cursor: 'pointer' }}
                        />
                        Force refresh: ignore the 30-day freshness gate and re-fetch every series
                      </label>
                    )}
                    {s.success && s.summary && (
                      <div style={{ marginTop: '4px', padding: '6px 10px', backgroundColor: `${colors.success}10`, borderRadius: '6px', fontSize: '10px', color: colors.darkText }}>
                        {s.summary}
                      </div>
                    )}
                    {detail && (
                      <div style={{ marginTop: '2px', padding: '6px 10px', backgroundColor: colors.lightGray, borderRadius: '6px', fontSize: '10px', color: colors.textMuted }}>
                        {detail}
                      </div>
                    )}
                    {s.error && (
                      <div style={{ marginTop: '4px', padding: '6px 10px', backgroundColor: `${colors.danger}10`, borderRadius: '6px', fontSize: '10px', color: colors.danger }}>
                        {s.error}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

          </div>
        </div>
      )}

      {activeTab === 'users' && (
        <div>
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '16px',
          }}>
            <div style={{ fontSize: 'var(--font-size-base)', color: colors.textMuted }}>
              {users.length} {users.length === 1 ? 'user' : 'users'}
            </div>
            <button
              onClick={() => setShowUserModal(true)}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                padding: '10px 16px',
                backgroundColor: colors.accent,
                color: colors.white,
                border: 'none',
                borderRadius: '8px',
                fontSize: 'var(--font-size-base)',
                fontWeight: 'var(--font-weight-semibold)',
                cursor: 'pointer',
              }}
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg> Add User
            </button>
          </div>

          {users.length === 0 ? (
            <div style={{
              backgroundColor: colors.white,
              borderRadius: '12px',
              padding: '60px 20px',
              textAlign: 'center',
              boxShadow: 'var(--shadow-sm)',
            }}>
              <div style={{ fontSize: 'var(--font-size-hero)', marginBottom: '16px', display: 'flex', justifyContent: 'center' }}><svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg></div>
              <h3 style={{ fontSize: 'var(--font-size-md)', color: colors.darkText, marginBottom: '8px' }}>
                No users yet
              </h3>
              <p style={{ color: colors.textMuted, marginBottom: '24px' }}>
                Add the first user to this company
              </p>
              <button
                onClick={() => setShowUserModal(true)}
                style={{
                  padding: '12px 24px',
                  backgroundColor: colors.accent,
                  color: colors.white,
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: 'var(--font-size-base)',
                  fontWeight: 'var(--font-weight-semibold)',
                  cursor: 'pointer',
                }}
              >
                Add User
              </button>
            </div>
          ) : (
            <div style={{
              backgroundColor: colors.white,
              borderRadius: '12px',
              overflow: 'hidden',
              boxShadow: 'var(--shadow-sm)',
            }}>
              <table className="standard-table" style={{ width: '100%', borderCollapse: 'collapse', tableLayout: 'fixed' }}>
                <thead>
                  <tr>
                    <th style={{ padding: '8px 12px', textAlign: 'left', fontSize: 'var(--font-size-xs)', fontWeight: 'var(--font-weight-medium)', color: 'var(--color-text-muted)', textTransform: 'uppercase', letterSpacing: '0.03em', borderBottom: '1px solid #E2E5EA', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>User</th>
                    <th style={{ padding: '8px 12px', textAlign: 'left', fontSize: 'var(--font-size-xs)', fontWeight: 'var(--font-weight-medium)', color: 'var(--color-text-muted)', textTransform: 'uppercase', letterSpacing: '0.03em', borderBottom: '1px solid #E2E5EA', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>Role</th>
                    <th style={{ padding: '8px 12px', textAlign: 'left', fontSize: 'var(--font-size-xs)', fontWeight: 'var(--font-weight-medium)', color: 'var(--color-text-muted)', textTransform: 'uppercase', letterSpacing: '0.03em', borderBottom: '1px solid #E2E5EA', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>Department</th>
                    <th style={{ padding: '8px 12px', textAlign: 'left', fontSize: 'var(--font-size-xs)', fontWeight: 'var(--font-weight-medium)', color: 'var(--color-text-muted)', textTransform: 'uppercase', letterSpacing: '0.03em', borderBottom: '1px solid #E2E5EA', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>Last Login</th>
                    <th style={{ padding: '8px 12px', textAlign: 'center', fontSize: 'var(--font-size-xs)', fontWeight: 'var(--font-weight-medium)', color: 'var(--color-text-muted)', textTransform: 'uppercase', letterSpacing: '0.03em', borderBottom: '1px solid #E2E5EA', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>Status</th>
                    <th style={{ padding: '8px 12px', textAlign: 'right', fontSize: 'var(--font-size-xs)', fontWeight: 'var(--font-weight-medium)', color: 'var(--color-text-muted)', textTransform: 'uppercase', letterSpacing: '0.03em', borderBottom: '1px solid #E2E5EA', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {users.map((user, idx) => (
                    <tr
                      key={user.user_id}
                      style={{ borderBottom: idx < users.length - 1 ? `1px solid ${colors.mediumGray}` : 'none' }}
                    >
                      <td style={{ padding: '10px 12px', fontSize: 'var(--font-size-sm)', color: 'var(--color-text-primary)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        <span style={{ fontWeight: 'var(--font-weight-semibold)', color: colors.darkText }}>{user.full_name}</span>
                        <span style={{ fontSize: 'var(--font-size-sm)', color: colors.textMuted, marginLeft: '8px' }}>{user.email}</span>
                      </td>
                      <td style={{ padding: '10px 12px', fontSize: 'var(--font-size-sm)', color: 'var(--color-text-primary)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{getRoleBadge(user.role)}</td>
                      <td style={{ padding: '10px 12px', fontSize: 'var(--font-size-sm)', color: 'var(--color-text-primary)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {user.department || '-'}
                      </td>
                      <td style={{ padding: '10px 12px', fontSize: 'var(--font-size-sm)', color: 'var(--color-text-primary)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {user.last_login ? formatDate(user.last_login) : 'Never'}
                      </td>
                      <td style={{ padding: '10px 12px', fontSize: 'var(--font-size-sm)', color: 'var(--color-text-primary)', textAlign: 'center', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {getStatusBadge(user.status)}
                      </td>
                      <td style={{ padding: '10px 12px', fontSize: 'var(--font-size-sm)', color: 'var(--color-text-primary)', textAlign: 'right', whiteSpace: 'nowrap' }}>
                        <div style={{ display: 'inline-block' }}>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              const menuId = `user-${user.user_id}`;
                              if (openActionMenu === menuId) { setOpenActionMenu(null); return; }
                              const rect = e.currentTarget.getBoundingClientRect();
                              const dropdownHeight = 140;
                              const spaceBelow = window.innerHeight - rect.bottom;
                              const goUp = spaceBelow < dropdownHeight && rect.top > dropdownHeight;
                              setActionMenuPos({
                                top: goUp ? rect.top - dropdownHeight : rect.bottom + 4,
                                left: Math.max(8, rect.right - 200),
                              });
                              setOpenActionMenu(menuId);
                            }}
                            style={{
                              padding: '4px 10px', fontSize: 'var(--font-size-sm)', fontWeight: 'var(--font-weight-medium)',
                              color: colors.textMuted, backgroundColor: colors.lightGray,
                              border: `1px solid ${colors.mediumGray}`, borderRadius: '6px',
                              cursor: 'pointer', whiteSpace: 'nowrap',
                            }}
                          >
                            Actions
                          </button>
                          {openActionMenu === `user-${user.user_id}` && (
                            <div style={{
                              position: 'fixed',
                              top: actionMenuPos.top, left: actionMenuPos.left,
                              backgroundColor: colors.white, borderRadius: '8px',
                              boxShadow: '0 8px 24px rgba(0,0,0,0.18)', border: `1px solid ${colors.mediumGray}`,
                              zIndex: 99999, minWidth: '200px',
                            }}>
                              <button
                                onClick={(e) => { e.stopPropagation(); toggleUserStatus(user.user_id, user.status); setOpenActionMenu(null); }}
                                style={menuItemStyle}
                              >
                                <span style={{ color: user.status === 'active' ? colors.danger : colors.success }}>
                                  {user.status === 'active' ? 'Deactivate' : 'Activate'}
                                </span>
                              </button>
                              <button
                                onClick={(e) => { e.stopPropagation(); handleResetPassword(user.user_id, user.email); setOpenActionMenu(null); }}
                                style={menuItemStyle}
                              >
                                <span style={{ color: colors.warning }}>Reset Password</span>
                              </button>
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setConfirmDeleteUser(user);
                                  setOpenActionMenu(null);
                                }}
                                style={{ ...menuItemStyle, borderTop: `1px solid ${colors.mediumGray}` }}
                              >
                                <span style={{ color: colors.danger, fontWeight: 'var(--font-weight-semibold)' }}>Delete Permanently</span>
                              </button>
                            </div>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* ── Core Data Tab ── */}
      {activeTab === 'core_data' && (
        <div>
          <p style={{ fontSize: 'var(--font-size-sm)', color: colors.textMuted, marginBottom: '20px', marginTop: 0 }}>
            Upload the 7 core data files for this company. Files should be Excel format (.xlsx). Uploading replaces existing data via upsert.
          </p>

          {/* Upload Result Banner */}
          {uploadResult && (
            <div style={{
              marginBottom: '16px', padding: '12px 16px', borderRadius: '8px',
              backgroundColor: uploadResult.type === 'success' ? `${colors.success}10` : `${colors.danger}10`,
              border: `1px solid ${uploadResult.type === 'success' ? colors.success : colors.danger}30`,
            }}>
              <div style={{ fontWeight: 'var(--font-weight-semibold)', fontSize: 'var(--font-size-sm)', color: uploadResult.type === 'success' ? colors.success : colors.danger, marginBottom: '4px' }}>
                {uploadResult.type === 'success' ? <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round" style={{verticalAlign: 'middle', marginRight: '4px'}}><polyline points="20 6 9 17 4 12"/></svg> : <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round" style={{verticalAlign: 'middle', marginRight: '4px'}}><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>} {uploadResult.dataType}: {uploadResult.message || uploadResult.detail}
              </div>
              {uploadResult.inserted !== undefined && (
                <div style={{ fontSize: 'var(--font-size-sm)', color: colors.textMuted }}>
                  {uploadResult.inserted} rows processed
                  {uploadResult.errors?.length > 0 && ` • ${uploadResult.errors.length} errors`}
                </div>
              )}
              {uploadResult.errors?.length > 0 && (
                <details style={{ marginTop: '8px', fontSize: 'var(--font-size-sm)', color: colors.danger }}>
                  <summary style={{ cursor: 'pointer' }}>View errors</summary>
                  <div style={{ marginTop: '6px', maxHeight: '120px', overflow: 'auto' }}>
                    {uploadResult.errors.slice(0, 10).map((err, i) => <div key={i}>{err}</div>)}
                    {uploadResult.errors.length > 10 && <div>...and {uploadResult.errors.length - 10} more</div>}
                  </div>
                </details>
              )}
            </div>
          )}

          {/* Core Data Cards Grid */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: '16px' }}>
            {[
              { key: 'materials', label: 'Materials Master', icon: <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round"><line x1="16.5" y1="9.4" x2="7.5" y2="4.21"/><path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"/><polyline points="3.27 6.96 12 12.01 20.73 6.96"/><line x1="12" y1="22.08" x2="12" y2="12"/></svg>, endpoint: '/api/upload/materials-master', accept: '.xlsx,.xlsm', description: 'Material catalog with descriptions, types, UOM' },
              { key: 'approved_suppliers', label: 'Approved Suppliers', icon: <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round"><path d="M2 20h20"/><path d="M5 20V8l5 4V8l5 4V4h3a2 2 0 0 1 2 2v14"/></svg>, endpoint: '/api/upload/approved-suppliers', accept: '.xlsx,.xlsm', description: 'Which suppliers are approved for which materials' },
              { key: 'purchase_orders', label: 'Purchase Orders', icon: <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>, endpoint: '/api/upload/purchase-orders', accept: '.xlsx,.xlsm', description: 'Historical PO transaction data' },
              { key: 'forecasts', label: 'Forecasts', icon: <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>, endpoint: '/api/upload/forecasts', accept: '.xlsx,.xlsm', description: 'Future demand forecasts by material' },
              { key: 'contracts', label: 'Contracts', icon: <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>, endpoint: '/api/upload/contract-document', accept: '.pdf,.docx,.doc', description: 'Supplier contract documents', isContract: true },
              { key: 'standard_costs', label: 'Standard Costs', icon: <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="1" x2="12" y2="23"/><path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/></svg>, endpoint: '/api/upload/standard-costs', accept: '.xlsx,.xlsm', description: 'Baseline pricing per material per year' },
              { key: 'taxonomy', label: 'Material Taxonomy', icon: <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round"><path d="M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z"/><line x1="7" y1="7" x2="7.01" y2="7"/></svg>, endpoint: '/api/upload/taxonomy', accept: '.xlsx,.xlsm', description: 'Category hierarchy (Level 1-6) for each material' },
            ].map((dataType) => {
              const isUploading = uploadingDataType === dataType.label;
              const stats = coreDataStats[dataType.key];
              const hasData = stats && stats.count > 0;
              return (
                <div key={dataType.key} style={{
                  backgroundColor: colors.white, borderRadius: '12px', padding: '20px',
                  boxShadow: 'var(--shadow-sm)', border: `1px solid ${hasData ? colors.success + '40' : colors.mediumGray}`,
                  opacity: isUploading ? 0.7 : 1,
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '8px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                      <span style={{ display: 'flex', alignItems: 'center' }}>{dataType.icon}</span>
                      <h3 style={{ fontSize: 'var(--font-size-base)', fontWeight: 'var(--font-weight-semibold)', color: colors.darkText, margin: 0 }}>
                        {dataType.label}
                      </h3>
                    </div>
                    {hasData && (
                      <span style={{
                        padding: '3px 10px', borderRadius: '12px', fontSize: 'var(--font-size-sm)', fontWeight: 'var(--font-weight-bold)',
                        backgroundColor: `${colors.success}12`, color: colors.success,
                      }}>
                        {stats.count.toLocaleString()} rows
                      </span>
                    )}
                  </div>
                  <p style={{ fontSize: 'var(--font-size-sm)', color: colors.textMuted, margin: '0 0 6px 0', lineHeight: '1.4' }}>
                    {dataType.description}
                  </p>

                  {/* Current data status */}
                  {hasData ? (
                    <div style={{ fontSize: 'var(--font-size-xs)', color: colors.success, marginBottom: '12px', display: 'flex', alignItems: 'center', gap: '4px' }}>
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round" style={{verticalAlign: 'middle', marginRight: '4px'}}><polyline points="20 6 9 17 4 12"/></svg> Data loaded {stats.last_uploaded_at ? `· Updated ${formatDateShort(stats.last_uploaded_at)}` : ''}
                    </div>
                  ) : (
                    <div style={{ fontSize: 'var(--font-size-xs)', color: colors.warning, marginBottom: '12px', display: 'flex', alignItems: 'center', gap: '4px' }}>
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round" style={{verticalAlign: 'middle', marginRight: '4px'}}><path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg> No data uploaded yet
                    </div>
                  )}

                  {/* Upload area */}
                  <label style={{
                    display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
                    padding: '16px 12px', border: `2px dashed ${colors.mediumGray}`, borderRadius: '8px',
                    backgroundColor: colors.lightGray, cursor: isUploading ? 'not-allowed' : 'pointer',
                    transition: 'border-color 0.2s',
                  }}>
                    <input
                      type="file"
                      accept={dataType.accept}
                      style={{ display: 'none' }}
                      disabled={isUploading}
                      onChange={(e) => {
                        const file = e.target.files[0];
                        if (file) {
                          if (dataType.isContract) {
                            handleContractUpload(file);
                          } else {
                            handleCoreDataUpload(dataType.label, dataType.endpoint, file);
                          }
                        }
                        e.target.value = '';
                      }}
                    />
                    {isUploading ? (
                      <span style={{ fontSize: 'var(--font-size-sm)', color: colors.adminPurple, fontWeight: 'var(--font-weight-medium)' }}>Uploading...</span>
                    ) : (
                      <>
                        <span style={{ fontSize: 'var(--font-size-sm)', color: colors.accent, fontWeight: 'var(--font-weight-semibold)' }}>
                          Choose file to upload
                        </span>
                        <span style={{ fontSize: 'var(--font-size-xs)', color: colors.textMuted, marginTop: '4px' }}>
                          {dataType.accept.replace(/\./g, '').toUpperCase().replace(/,/g, ', ')}
                        </span>
                      </>
                    )}
                  </label>

                  {/* Opportunities briefing run-status — PO card only (Lane 37 step 3 §4).
                      Silent when the module flag is OFF (oppStatus stays null). */}
                  {dataType.key === 'purchase_orders' && (
                    <OpportunityStatusLine status={oppStatus} formatDate={formatDateShort} />
                  )}
                </div>
              );
            })}
          </div>

          {/* Top 5 by Spend */}
          {(topMaterials.length > 0 || topSuppliers.length > 0) && (
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginTop: '24px' }}>
              {topMaterials.length > 0 && (
                <div style={{ backgroundColor: colors.white, borderRadius: '12px', boxShadow: 'var(--shadow-sm)', overflow: 'hidden' }}>
                  <div style={{ padding: '14px 20px', borderBottom: `1px solid ${colors.mediumGray}` }}>
                    <h3 style={{ fontSize: 'var(--font-size-base)', fontWeight: 'var(--font-weight-semibold)', color: colors.darkText, margin: 0 }}>Top 5 Materials by Spend</h3>
                  </div>
                  {topMaterials.map((m, i) => (
                    <div key={i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 20px', borderBottom: i < topMaterials.length - 1 ? `1px solid ${colors.mediumGray}` : 'none' }}>
                      <div>
                        <div style={{ fontSize: 'var(--font-size-sm)', fontWeight: 'var(--font-weight-medium)', color: colors.darkText }}>{m.material_name}</div>
                        <div style={{ fontSize: 'var(--font-size-xs)', color: colors.textMuted }}>{m.material_number}</div>
                      </div>
                      <div style={{ fontSize: 'var(--font-size-sm)', fontWeight: 'var(--font-weight-semibold)', color: colors.darkText }}>
                        ${Number(m.total_spend).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
                      </div>
                    </div>
                  ))}
                </div>
              )}
              {topSuppliers.length > 0 && (
                <div style={{ backgroundColor: colors.white, borderRadius: '12px', boxShadow: 'var(--shadow-sm)', overflow: 'hidden' }}>
                  <div style={{ padding: '14px 20px', borderBottom: `1px solid ${colors.mediumGray}` }}>
                    <h3 style={{ fontSize: 'var(--font-size-base)', fontWeight: 'var(--font-weight-semibold)', color: colors.darkText, margin: 0 }}>Top 5 Suppliers by Spend</h3>
                  </div>
                  {topSuppliers.map((s, i) => (
                    <div key={i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 20px', borderBottom: i < topSuppliers.length - 1 ? `1px solid ${colors.mediumGray}` : 'none' }}>
                      <div style={{ fontSize: 'var(--font-size-sm)', fontWeight: 'var(--font-weight-medium)', color: colors.darkText }}>{s.supplier_name}</div>
                      <div style={{ fontSize: 'var(--font-size-sm)', fontWeight: 'var(--font-weight-semibold)', color: colors.darkText }}>
                        ${Number(s.total_spend).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* ── Knowledge Tab ── */}
      {activeTab === 'knowledge' && (
        <div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
            <p style={{ fontSize: 'var(--font-size-sm)', color: colors.textMuted, margin: 0 }}>
              Company-specific documents: internal strategy, CPO guidance, and other proprietary intelligence.
            </p>
            <button
              onClick={() => setShowKnowledgeUpload(true)}
              style={{
                display: 'flex', alignItems: 'center', gap: '8px',
                padding: '8px 16px', backgroundColor: colors.adminPurple, color: colors.white,
                border: 'none', borderRadius: '6px', fontSize: 'var(--font-size-sm)', fontWeight: 'var(--font-weight-semibold)', cursor: 'pointer',
              }}
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg> Upload Document
            </button>
          </div>

          {knowledgeLoading ? (
            <div style={{ textAlign: 'center', padding: '40px', color: colors.textMuted }}>Loading documents...</div>
          ) : knowledgeDocs.length === 0 ? (
            <div style={{
              backgroundColor: colors.white, borderRadius: '12px', padding: '50px 20px',
              textAlign: 'center', boxShadow: 'var(--shadow-sm)',
            }}>
              <div style={{ fontSize: 'var(--font-size-hero)', marginBottom: '12px', display: 'flex', justifyContent: 'center' }}><svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round"><path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20"/><path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z"/></svg></div>
              <h3 style={{ fontSize: 'var(--font-size-md)', color: colors.darkText, marginBottom: '8px' }}>No company documents yet</h3>
              <p style={{ color: colors.textMuted, fontSize: 'var(--font-size-sm)', marginBottom: '20px' }}>
                Upload internal strategy, CPO guidance, or other company-specific documents
              </p>
              <button
                onClick={() => setShowKnowledgeUpload(true)}
                style={{
                  padding: '10px 20px', backgroundColor: colors.adminPurple, color: colors.white,
                  border: 'none', borderRadius: '6px', fontSize: 'var(--font-size-sm)', fontWeight: 'var(--font-weight-semibold)', cursor: 'pointer',
                }}
              >Upload First Document</button>
            </div>
          ) : (
            <div style={{
              backgroundColor: colors.white, borderRadius: '8px',
              boxShadow: 'var(--shadow-sm)', overflow: 'hidden',
            }}>
              {/* Table Header */}
              <div style={{
                display: 'grid', gridTemplateColumns: '1fr 120px 100px 60px',
                padding: '8px 12px',
                borderBottom: '1px solid #E2E5EA',
                fontSize: 'var(--font-size-xs)', fontWeight: 'var(--font-weight-medium)', color: 'var(--color-text-muted)',
                textTransform: 'uppercase', letterSpacing: '0.03em',
              }}>
                <span>Title</span>
                <span>Source</span>
                <span>Date</span>
                <span></span>
              </div>
              {knowledgeDocs.map((doc) => (
                <div key={doc.document_id} style={{
                  display: 'grid', gridTemplateColumns: '1fr 120px 100px 60px',
                  padding: '10px 12px', borderBottom: `1px solid ${colors.mediumGray}15`,
                  alignItems: 'center', fontSize: 'var(--font-size-sm)',
                }}>
                  <div style={{ fontWeight: 'var(--font-weight-medium)', color: colors.darkText, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', paddingRight: '12px' }}>
                    {doc.title}
                  </div>
                  <div style={{ color: colors.textMuted, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {doc.source || 'Not available'}
                  </div>
                  <div style={{ color: colors.textMuted, fontSize: 'var(--font-size-sm)' }}>
                    {formatDateShort(doc.document_date || doc.created_at)}
                  </div>
                  <div style={{ textAlign: 'right' }}>
                    <button
                      onClick={() => handleDeleteKnowledgeDoc(doc.document_id, doc.title)}
                      style={{
                        background: 'none', border: 'none', cursor: 'pointer',
                        color: colors.danger, fontSize: 'var(--font-size-base)', padding: '4px',
                      }}
                      title="Delete"
                    ><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round"><polyline points="3 6 5 6 21 6"/><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/></svg></button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ── Coverage Tab ── */}
      {activeTab === 'coverage' && (
        <div>
          {coverageLoading ? (
            <div style={{ textAlign: 'center', padding: '40px', color: colors.textMuted }}>Loading coverage data...</div>
          ) : coverageError ? (
            <div style={{ textAlign: 'center', padding: '40px', color: colors.danger }}>{coverageError}</div>
          ) : coverageData ? (
            <div>
              {/* Summary Bar */}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '12px', marginBottom: '24px' }}>
                {[
                  { label: 'Materials with fields', value: coverageData.summary?.materials_with_fields ?? 0, total: coverageData.summary?.materials_total ?? 0, color: colors.accent },
                  { label: 'Materials claimed', value: coverageData.summary?.materials_claimed ?? 0, total: coverageData.summary?.materials_total ?? 0, color: colors.adminPurple },
                  { label: 'Suppliers with fields', value: coverageData.summary?.suppliers_with_fields ?? 0, total: coverageData.summary?.suppliers_total ?? 0, color: colors.success },
                  { label: 'Suppliers claimed', value: coverageData.summary?.suppliers_claimed ?? 0, total: coverageData.summary?.suppliers_total ?? 0, color: colors.warning },
                ].map((stat, i) => (
                  <div key={i} style={{
                    backgroundColor: colors.white, borderRadius: '10px', padding: '16px 20px',
                    boxShadow: 'var(--shadow-sm)',
                  }}>
                    <div style={{ fontSize: 'var(--font-size-hero)', fontWeight: 'var(--font-weight-bold)', color: colors.darkText }}>
                      {stat.value}<span style={{ fontSize: 'var(--font-size-sm)', color: colors.textMuted, fontWeight: 'var(--font-weight-medium)' }}> / {stat.total}</span>
                    </div>
                    <div style={{ fontSize: 'var(--font-size-sm)', color: colors.textMuted, marginTop: '4px' }}>{stat.label}</div>
                  </div>
                ))}
              </div>

              {/* Materials Coverage Table */}
              <div style={{ backgroundColor: colors.white, borderRadius: '12px', boxShadow: 'var(--shadow-sm)', marginBottom: '24px', overflow: 'hidden' }}>
                <div style={{ padding: '16px 20px', borderBottom: `1px solid ${colors.mediumGray}` }}>
                  <h3 style={{ fontSize: 'var(--font-size-md)', fontWeight: 'var(--font-weight-semibold)', color: colors.darkText, margin: 0 }}>Materials Coverage</h3>
                </div>
                {(coverageData.materials || []).length === 0 ? (
                  <div style={{ padding: '32px', textAlign: 'center', color: colors.textMuted }}>No materials found for this company</div>
                ) : (
                  <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead>
                      <tr style={{ borderBottom: `1px solid ${colors.mediumGray}` }}>
                        <th style={{ padding: '10px 16px', textAlign: 'left', fontSize: 'var(--font-size-sm)', fontWeight: 'var(--font-weight-semibold)', color: colors.textMuted }}>Material</th>
                        <th style={{ padding: '10px 16px', textAlign: 'center', fontSize: 'var(--font-size-sm)', fontWeight: 'var(--font-weight-semibold)', color: colors.textMuted }}>Fields</th>
                        <th style={{ padding: '10px 16px', textAlign: 'left', fontSize: 'var(--font-size-sm)', fontWeight: 'var(--font-weight-semibold)', color: colors.textMuted }}>Which Fields</th>
                        <th style={{ padding: '10px 16px', textAlign: 'left', fontSize: 'var(--font-size-sm)', fontWeight: 'var(--font-weight-semibold)', color: colors.textMuted }}>Claimed By</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(coverageData.materials || []).map((m) => (
                        <tr key={m.id} style={{ borderBottom: `1px solid ${colors.mediumGray}` }}>
                          <td style={{ padding: '10px 16px', fontSize: 'var(--font-size-base)', color: colors.darkText }}>
                            <div style={{ fontWeight: 'var(--font-weight-medium)' }}>{m.material_name}</div>
                            <div style={{ fontSize: 'var(--font-size-sm)', color: colors.textMuted }}>{m.material_number}</div>
                          </td>
                          <td style={{ padding: '10px 16px', textAlign: 'center' }}>
                            <span style={{
                              padding: '2px 10px', borderRadius: '20px', fontSize: 'var(--font-size-sm)', fontWeight: 'var(--font-weight-semibold)',
                              backgroundColor: m.fields_filled === m.fields_total ? `${colors.success}15` : m.fields_filled > 0 ? `${colors.warning}15` : `${colors.danger}15`,
                              color: m.fields_filled === m.fields_total ? colors.success : m.fields_filled > 0 ? colors.warning : colors.danger,
                            }}>
                              {m.fields_filled}/{m.fields_total}
                            </span>
                          </td>
                          <td style={{ padding: '10px 16px', fontSize: 'var(--font-size-sm)' }}>
                            {m.field_details && Object.entries(m.field_details).filter(([, v]) => v).map(([k]) => (
                              <span key={k} style={{ display: 'inline-block', padding: '2px 8px', borderRadius: '4px', backgroundColor: `${colors.accent}10`, color: colors.accent, fontSize: 'var(--font-size-xs)', marginRight: '4px', marginBottom: '2px' }}>
                                {k.replace('_notes', '')}
                              </span>
                            ))}
                            {m.fields_filled === 0 && <span style={{ color: colors.textMuted }}>-</span>}
                          </td>
                          <td style={{ padding: '10px 16px', fontSize: 'var(--font-size-sm)', color: m.claimed_by?.length > 0 ? colors.darkText : colors.textMuted }}>
                            {m.claimed_by?.length > 0 ? m.claimed_by.join(', ') : 'Unclaimed'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>

              {/* Suppliers Coverage Table */}
              <div style={{ backgroundColor: colors.white, borderRadius: '12px', boxShadow: 'var(--shadow-sm)', marginBottom: '24px', overflow: 'hidden' }}>
                <div style={{ padding: '16px 20px', borderBottom: `1px solid ${colors.mediumGray}` }}>
                  <h3 style={{ fontSize: 'var(--font-size-md)', fontWeight: 'var(--font-weight-semibold)', color: colors.darkText, margin: 0 }}>Suppliers Coverage</h3>
                </div>
                {(coverageData.suppliers || []).length === 0 ? (
                  <div style={{ padding: '32px', textAlign: 'center', color: colors.textMuted }}>No suppliers found for this company</div>
                ) : (
                  <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead>
                      <tr style={{ borderBottom: `1px solid ${colors.mediumGray}` }}>
                        <th style={{ padding: '10px 16px', textAlign: 'left', fontSize: 'var(--font-size-sm)', fontWeight: 'var(--font-weight-semibold)', color: colors.textMuted }}>Supplier</th>
                        <th style={{ padding: '10px 16px', textAlign: 'center', fontSize: 'var(--font-size-sm)', fontWeight: 'var(--font-weight-semibold)', color: colors.textMuted }}>Fields</th>
                        <th style={{ padding: '10px 16px', textAlign: 'left', fontSize: 'var(--font-size-sm)', fontWeight: 'var(--font-weight-semibold)', color: colors.textMuted }}>Which Fields</th>
                        <th style={{ padding: '10px 16px', textAlign: 'left', fontSize: 'var(--font-size-sm)', fontWeight: 'var(--font-weight-semibold)', color: colors.textMuted }}>Claimed By</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(coverageData.suppliers || []).map((s) => (
                        <tr key={s.id} style={{ borderBottom: `1px solid ${colors.mediumGray}` }}>
                          <td style={{ padding: '10px 16px', fontSize: 'var(--font-size-base)', fontWeight: 'var(--font-weight-medium)', color: colors.darkText }}>{s.supplier_name}</td>
                          <td style={{ padding: '10px 16px', textAlign: 'center' }}>
                            <span style={{
                              padding: '2px 10px', borderRadius: '20px', fontSize: 'var(--font-size-sm)', fontWeight: 'var(--font-weight-semibold)',
                              backgroundColor: s.fields_filled === s.fields_total ? `${colors.success}15` : s.fields_filled > 0 ? `${colors.warning}15` : `${colors.danger}15`,
                              color: s.fields_filled === s.fields_total ? colors.success : s.fields_filled > 0 ? colors.warning : colors.danger,
                            }}>
                              {s.fields_filled}/{s.fields_total}
                            </span>
                          </td>
                          <td style={{ padding: '10px 16px', fontSize: 'var(--font-size-sm)' }}>
                            {s.field_details && Object.entries(s.field_details).filter(([, v]) => v).map(([k]) => (
                              <span key={k} style={{ display: 'inline-block', padding: '2px 8px', borderRadius: '4px', backgroundColor: `${colors.success}10`, color: colors.success, fontSize: 'var(--font-size-xs)', marginRight: '4px', marginBottom: '2px' }}>
                                {k.replace('_notes', '')}
                              </span>
                            ))}
                            {s.fields_filled === 0 && <span style={{ color: colors.textMuted }}>-</span>}
                          </td>
                          <td style={{ padding: '10px 16px', fontSize: 'var(--font-size-sm)', color: s.claimed_by?.length > 0 ? colors.darkText : colors.textMuted }}>
                            {s.claimed_by?.length > 0 ? s.claimed_by.join(', ') : 'Unclaimed'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>

              {/* Gap Analysis */}
              {((coverageData.gaps?.materials || []).length > 0 || (coverageData.gaps?.suppliers || []).length > 0) && (
                <div style={{ backgroundColor: `${colors.warning}08`, border: `1px solid ${colors.warning}30`, borderRadius: '12px', padding: '20px' }}>
                  <h3 style={{ fontSize: 'var(--font-size-md)', fontWeight: 'var(--font-weight-semibold)', color: colors.darkText, marginBottom: '12px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={colors.warning} strokeWidth={2} strokeLinecap="round" strokeLinejoin="round"><path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>
                    Gap Analysis: No Fields and Unclaimed
                  </h3>
                  {(coverageData.gaps?.materials || []).length > 0 && (
                    <div style={{ marginBottom: '12px' }}>
                      <div style={{ fontSize: 'var(--font-size-sm)', fontWeight: 'var(--font-weight-semibold)', color: colors.darkText, marginBottom: '6px' }}>Materials ({coverageData.gaps.materials.length})</div>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                        {coverageData.gaps.materials.map((g) => (
                          <span key={g.id} style={{ padding: '4px 10px', borderRadius: '6px', backgroundColor: colors.white, fontSize: 'var(--font-size-sm)', color: colors.darkText, border: `1px solid ${colors.mediumGray}` }}>
                            {g.material_name}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                  {(coverageData.gaps?.suppliers || []).length > 0 && (
                    <div>
                      <div style={{ fontSize: 'var(--font-size-sm)', fontWeight: 'var(--font-weight-semibold)', color: colors.darkText, marginBottom: '6px' }}>Suppliers ({coverageData.gaps.suppliers.length})</div>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                        {coverageData.gaps.suppliers.map((g) => (
                          <span key={g.id} style={{ padding: '4px 10px', borderRadius: '6px', backgroundColor: colors.white, fontSize: 'var(--font-size-sm)', color: colors.darkText, border: `1px solid ${colors.mediumGray}` }}>
                            {g.supplier_name}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          ) : (
            <div style={{ textAlign: 'center', padding: '40px', color: colors.textMuted }}>No coverage data available</div>
          )}
        </div>
      )}

      {/* ── Interview Activity Tab ── */}
      {activeTab === 'interviews' && (
        <div>
          {interviewsLoading ? (
            <div style={{ textAlign: 'center', padding: '40px', color: colors.textMuted }}>Loading interview activity...</div>
          ) : interviewSessions.length === 0 ? (
            <div style={{
              backgroundColor: colors.white, borderRadius: '12px', padding: '50px 20px',
              textAlign: 'center', boxShadow: 'var(--shadow-sm)',
            }}>
              <div style={{ fontSize: 'var(--font-size-hero)', marginBottom: '12px', display: 'flex', justifyContent: 'center' }}>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={colors.textMuted} strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></svg>
              </div>
              <h3 style={{ fontSize: 'var(--font-size-md)', color: colors.darkText, marginBottom: '8px' }}>No interview sessions yet</h3>
              <p style={{ color: colors.textMuted, fontSize: 'var(--font-size-sm)' }}>
                Interview sessions will appear here when company users complete knowledge interviews.
              </p>
            </div>
          ) : (
            <>
            {/* Cross-Interview Synthesis */}
            {entityGroupedSessions.length > 0 && (
              <div style={{ marginBottom: '20px' }}>
                <h4 style={{ fontSize: 'var(--font-size-md)', fontWeight: 'var(--font-weight-semibold)', color: colors.darkText, marginBottom: '4px' }}>Cross-Interview Synthesis</h4>
                <p style={{ fontSize: 'var(--font-size-sm)', color: colors.textMuted, marginBottom: '12px' }}>
                  Merge insights from multiple interviews about the same entity into unified context fields.
                </p>
                {synthesisResult && (
                  <div style={{
                    padding: '10px 14px', borderRadius: '8px', marginBottom: '12px',
                    backgroundColor: synthesisResult.success ? '#05966915' : '#DC262615',
                    color: synthesisResult.success ? '#059669' : '#DC2626',
                    fontSize: 'var(--font-size-sm)',
                  }}>
                    <div style={{ fontWeight: 'var(--font-weight-semibold)' }}>{synthesisResult.message}</div>
                    {synthesisResult.summary && (
                      <div style={{ marginTop: '6px', color: colors.darkText, fontWeight: 'var(--font-weight-regular)' }}>{synthesisResult.summary}</div>
                    )}
                  </div>
                )}
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  {entityGroupedSessions.map(group => {
                    const isSynthesizing = synthesizingEntity?.entity_type === group.entity_type && synthesizingEntity?.entity_id === group.entity_id;
                    const history = synthesisHistory.find(h => h.entity_type === group.entity_type && h.entity_id === group.entity_id);
                    const newSinceLastSynthesis = history ? group.sessions.filter(s => s.completed_at && new Date(s.completed_at) > new Date(history.created_at)).length : 0;
                    return (
                      <div key={`${group.entity_type}:${group.entity_id}`} style={{
                        backgroundColor: colors.white, border: `1px solid ${colors.mediumGray}`, borderRadius: '10px',
                        padding: '12px 16px', display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                      }}>
                        <div>
                          <div style={{ fontSize: 'var(--font-size-base)', fontWeight: 'var(--font-weight-semibold)', color: colors.darkText }}>{group.entity_name}</div>
                          <div style={{ fontSize: 'var(--font-size-sm)', color: colors.textMuted }}>
                            {group.entity_type === 'supplier' ? 'Supplier' : 'Account'} &middot; {group.sessions.length} completed interview{group.sessions.length !== 1 ? 's' : ''}
                          </div>
                          {history && (
                            <div style={{ fontSize: '11px', color: colors.textMuted, marginTop: '2px' }}>
                              Last synthesized: {new Date(history.created_at).toLocaleDateString()} ({history.sessions_synthesized} interview{history.sessions_synthesized !== 1 ? 's' : ''})
                              {newSinceLastSynthesis > 0 && (
                                <span style={{ color: '#D97706', fontWeight: 'var(--font-weight-semibold)', marginLeft: '6px' }}>
                                  {newSinceLastSynthesis} new since
                                </span>
                              )}
                            </div>
                          )}
                        </div>
                        <button
                          onClick={() => handleCrossSynthesis(group.entity_type, group.entity_id)}
                          disabled={isSynthesizing}
                          style={{
                            padding: '6px 14px', borderRadius: '6px', border: 'none', cursor: isSynthesizing ? 'default' : 'pointer',
                            backgroundColor: isSynthesizing ? '#93a3b8' : (newSinceLastSynthesis > 0 ? '#D97706' : colors.accent), color: '#fff',
                            fontSize: 'var(--font-size-sm)', fontWeight: 'var(--font-weight-semibold)',
                            opacity: isSynthesizing ? 0.7 : 1,
                          }}
                        >
                          {isSynthesizing ? 'Synthesizing...' : newSinceLastSynthesis > 0 ? `Re-synthesize ${group.sessions.length} interviews` : `Synthesize ${group.sessions.length} interview${group.sessions.length !== 1 ? 's' : ''}`}
                        </button>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
            <div style={{ backgroundColor: colors.white, borderRadius: '12px', boxShadow: 'var(--shadow-sm)', overflow: 'hidden' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ borderBottom: `1px solid ${colors.mediumGray}` }}>
                    <th style={{ padding: '10px 16px', textAlign: 'left', fontSize: 'var(--font-size-sm)', fontWeight: 'var(--font-weight-semibold)', color: colors.textMuted }}>User</th>
                    <th style={{ padding: '10px 16px', textAlign: 'left', fontSize: 'var(--font-size-sm)', fontWeight: 'var(--font-weight-semibold)', color: colors.textMuted }}>Started</th>
                    <th style={{ padding: '10px 16px', textAlign: 'left', fontSize: 'var(--font-size-sm)', fontWeight: 'var(--font-weight-semibold)', color: colors.textMuted }}>Completed</th>
                    <th style={{ padding: '10px 16px', textAlign: 'center', fontSize: 'var(--font-size-sm)', fontWeight: 'var(--font-weight-semibold)', color: colors.textMuted }}>Rounds</th>
                    <th style={{ padding: '10px 16px', textAlign: 'center', fontSize: 'var(--font-size-sm)', fontWeight: 'var(--font-weight-semibold)', color: colors.textMuted }}>Status</th>
                    <th style={{ padding: '10px 16px', textAlign: 'right', fontSize: 'var(--font-size-sm)', fontWeight: 'var(--font-weight-semibold)', color: colors.textMuted }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {interviewSessions.map((session) => {
                    const isExpanded = expandedSessionId === session.id;
                    // Item 1.3b: surface failed synthesis as a dedicated pill, taking
                    // priority over session.status. NULL synthesis_status (legacy rows)
                    // normalizes to COMPLETE so PROD pre-1.3a sessions stay neutral.
                    const isSynthFailed =
                      normalizeSynthesisStatus(session.synthesis_status) === SYNTHESIS_STATUS.FAILED;
                    const statusStyle = isSynthFailed
                      ? { backgroundColor: '#DC262615', color: '#DC2626' }
                      : session.status === 'completed'
                        ? { backgroundColor: '#05966915', color: '#059669' }
                        : session.status === 'abandoned'
                          ? { backgroundColor: '#6B728015', color: '#6B7280' }
                          : { backgroundColor: '#2251FF15', color: '#2251FF' };
                    const statusLabel = isSynthFailed
                      ? SYNTHESIS_COPY.FAILED_SHORT
                      : session.status === 'completed' ? 'Completed'
                        : session.status === 'abandoned' ? 'Abandoned' : 'In Progress';
                    return (
                      <React.Fragment key={session.id}>
                        <tr
                          onClick={() => toggleSessionDetail(session.id)}
                          style={{ borderBottom: `1px solid ${colors.mediumGray}`, cursor: 'pointer', backgroundColor: isExpanded ? '#F9FAFB' : 'transparent' }}
                        >
                          <td style={{ padding: '10px 16px' }}>
                            <div style={{ fontWeight: 'var(--font-weight-medium)', color: colors.darkText, fontSize: 'var(--font-size-base)' }}>{session.user_name}</div>
                            {session.user_email && <div style={{ fontSize: 'var(--font-size-sm)', color: colors.textMuted }}>{session.user_email}</div>}
                          </td>
                          <td style={{ padding: '10px 16px', fontSize: 'var(--font-size-sm)', color: colors.textMuted }}>
                            {session.started_at ? new Date(session.started_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '-'}
                          </td>
                          <td style={{ padding: '10px 16px', fontSize: 'var(--font-size-sm)', color: colors.textMuted }}>
                            {session.completed_at ? new Date(session.completed_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '-'}
                          </td>
                          <td style={{ padding: '10px 16px', textAlign: 'center', fontSize: 'var(--font-size-base)', fontWeight: 'var(--font-weight-semibold)', color: colors.darkText }}>
                            {session.round_number || 1}
                          </td>
                          <td style={{ padding: '10px 16px', textAlign: 'center' }}>
                            <span
                              title={isSynthFailed ? SYNTHESIS_COPY.FAILED_ADMIN_TOOLTIP : undefined}
                              style={{ padding: '3px 10px', borderRadius: '20px', fontSize: 'var(--font-size-sm)', fontWeight: 'var(--font-weight-semibold)', ...statusStyle }}
                            >
                              {statusLabel}
                            </span>
                          </td>
                          <td style={{ padding: '10px 16px', textAlign: 'right' }}>
                            <button
                              onClick={(e) => { e.stopPropagation(); deleteSession(session.id); }}
                              disabled={deletingSessionId === session.id}
                              style={{ background: 'none', border: 'none', color: '#DC2626', fontSize: 'var(--font-size-sm)', cursor: 'pointer', padding: '4px 8px', opacity: deletingSessionId === session.id ? 0.5 : 1 }}
                            >
                              {deletingSessionId === session.id ? 'Deleting...' : 'Delete'}
                            </button>
                          </td>
                        </tr>
                        {isExpanded && (
                          <tr>
                            <td colSpan={6} style={{ padding: '0', borderBottom: `1px solid ${colors.mediumGray}` }}>
                              <div style={{ padding: '16px 20px', backgroundColor: '#F9FAFB' }}>
                                {sessionDetailLoading ? (
                                  <div style={{ textAlign: 'center', padding: '20px', color: colors.textMuted, fontSize: 'var(--font-size-sm)' }}>Loading session detail...</div>
                                ) : sessionDetail && sessionDetail.session ? (
                                  <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                                    {/* Session metadata */}
                                    <div style={{ display: 'flex', gap: '24px', flexWrap: 'wrap', fontSize: 'var(--font-size-sm)' }}>
                                      {sessionDetail.session.role && (
                                        <div><span style={{ color: colors.textMuted }}>Role:</span> <span style={{ color: colors.darkText, fontWeight: 500 }}>{sessionDetail.session.role}</span></div>
                                      )}
                                      {sessionDetail.session.interview_version && (
                                        <div><span style={{ color: colors.textMuted }}>Version:</span> <span style={{ color: colors.darkText, fontWeight: 500 }}>v{sessionDetail.session.interview_version}</span></div>
                                      )}
                                      {sessionDetail.session.phase && (
                                        <div><span style={{ color: colors.textMuted }}>Phase:</span> <span style={{ color: colors.darkText, fontWeight: 500 }}>{sessionDetail.session.phase}</span></div>
                                      )}
                                      {sessionDetail.session.focus_areas && (() => {
                                        const fa = sessionDetail.session.focus_areas;
                                        let names = [];
                                        if (Array.isArray(fa)) {
                                          names = fa.map(f => f.entity_name || f.name || '').filter(Boolean);
                                        } else if (fa && typeof fa === 'object') {
                                          for (const items of Object.values(fa)) {
                                            if (Array.isArray(items)) {
                                              for (const item of items) {
                                                if (item.name || item.entity_name) names.push(item.name || item.entity_name);
                                              }
                                            }
                                          }
                                        }
                                        return names.length > 0 ? (
                                          <div><span style={{ color: colors.textMuted }}>Focus:</span> <span style={{ color: colors.darkText, fontWeight: 500 }}>{names.join(', ')}</span></div>
                                        ) : null;
                                      })()}
                                    </div>
                                    {/* Session summary */}
                                    {sessionDetail.session.session_summary && (
                                      <div style={{ fontSize: 'var(--font-size-sm)', color: colors.darkText, lineHeight: 1.5, padding: '10px 14px', backgroundColor: '#fff', borderRadius: '6px', border: `1px solid ${colors.mediumGray}` }}>
                                        <div style={{ fontSize: '11px', textTransform: 'uppercase', fontWeight: 600, color: colors.textMuted, marginBottom: '4px' }}>Session Summary</div>
                                        {sessionDetail.session.session_summary}
                                      </div>
                                    )}
                                    {/* Questions & Answers */}
                                    <div>
                                      <div style={{ fontSize: '11px', textTransform: 'uppercase', fontWeight: 600, letterSpacing: '0.06em', color: colors.textMuted, marginBottom: '8px' }}>
                                        Questions & Answers ({sessionDetail.questions?.length || 0})
                                      </div>
                                      {(!sessionDetail.questions || sessionDetail.questions.length === 0) ? (
                                        <div style={{ fontSize: 'var(--font-size-sm)', color: colors.textMuted, fontStyle: 'italic' }}>No answers recorded</div>
                                      ) : (
                                        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                                          {sessionDetail.questions.map((q, idx) => (
                                            <div key={q.id || idx} style={{ padding: '10px 14px', backgroundColor: '#fff', borderRadius: '6px', border: `1px solid ${colors.mediumGray}` }}>
                                              <div style={{ fontSize: 'var(--font-size-sm)', color: colors.textMuted, marginBottom: '4px', lineHeight: 1.4 }}>
                                                {q.round_number > 1 && <span style={{ fontSize: '10px', fontWeight: 600, marginRight: '6px', color: colors.accent }}>R{q.round_number}</span>}
                                                Q{q.question_index + 1}: {q.question_text}
                                              </div>
                                              <div style={{ fontSize: 'var(--font-size-sm)', color: q.answer_text ? colors.darkText : colors.textMuted, lineHeight: 1.5, fontStyle: q.answer_text ? 'normal' : 'italic' }}>
                                                {q.answer_text || 'Not answered'}
                                              </div>
                                            </div>
                                          ))}
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                ) : (
                                  <div style={{ textAlign: 'center', padding: '20px', color: colors.textMuted, fontSize: 'var(--font-size-sm)' }}>Unable to load session detail</div>
                                )}
                              </div>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
            </>
          )}
        </div>
      )}

      {/* ── Interview Invites Tab ──────────────────────────────────── */}
      {activeTab === 'interview_invites' && (
        <div>
          {/* Create Invite Button + New Invite Result */}
          <div style={{ marginBottom: '20px', display: 'flex', gap: '12px', alignItems: 'center' }}>
            <button
              onClick={() => { setShowCreateInvite(true); setNewInviteResult(null); }}
              style={{
                padding: '8px 16px', fontSize: 'var(--font-size-sm)', fontWeight: 'var(--font-weight-semibold)',
                color: '#fff', backgroundColor: colors.accent, border: 'none', borderRadius: '8px', cursor: 'pointer',
              }}
            >
              Generate Invite
            </button>
          </div>

          {/* Create Invite Modal */}
          {showCreateInvite && !newInviteResult && (
            <div style={{
              backgroundColor: colors.white, borderRadius: '12px', padding: '24px', marginBottom: '20px',
              boxShadow: 'var(--shadow-sm)', border: `1px solid ${colors.mediumGray}`,
            }}>
              <h3 style={{ fontSize: 'var(--font-size-md)', color: colors.darkText, marginBottom: '16px' }}>Generate Interview Invite</h3>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                <div>
                  <label style={{ fontSize: '11px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em', color: colors.textMuted, display: 'block', marginBottom: '4px' }}>Interviewee Name *</label>
                  <input
                    type="text" value={createInviteForm.interviewee_name}
                    onChange={(e) => setCreateInviteForm(prev => ({ ...prev, interviewee_name: e.target.value }))}
                    placeholder="e.g., Sarah Chen"
                    style={{ width: '100%', padding: '8px 12px', border: `1px solid ${colors.mediumGray}`, borderRadius: '6px', fontSize: 'var(--font-size-base)' }}
                  />
                </div>
                <div>
                  <label style={{ fontSize: '11px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em', color: colors.textMuted, display: 'block', marginBottom: '4px' }}>Interviewee Email *</label>
                  <input
                    type="email" value={createInviteForm.interviewee_email}
                    onChange={(e) => setCreateInviteForm(prev => ({ ...prev, interviewee_email: e.target.value }))}
                    placeholder="e.g., sarah.chen@acme.com"
                    style={{ width: '100%', padding: '8px 12px', border: `1px solid ${colors.mediumGray}`, borderRadius: '6px', fontSize: 'var(--font-size-base)' }}
                  />
                </div>
                <div>
                  <label style={{ fontSize: '11px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em', color: colors.textMuted, display: 'block', marginBottom: '4px' }}>Role Hint (optional)</label>
                  <input
                    type="text" value={createInviteForm.role_hint}
                    onChange={(e) => setCreateInviteForm(prev => ({ ...prev, role_hint: e.target.value }))}
                    placeholder="e.g., buyer, engineer, plant manager"
                    style={{ width: '100%', padding: '8px 12px', border: `1px solid ${colors.mediumGray}`, borderRadius: '6px', fontSize: 'var(--font-size-base)' }}
                  />
                </div>
                <div>
                  <label style={{ fontSize: '11px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em', color: colors.textMuted, display: 'block', marginBottom: '4px' }}>Notes (optional)</label>
                  <textarea
                    value={createInviteForm.notes}
                    onChange={(e) => setCreateInviteForm(prev => ({ ...prev, notes: e.target.value }))}
                    placeholder="Context about why this person is being interviewed"
                    rows={2}
                    style={{ width: '100%', padding: '8px 12px', border: `1px solid ${colors.mediumGray}`, borderRadius: '6px', fontSize: 'var(--font-size-base)', resize: 'vertical' }}
                  />
                </div>
                <div>
                  <label style={{ fontSize: '11px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em', color: colors.textMuted, display: 'block', marginBottom: '4px' }}>Expiration (days)</label>
                  <input
                    type="number" value={createInviteForm.expiration_days} min={1} max={90}
                    onChange={(e) => setCreateInviteForm(prev => ({ ...prev, expiration_days: parseInt(e.target.value) || 14 }))}
                    style={{ width: '120px', padding: '8px 12px', border: `1px solid ${colors.mediumGray}`, borderRadius: '6px', fontSize: 'var(--font-size-base)' }}
                  />
                </div>
                <div style={{ display: 'flex', gap: '8px', marginTop: '8px' }}>
                  <button
                    onClick={handleCreateInvite} disabled={creatingInvite || !createInviteForm.interviewee_name.trim() || !createInviteForm.interviewee_email.trim()}
                    style={{ padding: '8px 20px', fontSize: 'var(--font-size-sm)', fontWeight: 600, color: '#fff', backgroundColor: colors.accent, border: 'none', borderRadius: '6px', cursor: creatingInvite ? 'not-allowed' : 'pointer', opacity: creatingInvite ? 0.6 : 1 }}
                  >
                    {creatingInvite ? 'Creating...' : 'Create Invite'}
                  </button>
                  <button
                    onClick={() => setShowCreateInvite(false)}
                    style={{ padding: '8px 20px', fontSize: 'var(--font-size-sm)', color: colors.textMuted, backgroundColor: 'transparent', border: `1px solid ${colors.mediumGray}`, borderRadius: '6px', cursor: 'pointer' }}
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Extend Invite Modal — inline panel, mirrors Create modal pattern */}
          {extendingInvite && (
            <div style={{
              backgroundColor: colors.white, borderRadius: '12px', padding: '24px', marginBottom: '20px',
              boxShadow: 'var(--shadow-sm)', border: `1px solid ${colors.mediumGray}`,
            }}>
              <h3 style={{ fontSize: 'var(--font-size-md)', color: colors.darkText, marginBottom: '4px' }}>
                Extend invite for {extendingInvite.interviewee_name}
              </h3>
              <p style={{ fontSize: 'var(--font-size-sm)', color: colors.textMuted, margin: '0 0 16px 0' }}>
                Currently {extendingInvite.status === 'in_progress' ? 'In Progress' : extendingInvite.status.charAt(0).toUpperCase() + extendingInvite.status.slice(1)},
                expires {extendingInvite.expires_at ? new Date(extendingInvite.expires_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '-'}
              </p>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                <div>
                  <label style={{ fontSize: '11px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em', color: colors.textMuted, display: 'block', marginBottom: '4px' }}>New Expiry Date</label>
                  <input
                    type="date"
                    value={extendingDateValue}
                    min={todayPlusDays(1)}
                    max={todayPlusDays(inviteConfig.max_expiry_days)}
                    onChange={(e) => setExtendingDateValue(e.target.value)}
                    style={{ padding: '8px 12px', border: `1px solid ${colors.mediumGray}`, borderRadius: '6px', fontSize: 'var(--font-size-base)' }}
                  />
                </div>
                <div style={{ display: 'flex', gap: '8px', marginTop: '8px' }}>
                  <button
                    onClick={handleExtendInvite}
                    disabled={extendingInFlight || !extendingDateValue}
                    style={{ padding: '8px 20px', fontSize: 'var(--font-size-sm)', fontWeight: 600, color: '#fff', backgroundColor: colors.accent, border: 'none', borderRadius: '6px', cursor: (extendingInFlight || !extendingDateValue) ? 'not-allowed' : 'pointer', opacity: (extendingInFlight || !extendingDateValue) ? 0.6 : 1 }}
                  >
                    {extendingInFlight ? 'Saving...' : 'Save'}
                  </button>
                  <button
                    onClick={closeExtendModal}
                    disabled={extendingInFlight}
                    style={{ padding: '8px 20px', fontSize: 'var(--font-size-sm)', color: colors.textMuted, backgroundColor: 'transparent', border: `1px solid ${colors.mediumGray}`, borderRadius: '6px', cursor: extendingInFlight ? 'not-allowed' : 'pointer' }}
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* New Invite Result (shown once) */}
          {newInviteResult && (
            <div style={{
              backgroundColor: '#F0FDF4', borderRadius: '12px', padding: '24px', marginBottom: '20px',
              border: '1px solid #BBF7D0',
            }}>
              <h3 style={{ fontSize: 'var(--font-size-md)', color: '#166534', marginBottom: '12px' }}>Invite Created</h3>
              <p style={{ fontSize: 'var(--font-size-sm)', color: '#166534', marginBottom: '16px' }}>
                Share the link and passcode below with {newInviteResult.invite?.interviewee_name || 'the interviewee'}. The passcode is shown only once and cannot be retrieved later.
              </p>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                <div>
                  <label style={{ fontSize: '11px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em', color: '#166534', display: 'block', marginBottom: '4px' }}>Interview Link</label>
                  <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                    <code style={{ flex: 1, padding: '8px 12px', backgroundColor: '#fff', border: '1px solid #BBF7D0', borderRadius: '6px', fontSize: 'var(--font-size-sm)', wordBreak: 'break-all' }}>
                      {window.location.origin}{newInviteResult.link_path}
                    </code>
                    <button onClick={() => copyToClipboard(`${window.location.origin}${newInviteResult.link_path}`)} style={{ padding: '8px 12px', fontSize: 'var(--font-size-sm)', backgroundColor: '#fff', border: '1px solid #BBF7D0', borderRadius: '6px', cursor: 'pointer', whiteSpace: 'nowrap' }}>
                      Copy Link
                    </button>
                  </div>
                </div>
                <div>
                  <label style={{ fontSize: '11px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em', color: '#166534', display: 'block', marginBottom: '4px' }}>Passcode (shown once)</label>
                  <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                    <code style={{ padding: '8px 16px', backgroundColor: '#fff', border: '1px solid #BBF7D0', borderRadius: '6px', fontSize: '18px', fontWeight: 700, letterSpacing: '0.2em', fontFamily: 'monospace' }}>
                      {newInviteResult.passcode}
                    </code>
                    <button onClick={() => copyToClipboard(newInviteResult.passcode)} style={{ padding: '8px 12px', fontSize: 'var(--font-size-sm)', backgroundColor: '#fff', border: '1px solid #BBF7D0', borderRadius: '6px', cursor: 'pointer', whiteSpace: 'nowrap' }}>
                      Copy Passcode
                    </button>
                  </div>
                </div>
              </div>
              <button
                onClick={() => { setNewInviteResult(null); setShowCreateInvite(false); }}
                style={{ marginTop: '16px', padding: '8px 16px', fontSize: 'var(--font-size-sm)', color: '#166534', backgroundColor: 'transparent', border: '1px solid #BBF7D0', borderRadius: '6px', cursor: 'pointer' }}
              >
                Done
              </button>
            </div>
          )}

          {/* Invites List */}
          {invitesLoading ? (
            <div style={{ textAlign: 'center', padding: '40px', color: colors.textMuted }}>Loading invites...</div>
          ) : invites.length === 0 ? (
            <div style={{
              backgroundColor: colors.white, borderRadius: '12px', padding: '50px 20px',
              textAlign: 'center', boxShadow: 'var(--shadow-sm)',
            }}>
              <h3 style={{ fontSize: 'var(--font-size-md)', color: colors.darkText, marginBottom: '8px' }}>No interview invites yet</h3>
              <p style={{ color: colors.textMuted, fontSize: 'var(--font-size-sm)' }}>
                Generate an invite to allow external team members to complete a knowledge interview.
              </p>
            </div>
          ) : (
            <div style={{ backgroundColor: colors.white, borderRadius: '12px', boxShadow: 'var(--shadow-sm)', overflow: 'hidden' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ borderBottom: `1px solid ${colors.mediumGray}` }}>
                    <th style={{ padding: '10px 16px', textAlign: 'left', fontSize: 'var(--font-size-sm)', fontWeight: 'var(--font-weight-semibold)', color: colors.textMuted }}>Name</th>
                    <th style={{ padding: '10px 16px', textAlign: 'left', fontSize: 'var(--font-size-sm)', fontWeight: 'var(--font-weight-semibold)', color: colors.textMuted }}>Email</th>
                    <th style={{ padding: '10px 16px', textAlign: 'center', fontSize: 'var(--font-size-sm)', fontWeight: 'var(--font-weight-semibold)', color: colors.textMuted }}>Status</th>
                    <th style={{ padding: '10px 16px', textAlign: 'left', fontSize: 'var(--font-size-sm)', fontWeight: 'var(--font-weight-semibold)', color: colors.textMuted }}>Created</th>
                    <th style={{ padding: '10px 16px', textAlign: 'left', fontSize: 'var(--font-size-sm)', fontWeight: 'var(--font-weight-semibold)', color: colors.textMuted }}>Expires</th>
                    <th style={{ padding: '10px 16px', textAlign: 'right', fontSize: 'var(--font-size-sm)', fontWeight: 'var(--font-weight-semibold)', color: colors.textMuted }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {invites.map((invite) => {
                    const statusColors = {
                      pending: { bg: '#2251FF15', color: '#2251FF' },
                      in_progress: { bg: '#F59E0B15', color: '#D97706' },
                      completed: { bg: '#05966915', color: '#059669' },
                      expired: { bg: '#6B728015', color: '#6B7280' },
                      revoked: { bg: '#DC262615', color: '#DC2626' },
                    };
                    const sc = statusColors[invite.status] || statusColors.pending;
                    return (
                      <tr key={invite.id} style={{ borderBottom: `1px solid ${colors.mediumGray}` }}>
                        <td style={{ padding: '10px 16px' }}>
                          <div style={{ fontWeight: 'var(--font-weight-medium)', color: colors.darkText, fontSize: 'var(--font-size-base)' }}>{invite.interviewee_name}</div>
                          {invite.role_hint && <div style={{ fontSize: 'var(--font-size-sm)', color: colors.textMuted }}>{invite.role_hint}</div>}
                        </td>
                        <td style={{ padding: '10px 16px', fontSize: 'var(--font-size-sm)', color: colors.textMuted }}>{invite.interviewee_email}</td>
                        <td style={{ padding: '10px 16px', textAlign: 'center' }}>
                          <span style={{ padding: '3px 10px', borderRadius: '20px', fontSize: 'var(--font-size-sm)', fontWeight: 'var(--font-weight-semibold)', backgroundColor: sc.bg, color: sc.color }}>
                            {invite.status === 'in_progress' ? 'In Progress' : invite.status.charAt(0).toUpperCase() + invite.status.slice(1)}
                          </span>
                        </td>
                        <td style={{ padding: '10px 16px', fontSize: 'var(--font-size-sm)', color: colors.textMuted }}>
                          {invite.created_at ? new Date(invite.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '-'}
                        </td>
                        <td style={{ padding: '10px 16px', fontSize: 'var(--font-size-sm)', color: colors.textMuted }}>
                          {invite.expires_at ? new Date(invite.expires_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '-'}
                        </td>
                        <td style={{ padding: '10px 16px', textAlign: 'right' }}>
                          {renderInviteActions(invite)}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {activeTab === 'profile' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
          <div>
            <h3 style={{ fontSize: 'var(--font-size-lg)', fontWeight: 'var(--font-weight-semibold)', color: colors.darkText, margin: '0 0 4px 0' }}>
              Company Profile
            </h3>
            <p style={{ fontSize: 'var(--font-size-sm)', color: colors.textMuted, margin: 0 }}>
              This information helps Wingman's AI understand your business and provide more relevant analysis.
            </p>
          </div>

          {profileLoading ? (
            <div style={{ padding: '40px', textAlign: 'center', color: colors.textMuted }}>Loading profile...</div>
          ) : (
            <>
              {/* Structured fields grid */}
              <div style={{
                display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: '16px',
                padding: '20px', backgroundColor: colors.white, border: `1px solid ${colors.mediumGray}`,
                borderRadius: '12px'
              }}>
                <div>
                  <label style={{ display: 'block', marginBottom: '6px', fontSize: 'var(--font-size-sm)', fontWeight: 'var(--font-weight-medium)', color: colors.darkText }}>Company Type</label>
                  <select value={profileForm.company_type || ''} onChange={(e) => setProfileForm({...profileForm, company_type: e.target.value})}
                    style={{ width: '100%', padding: '10px', border: `1px solid ${colors.mediumGray}`, borderRadius: '8px', fontSize: 'var(--font-size-sm)', boxSizing: 'border-box' }}>
                    <option value="">-- Select --</option>
                    <option value="manufacturer">Manufacturer</option>
                    <option value="distributor">Distributor</option>
                    <option value="service_provider">Service Provider</option>
                    <option value="contractor">Contractor</option>
                    <option value="other">Other</option>
                  </select>
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: '6px', fontSize: 'var(--font-size-sm)', fontWeight: 'var(--font-weight-medium)', color: colors.darkText }}>Industry</label>
                  <input type="text" value={profileForm.industry || ''} onChange={(e) => setProfileForm({...profileForm, industry: e.target.value})}
                    placeholder="e.g., Aerospace & Defense"
                    style={{ width: '100%', padding: '10px', border: `1px solid ${colors.mediumGray}`, borderRadius: '8px', fontSize: 'var(--font-size-sm)', boxSizing: 'border-box' }} />
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: '6px', fontSize: 'var(--font-size-sm)', fontWeight: 'var(--font-weight-medium)', color: colors.darkText }}>Annual Revenue Range</label>
                  <select value={profileForm.annual_revenue_range || ''} onChange={(e) => setProfileForm({...profileForm, annual_revenue_range: e.target.value})}
                    style={{ width: '100%', padding: '10px', border: `1px solid ${colors.mediumGray}`, borderRadius: '8px', fontSize: 'var(--font-size-sm)', boxSizing: 'border-box' }}>
                    <option value="">-- Select --</option>
                    <option value="under_10m">Under $10M</option>
                    <option value="10m_50m">$10M - $50M</option>
                    <option value="50m_200m">$50M - $200M</option>
                    <option value="200m_1b">$200M - $1B</option>
                    <option value="over_1b">Over $1B</option>
                  </select>
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: '6px', fontSize: 'var(--font-size-sm)', fontWeight: 'var(--font-weight-medium)', color: colors.darkText }}>Employee Count</label>
                  <select value={profileForm.employee_count_range || ''} onChange={(e) => setProfileForm({...profileForm, employee_count_range: e.target.value})}
                    style={{ width: '100%', padding: '10px', border: `1px solid ${colors.mediumGray}`, borderRadius: '8px', fontSize: 'var(--font-size-sm)', boxSizing: 'border-box' }}>
                    <option value="">-- Select --</option>
                    <option value="under_50">Under 50</option>
                    <option value="50_200">50 - 200</option>
                    <option value="200_1000">200 - 1,000</option>
                    <option value="1000_5000">1,000 - 5,000</option>
                    <option value="over_5000">Over 5,000</option>
                  </select>
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: '6px', fontSize: 'var(--font-size-sm)', fontWeight: 'var(--font-weight-medium)', color: colors.darkText }}>Procurement Model</label>
                  <select value={profileForm.primary_procurement_model || ''} onChange={(e) => setProfileForm({...profileForm, primary_procurement_model: e.target.value})}
                    style={{ width: '100%', padding: '10px', border: `1px solid ${colors.mediumGray}`, borderRadius: '8px', fontSize: 'var(--font-size-sm)', boxSizing: 'border-box' }}>
                    <option value="">-- Select --</option>
                    <option value="strategic_sourcing">Strategic Sourcing</option>
                    <option value="tactical_sourcing">Tactical Sourcing</option>
                    <option value="mixed">Mixed</option>
                  </select>
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: '6px', fontSize: 'var(--font-size-sm)', fontWeight: 'var(--font-weight-medium)', color: colors.darkText }}>Headquarters Country</label>
                  <input type="text" value={profileForm.headquarters_country || ''} onChange={(e) => setProfileForm({...profileForm, headquarters_country: e.target.value})}
                    placeholder="e.g., United States"
                    style={{ width: '100%', padding: '10px', border: `1px solid ${colors.mediumGray}`, borderRadius: '8px', fontSize: 'var(--font-size-sm)', boxSizing: 'border-box' }} />
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: '6px', fontSize: 'var(--font-size-sm)', fontWeight: 'var(--font-weight-medium)', color: colors.darkText }}>Number of Sites</label>
                  <input type="number" value={profileForm.number_of_sites || ''} onChange={(e) => setProfileForm({...profileForm, number_of_sites: e.target.value})}
                    min="0" placeholder="e.g., 3"
                    style={{ width: '100%', padding: '10px', border: `1px solid ${colors.mediumGray}`, borderRadius: '8px', fontSize: 'var(--font-size-sm)', boxSizing: 'border-box' }} />
                </div>
              </div>

              {/* Free-form fields */}
              <div style={{
                display: 'flex', flexDirection: 'column', gap: '16px',
                padding: '20px', backgroundColor: colors.white, border: `1px solid ${colors.mediumGray}`,
                borderRadius: '12px'
              }}>
                <div>
                  <label style={{ display: 'block', marginBottom: '6px', fontSize: 'var(--font-size-sm)', fontWeight: 'var(--font-weight-medium)', color: colors.darkText }}>Company Summary</label>
                  <textarea value={profileForm.company_profile_summary || ''} onChange={(e) => setProfileForm({...profileForm, company_profile_summary: e.target.value})}
                    rows={6} placeholder="Describe your company, what you procure, your key markets, and any important business context..."
                    style={{ width: '100%', padding: '12px', border: `1px solid ${colors.mediumGray}`, borderRadius: '8px', fontSize: 'var(--font-size-sm)', resize: 'vertical', boxSizing: 'border-box' }} />
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: '6px', fontSize: 'var(--font-size-sm)', fontWeight: 'var(--font-weight-medium)', color: colors.darkText }}>AI Context Notes</label>
                  <textarea value={profileForm.ai_context_notes || ''} onChange={(e) => setProfileForm({...profileForm, ai_context_notes: e.target.value})}
                    rows={4} placeholder="Additional context for AI analysis, e.g., key supplier relationships, procurement strategy priorities, industry-specific considerations..."
                    style={{ width: '100%', padding: '12px', border: `1px solid ${colors.mediumGray}`, borderRadius: '8px', fontSize: 'var(--font-size-sm)', resize: 'vertical', boxSizing: 'border-box' }} />
                </div>
              </div>

              {/* Read-only interview fields */}
              {(profileData.business_context || profileData.procurement_priorities) && (
                <div style={{
                  padding: '20px', backgroundColor: `${colors.adminPurple}05`, border: `1px solid ${colors.adminPurple}20`,
                  borderRadius: '12px'
                }}>
                  <div style={{ fontSize: 'var(--font-size-sm)', fontWeight: 'var(--font-weight-semibold)', color: colors.adminPurple, marginBottom: '12px', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                    From Knowledge Interview
                  </div>
                  {profileData.business_context && (
                    <div style={{ marginBottom: '12px' }}>
                      <div style={{ fontSize: 'var(--font-size-sm)', fontWeight: 'var(--font-weight-medium)', color: colors.darkText, marginBottom: '4px' }}>Business Context</div>
                      <div style={{ fontSize: 'var(--font-size-sm)', color: colors.textMuted, whiteSpace: 'pre-wrap', lineHeight: 1.5 }}>{profileData.business_context}</div>
                    </div>
                  )}
                  {profileData.procurement_priorities && (
                    <div>
                      <div style={{ fontSize: 'var(--font-size-sm)', fontWeight: 'var(--font-weight-medium)', color: colors.darkText, marginBottom: '4px' }}>Procurement Priorities</div>
                      <div style={{ fontSize: 'var(--font-size-sm)', color: colors.textMuted, whiteSpace: 'pre-wrap', lineHeight: 1.5 }}>{profileData.procurement_priorities}</div>
                    </div>
                  )}
                </div>
              )}

              {/* Save button */}
              <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                <button onClick={handleSaveProfile} disabled={profileSaving}
                  style={{
                    padding: '10px 24px', backgroundColor: colors.accent, color: colors.white,
                    border: 'none', borderRadius: '8px', fontSize: 'var(--font-size-base)',
                    fontWeight: 'var(--font-weight-semibold)', cursor: profileSaving ? 'not-allowed' : 'pointer',
                    opacity: profileSaving ? 0.7 : 1
                  }}>
                  {profileSaving ? 'Saving...' : 'Save Profile'}
                </button>
              </div>
            </>
          )}
        </div>
      )}

      {/* ── Supplier Groups Tab (A2) ── */}
      {activeTab === 'supplier_groups' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div>
              <h3 style={{ fontSize: 'var(--font-size-lg)', fontWeight: 'var(--font-weight-semibold)', color: colors.darkText, margin: '0 0 4px 0' }}>
                Supplier Groups
              </h3>
              <p style={{ fontSize: 'var(--font-size-sm)', color: colors.textMuted, margin: 0 }}>
                Group related suppliers (parent/subsidiary/division) to see consolidated spend and inform AI analysis.
              </p>
            </div>
            <button
              onClick={() => setShowCreateGroup(true)}
              style={{
                padding: '8px 16px', backgroundColor: colors.accent, color: colors.white,
                border: 'none', borderRadius: '8px', fontSize: 'var(--font-size-sm)',
                fontWeight: 'var(--font-weight-semibold)', cursor: 'pointer',
                display: 'flex', alignItems: 'center', gap: '6px', whiteSpace: 'nowrap',
              }}
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
              Create Group
            </button>
          </div>

          {/* Create Group Form (inline) */}
          {showCreateGroup && (
            <div style={{
              padding: '20px', backgroundColor: colors.white, border: `1px solid ${colors.accent}40`,
              borderRadius: '12px', display: 'flex', flexDirection: 'column', gap: '12px',
            }}>
              <div style={{ fontSize: 'var(--font-size-base)', fontWeight: 'var(--font-weight-semibold)', color: colors.darkText }}>
                New Supplier Group
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                <div>
                  <label style={{ display: 'block', marginBottom: '6px', fontSize: 'var(--font-size-sm)', fontWeight: 'var(--font-weight-medium)', color: colors.darkText }}>
                    Group Name *
                  </label>
                  <input
                    type="text"
                    value={createGroupForm.group_name}
                    onChange={(e) => setCreateGroupForm({ ...createGroupForm, group_name: e.target.value })}
                    placeholder="e.g., Precision Metals Corp"
                    style={{ width: '100%', padding: '10px', border: `1px solid ${colors.mediumGray}`, borderRadius: '8px', fontSize: 'var(--font-size-sm)', boxSizing: 'border-box' }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: '6px', fontSize: 'var(--font-size-sm)', fontWeight: 'var(--font-weight-medium)', color: colors.darkText }}>
                    Description
                  </label>
                  <input
                    type="text"
                    value={createGroupForm.description}
                    onChange={(e) => setCreateGroupForm({ ...createGroupForm, description: e.target.value })}
                    placeholder="e.g., Parent company for TitanEdge and AeroCircuit"
                    style={{ width: '100%', padding: '10px', border: `1px solid ${colors.mediumGray}`, borderRadius: '8px', fontSize: 'var(--font-size-sm)', boxSizing: 'border-box' }}
                  />
                </div>
              </div>
              <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
                <button
                  onClick={() => { setShowCreateGroup(false); setCreateGroupForm({ group_name: '', description: '' }); }}
                  style={{ padding: '8px 16px', backgroundColor: 'transparent', border: `1px solid ${colors.mediumGray}`, borderRadius: '8px', fontSize: 'var(--font-size-sm)', cursor: 'pointer' }}
                >
                  Cancel
                </button>
                <button
                  onClick={handleCreateGroup}
                  disabled={creatingGroup || !createGroupForm.group_name.trim()}
                  style={{
                    padding: '8px 16px', backgroundColor: colors.accent, color: colors.white,
                    border: 'none', borderRadius: '8px', fontSize: 'var(--font-size-sm)',
                    fontWeight: 'var(--font-weight-semibold)', cursor: creatingGroup ? 'not-allowed' : 'pointer',
                    opacity: (creatingGroup || !createGroupForm.group_name.trim()) ? 0.7 : 1,
                  }}
                >
                  {creatingGroup ? 'Creating...' : 'Create'}
                </button>
              </div>
            </div>
          )}

          {/* Groups List */}
          {groupsLoading ? (
            <div style={{ padding: '40px', textAlign: 'center', color: colors.textMuted }}>Loading supplier groups...</div>
          ) : supplierGroups.length === 0 ? (
            <div style={{
              padding: '48px', textAlign: 'center', backgroundColor: colors.white,
              border: `1px solid ${colors.mediumGray}`, borderRadius: '12px',
            }}>
              <div style={{ fontSize: 'var(--font-size-base)', color: colors.textMuted, marginBottom: '4px' }}>
                No supplier groups yet
              </div>
              <div style={{ fontSize: 'var(--font-size-sm)', color: colors.textMuted }}>
                Create a group to link related suppliers and see consolidated spend.
              </div>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              {supplierGroups.map((group) => {
                const isExpanded = expandedGroupId === group.group_id;
                const memberCount = (group.members || []).length;

                return (
                  <div key={group.group_id} style={{
                    backgroundColor: colors.white, border: `1px solid ${colors.mediumGray}`,
                    borderRadius: '12px',
                  }}>
                    {/* Group Header Row */}
                    <div
                      style={{
                        display: 'flex', alignItems: 'center', padding: '16px 20px', gap: '16px',
                        cursor: 'pointer', userSelect: 'none',
                      }}
                      onClick={() => { setExpandedGroupId(isExpanded ? null : group.group_id); setGroupSearchTerm(''); }}
                    >
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke={colors.textMuted} strokeWidth={2}
                        style={{ transform: isExpanded ? 'rotate(90deg)' : 'rotate(0deg)', transition: 'transform 0.15s', flexShrink: 0 }}>
                        <polyline points="9 18 15 12 9 6"/>
                      </svg>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: 'var(--font-size-base)', fontWeight: 'var(--font-weight-semibold)', color: colors.darkText }}>
                          {group.group_name}
                        </div>
                        {group.description && (
                          <div style={{ fontSize: 'var(--font-size-sm)', color: colors.textMuted, marginTop: '2px' }}>
                            {group.description}
                          </div>
                        )}
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '20px', flexShrink: 0 }}>
                        <div style={{ textAlign: 'right' }}>
                          <div style={{ fontSize: 'var(--font-size-sm)', color: colors.textMuted }}>
                            {memberCount} {memberCount === 1 ? 'member' : 'members'}
                          </div>
                          <div style={{ fontSize: 'var(--font-size-base)', fontWeight: 'var(--font-weight-semibold)', color: colors.darkText }}>
                            ${(group.consolidated_spend || 0).toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
                          </div>
                        </div>
                        <button
                          onClick={(e) => { e.stopPropagation(); handleDeleteGroup(group.group_id, group.group_name); }}
                          title="Delete group"
                          style={{
                            background: 'none', border: 'none', cursor: 'pointer', padding: '4px',
                            color: colors.textMuted, display: 'flex', alignItems: 'center',
                          }}
                        >
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round">
                            <polyline points="3 6 5 6 21 6"/><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/>
                          </svg>
                        </button>
                      </div>
                    </div>

                    {/* Expanded: Members + Assign */}
                    {isExpanded && (
                      <div style={{ borderTop: `1px solid ${colors.mediumGray}`, padding: '16px 20px' }}>
                        {/* Member list */}
                        {memberCount > 0 ? (
                          <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: '16px' }}>
                            <thead>
                              <tr style={{ borderBottom: `1px solid ${colors.mediumGray}` }}>
                                <th style={{ textAlign: 'left', padding: '8px 0', fontSize: '11px', fontWeight: 'var(--font-weight-semibold)', color: colors.textMuted, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Supplier</th>
                                <th style={{ textAlign: 'right', padding: '8px 0', fontSize: '11px', fontWeight: 'var(--font-weight-semibold)', color: colors.textMuted, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Spend</th>
                                <th style={{ width: '40px' }}></th>
                              </tr>
                            </thead>
                            <tbody>
                              {(group.members || []).map((member) => (
                                <tr key={member.supplier_id} style={{ borderBottom: `1px solid ${colors.lightGray}` }}>
                                  <td style={{ padding: '10px 0', fontSize: 'var(--font-size-sm)', color: colors.darkText }}>
                                    {member.supplier_name}
                                  </td>
                                  <td style={{ padding: '10px 0', fontSize: 'var(--font-size-sm)', color: colors.darkText, textAlign: 'right' }}>
                                    ${(member.spend || 0).toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
                                  </td>
                                  <td style={{ padding: '10px 0', textAlign: 'right' }}>
                                    <button
                                      onClick={() => handleUnassignSupplier(member.supplier_id)}
                                      disabled={assigningSupplier === member.supplier_id}
                                      title="Remove from group"
                                      style={{
                                        background: 'none', border: 'none', cursor: 'pointer', padding: '2px',
                                        color: colors.textMuted, opacity: assigningSupplier === member.supplier_id ? 0.4 : 1,
                                      }}
                                    >
                                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round">
                                        <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
                                      </svg>
                                    </button>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        ) : (
                          <div style={{ padding: '12px 0 16px', fontSize: 'var(--font-size-sm)', color: colors.textMuted }}>
                            No members yet. Search below to add suppliers.
                          </div>
                        )}

                        {/* Search to add supplier */}
                        <div style={{ position: 'relative' }}>
                          <input
                            type="text"
                            value={groupSearchTerm}
                            onChange={(e) => setGroupSearchTerm(e.target.value)}
                            placeholder="Search suppliers to add..."
                            style={{
                              width: '100%', padding: '10px 12px', border: `1px solid ${colors.mediumGray}`,
                              borderRadius: '8px', fontSize: 'var(--font-size-sm)', boxSizing: 'border-box',
                            }}
                          />
                          {filteredSearchSuppliers.length > 0 && (
                            <div style={{
                              position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 10,
                              backgroundColor: colors.white, border: `1px solid ${colors.mediumGray}`,
                              borderRadius: '8px', marginTop: '4px', boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                              maxHeight: '200px', overflowY: 'auto',
                            }}>
                              {filteredSearchSuppliers.map((s) => (
                                <button
                                  key={s.supplier_id}
                                  onClick={() => handleAssignSupplier(s.supplier_id, group.group_id)}
                                  disabled={assigningSupplier === s.supplier_id}
                                  style={{
                                    display: 'block', width: '100%', padding: '10px 12px', textAlign: 'left',
                                    background: 'none', border: 'none', borderBottom: `1px solid ${colors.lightGray}`,
                                    fontSize: 'var(--font-size-sm)', color: colors.darkText, cursor: 'pointer',
                                  }}
                                  onMouseEnter={(e) => e.target.style.backgroundColor = colors.lightGray}
                                  onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
                                >
                                  {s.supplier_name}
                                </button>
                              ))}
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* ── Supplier Research Tab ── */}
      {activeTab === 'supplier_research' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div>
              <h3 style={{ fontSize: 'var(--font-size-lg)', fontWeight: 'var(--font-weight-semibold)', color: colors.darkText, margin: '0 0 4px 0' }}>
                Supplier Research
              </h3>
              <p style={{ fontSize: 'var(--font-size-sm)', color: colors.textMuted, margin: 0 }}>
                {researchSummary.researched} of {researchSummary.total} suppliers researched
              </p>
            </div>
          </div>

          {/* Batch controls */}
          {isBatchRunning ? (
            <div style={{
              padding: '20px 24px', backgroundColor: colors.white, borderRadius: '12px',
              border: `1px solid ${colors.accent}40`,
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '12px' }}>
                <div style={{
                  width: '10px', height: '10px', borderRadius: '50%', backgroundColor: colors.accent,
                  animation: 'pulse 1.5s ease-in-out infinite',
                }} />
                <span style={{ fontSize: 'var(--font-size-sm)', fontWeight: 'var(--font-weight-semibold)', color: colors.darkText }}>
                  Researching supplier {batchStatus.completed + 1} of {batchStatus.total}: {batchStatus.current_supplier || '...'}
                </span>
              </div>
              {/* Progress bar */}
              <div style={{ height: '6px', backgroundColor: `${colors.accent}15`, borderRadius: '3px', overflow: 'hidden', marginBottom: '10px' }}>
                <div style={{
                  height: '100%', backgroundColor: colors.accent, borderRadius: '3px',
                  width: `${batchStatus.total > 0 ? (batchStatus.completed / batchStatus.total) * 100 : 0}%`,
                  transition: 'width 0.5s ease',
                }} />
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span style={{ fontSize: '12px', color: colors.textMuted }}>
                  {batchStatus.completed} of {batchStatus.total} complete
                  {batchStatus.elapsed_minutes != null && ` | Elapsed: ${batchStatus.elapsed_minutes} min`}
                  {batchStatus.estimated_remaining_minutes != null && ` | Est. remaining: ~${batchStatus.estimated_remaining_minutes} min`}
                </span>
                <span style={{ fontSize: '12px', color: colors.textMuted, fontStyle: 'italic' }}>
                  You can close this page. Research continues in the background.
                </span>
              </div>
              {batchStatus.errors && batchStatus.errors.length > 0 && (
                <div style={{ marginTop: '10px', fontSize: '12px', color: '#ef4444' }}>
                  {batchStatus.errors.length} error{batchStatus.errors.length !== 1 ? 's' : ''} so far
                </div>
              )}
            </div>
          ) : batchStatus && batchStatus.status === 'completed' ? (
            <div style={{
              padding: '16px 24px', backgroundColor: colors.white, borderRadius: '12px',
              border: `1px solid #05966940`,
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div>
                  <span style={{ fontSize: 'var(--font-size-sm)', fontWeight: 'var(--font-weight-semibold)', color: '#059669' }}>
                    Batch complete: {batchStatus.completed - (batchStatus.errors?.length || 0)} researched successfully
                    {batchStatus.errors?.length > 0 && `, ${batchStatus.errors.length} error${batchStatus.errors.length !== 1 ? 's' : ''}`}
                  </span>
                </div>
                <div style={{ display: 'flex', gap: '8px' }}>
                  {unresearchedCount > 0 && (
                    <>
                      <button
                        onClick={() => handleStartBatch('top_100')}
                        disabled={batchStarting}
                        style={{
                          padding: '8px 16px', fontSize: '12px', fontWeight: 'var(--font-weight-semibold)',
                          backgroundColor: colors.accent, color: colors.white, border: 'none',
                          borderRadius: '6px', cursor: batchStarting ? 'not-allowed' : 'pointer',
                        }}
                      >
                        Research Top 100 ({top100Remaining} remaining)
                      </button>
                      <button
                        onClick={() => setShowBatchConfirm(true)}
                        disabled={batchStarting}
                        style={{
                          padding: '8px 16px', fontSize: '12px', fontWeight: 'var(--font-weight-medium)',
                          backgroundColor: colors.white, color: colors.darkText,
                          border: `1px solid ${colors.mediumGray}`, borderRadius: '6px',
                          cursor: batchStarting ? 'not-allowed' : 'pointer',
                        }}
                      >
                        Research All ({unresearchedCount} remaining)
                      </button>
                    </>
                  )}
                </div>
              </div>
              {batchStatus.errors?.length > 0 && (
                <details style={{ marginTop: '10px' }}>
                  <summary style={{ fontSize: '12px', color: '#ef4444', cursor: 'pointer' }}>
                    View errors
                  </summary>
                  <div style={{ marginTop: '6px', fontSize: '12px', color: colors.darkText }}>
                    {batchStatus.errors.map((err, i) => (
                      <div key={i} style={{ padding: '4px 0', borderBottom: `1px solid ${colors.lightGray}` }}>
                        <strong>{err.supplier_name}</strong>: {err.error_message}
                      </div>
                    ))}
                  </div>
                </details>
              )}
            </div>
          ) : batchStatus && batchStatus.status === 'stopped_billing_error' ? (
            <div style={{
              padding: '16px 24px', backgroundColor: colors.white, borderRadius: '12px',
              border: '1px solid #f59e0b40',
            }}>
              <span style={{ fontSize: 'var(--font-size-sm)', fontWeight: 'var(--font-weight-semibold)', color: '#f59e0b' }}>
                Batch stopped: API credit balance too low.
              </span>
              <span style={{ fontSize: '12px', color: colors.textMuted, marginLeft: '12px' }}>
                {batchStatus.completed} of {batchStatus.total} completed before stopping. Add API credits and use "Clear Failed Research" below, then re-run.
              </span>
            </div>
          ) : batchStatus && batchStatus.status === 'failed' ? (
            <div style={{
              padding: '16px 24px', backgroundColor: colors.white, borderRadius: '12px',
              border: '1px solid #ef444440',
            }}>
              <span style={{ fontSize: 'var(--font-size-sm)', fontWeight: 'var(--font-weight-semibold)', color: '#ef4444' }}>
                Batch research failed after {batchStatus.completed} of {batchStatus.total} suppliers.
              </span>
              <span style={{ fontSize: '12px', color: colors.textMuted, marginLeft: '12px' }}>
                Click a button below to retry. Already-researched suppliers will be skipped.
              </span>
              <div style={{ display: 'flex', gap: '8px', marginTop: '12px' }}>
                <button
                  onClick={() => handleStartBatch('top_100')}
                  disabled={batchStarting}
                  style={{
                    padding: '8px 16px', fontSize: '12px', fontWeight: 'var(--font-weight-semibold)',
                    backgroundColor: colors.accent, color: colors.white, border: 'none',
                    borderRadius: '6px', cursor: 'pointer',
                  }}
                >
                  Retry Top 100
                </button>
              </div>
            </div>
          ) : (
            <div style={{
              padding: '16px 24px', backgroundColor: colors.white, borderRadius: '12px',
              border: `1px solid ${colors.mediumGray}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center',
            }}>
              <span style={{ fontSize: 'var(--font-size-sm)', color: colors.darkText }}>
                {unresearchedCount > 0
                  ? `${unresearchedCount} supplier${unresearchedCount !== 1 ? 's' : ''} not yet researched`
                  : `${researchSummary.total} supplier${researchSummary.total !== 1 ? 's' : ''} researched`}
              </span>
              <div style={{ display: 'flex', gap: '8px' }}>
                <button
                  onClick={() => handleStartBatch('top_100')}
                  disabled={batchStarting}
                  style={{
                    padding: '8px 16px', fontSize: '12px', fontWeight: 'var(--font-weight-semibold)',
                    backgroundColor: colors.accent, color: colors.white, border: 'none',
                    borderRadius: '6px', cursor: batchStarting ? 'not-allowed' : 'pointer',
                    opacity: batchStarting ? 0.7 : 1,
                  }}
                >
                  {batchStarting ? 'Starting...' : `Research Top 100 (${Math.min(100, Math.max(unresearchedCount, researchSummary.total))} suppliers)`}
                </button>
                <button
                  onClick={() => setShowBatchConfirm(true)}
                  disabled={batchStarting}
                  style={{
                    padding: '8px 16px', fontSize: '12px', fontWeight: 'var(--font-weight-medium)',
                    backgroundColor: colors.white, color: colors.darkText,
                    border: `1px solid ${colors.mediumGray}`, borderRadius: '6px',
                    cursor: batchStarting ? 'not-allowed' : 'pointer',
                    opacity: batchStarting ? 0.7 : 1,
                  }}
                >
                  Research All ({Math.max(unresearchedCount, researchSummary.total)} suppliers)
                </button>
              </div>
            </div>
          )}

          {/* Clear Failed Research button — always visible when not mid-batch */}
          {!isBatchRunning && (
            <div style={{
              padding: '12px 24px', backgroundColor: colors.white, borderRadius: '12px',
              border: `1px solid #f59e0b40`, display: 'flex', justifyContent: 'space-between', alignItems: 'center',
            }}>
              <span style={{ fontSize: 'var(--font-size-sm)', color: colors.darkText }}>
                {(researchSummary.failed_research || 0) > 0
                  ? `${researchSummary.failed_research} supplier${researchSummary.failed_research !== 1 ? 's' : ''} with failed research (low confidence)`
                  : 'Clear low-confidence research entries so they can be re-researched'}
              </span>
              <button
                onClick={handleCleanupResearch}
                disabled={cleanupRunning}
                style={{
                  padding: '8px 16px', fontSize: '12px', fontWeight: 'var(--font-weight-semibold)',
                  backgroundColor: '#f59e0b', color: colors.white, border: 'none',
                  borderRadius: '6px', cursor: cleanupRunning ? 'not-allowed' : 'pointer',
                  opacity: cleanupRunning ? 0.7 : 1,
                }}
              >
                {cleanupRunning ? 'Clearing...' : 'Clear Failed Research'}
              </button>
            </div>
          )}

          {/* Batch confirm dialog for "Research All" */}
          {showBatchConfirm && (
            <div style={{
              padding: '16px 24px', backgroundColor: '#fef3c7', borderRadius: '12px',
              border: '1px solid #f59e0b40',
            }}>
              <p style={{ fontSize: 'var(--font-size-sm)', color: colors.darkText, margin: '0 0 12px 0' }}>
                This will research all {unresearchedCount} remaining suppliers and may take several hours. Continue?
              </p>
              <div style={{ display: 'flex', gap: '8px' }}>
                <button
                  onClick={() => handleStartBatch('all')}
                  style={{
                    padding: '8px 16px', fontSize: '12px', fontWeight: 'var(--font-weight-semibold)',
                    backgroundColor: colors.accent, color: colors.white, border: 'none',
                    borderRadius: '6px', cursor: 'pointer',
                  }}
                >
                  Yes, start batch research
                </button>
                <button
                  onClick={() => setShowBatchConfirm(false)}
                  style={{
                    padding: '8px 16px', fontSize: '12px', fontWeight: 'var(--font-weight-medium)',
                    backgroundColor: colors.white, color: colors.darkText,
                    border: `1px solid ${colors.mediumGray}`, borderRadius: '6px', cursor: 'pointer',
                  }}
                >
                  Cancel
                </button>
              </div>
            </div>
          )}

          {researchLoading ? (
            <div style={{ textAlign: 'center', padding: '40px', color: colors.textMuted }}>Loading suppliers...</div>
          ) : researchSuppliers.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '40px', color: colors.textMuted,
              backgroundColor: colors.white, borderRadius: '12px', border: `1px solid ${colors.mediumGray}` }}>
              No suppliers found for this company.
            </div>
          ) : (
            <>
              {/* Table */}
              <div style={{ backgroundColor: colors.white, borderRadius: '12px', border: `1px solid ${colors.mediumGray}`, overflow: 'hidden' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr style={{ borderBottom: `1px solid ${colors.mediumGray}` }}>
                      <th style={{ padding: '12px 16px', textAlign: 'left', fontSize: '11px', fontWeight: 'var(--font-weight-semibold)', color: colors.textMuted, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Supplier Name</th>
                      <th style={{ padding: '12px 16px', textAlign: 'right', fontSize: '11px', fontWeight: 'var(--font-weight-semibold)', color: colors.textMuted, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Annual Spend</th>
                      <th style={{ padding: '12px 16px', textAlign: 'center', fontSize: '11px', fontWeight: 'var(--font-weight-semibold)', color: colors.textMuted, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Status</th>
                      <th style={{ padding: '12px 16px', textAlign: 'center', fontSize: '11px', fontWeight: 'var(--font-weight-semibold)', color: colors.textMuted, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Last Researched</th>
                      <th style={{ padding: '12px 16px', textAlign: 'center', fontSize: '11px', fontWeight: 'var(--font-weight-semibold)', color: colors.textMuted, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Confidence</th>
                      <th style={{ padding: '12px 16px', textAlign: 'right', fontSize: '11px', fontWeight: 'var(--font-weight-semibold)', color: colors.textMuted, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {researchSuppliers.map((s) => (
                      <React.Fragment key={s.supplier_id}>
                        <tr
                          style={{ borderBottom: `1px solid ${colors.lightGray}`, cursor: s.research_date ? 'pointer' : 'default' }}
                          onClick={() => s.research_date && handleExpandResearch(s.supplier_id)}
                        >
                          <td style={{ padding: '12px 16px', fontSize: 'var(--font-size-sm)', color: colors.darkText, fontWeight: 'var(--font-weight-medium)' }}>
                            {s.supplier_name}
                            {expandedResearchId === s.supplier_id && <span style={{ marginLeft: '6px', fontSize: '10px', color: colors.textMuted }}>&#9650;</span>}
                          </td>
                          <td style={{ padding: '12px 16px', textAlign: 'right', fontSize: 'var(--font-size-sm)', color: colors.darkText }}>
                            {s.annual_spend > 0 ? `$${s.annual_spend.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}` : 'Not available'}
                          </td>
                          <td style={{ padding: '12px 16px', textAlign: 'center' }}>
                            <span style={{
                              display: 'inline-block', padding: '3px 10px', borderRadius: '12px', fontSize: '11px', fontWeight: 'var(--font-weight-semibold)',
                              backgroundColor: s.research_date ? '#05966915' : `${colors.textMuted}15`,
                              color: s.research_date ? '#059669' : colors.textMuted,
                            }}>
                              {s.research_date ? 'Researched' : 'Not Researched'}
                            </span>
                          </td>
                          <td style={{ padding: '12px 16px', textAlign: 'center', fontSize: 'var(--font-size-sm)', color: colors.textMuted }}>
                            {s.research_date ? new Date(s.research_date).toLocaleDateString() : 'Not researched'}
                          </td>
                          <td style={{ padding: '12px 16px', textAlign: 'center' }}>
                            {s.research_confidence ? (
                              <span style={{
                                display: 'inline-block', padding: '3px 10px', borderRadius: '12px', fontSize: '11px', fontWeight: 'var(--font-weight-semibold)',
                                backgroundColor: s.research_confidence === 'high' ? '#05966915' : s.research_confidence === 'medium' ? '#f5920015' : '#ef444415',
                                color: s.research_confidence === 'high' ? '#059669' : s.research_confidence === 'medium' ? '#f59200' : '#ef4444',
                              }}>
                                {s.research_confidence}
                              </span>
                            ) : 'Not available'}
                          </td>
                          <td style={{ padding: '12px 16px', textAlign: 'right' }}>
                            <button
                              onClick={(e) => { e.stopPropagation(); handleTriggerResearch(s.supplier_id); }}
                              disabled={researchingId === s.supplier_id || isBatchRunning}
                              style={{
                                padding: '6px 14px', fontSize: '12px', fontWeight: 'var(--font-weight-semibold)',
                                backgroundColor: (researchingId === s.supplier_id || isBatchRunning) ? colors.textMuted : colors.accent,
                                color: colors.white, border: 'none', borderRadius: '6px',
                                cursor: (researchingId === s.supplier_id || isBatchRunning) ? 'not-allowed' : 'pointer',
                                opacity: (researchingId === s.supplier_id || isBatchRunning) ? 0.7 : 1,
                                whiteSpace: 'nowrap',
                              }}
                            >
                              {researchingId === s.supplier_id ? 'Researching... (1-2 min)' : (s.research_date ? 'Re-research' : 'Research')}
                            </button>
                          </td>
                        </tr>

                        {/* Expanded research details */}
                        {expandedResearchId === s.supplier_id && (
                          <tr>
                            <td colSpan={6} style={{ padding: '0 16px 16px 16px', backgroundColor: `${colors.accent}04` }}>
                              {expandedResearchLoading ? (
                                <div style={{ padding: '20px', textAlign: 'center', color: colors.textMuted }}>Loading research details...</div>
                              ) : expandedResearchData ? (
                                <div style={{ padding: '16px', backgroundColor: colors.white, borderRadius: '8px', border: `1px solid ${colors.mediumGray}`, marginTop: '8px' }}>
                                  {/* Summary */}
                                  {expandedResearchData.description && (
                                    <p style={{ fontSize: 'var(--font-size-sm)', color: colors.darkText, lineHeight: '1.6', margin: '0 0 16px 0', fontStyle: 'italic' }}>
                                      {expandedResearchData.description}
                                    </p>
                                  )}
                                  {/* Details grid */}
                                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px 24px' }}>
                                    {expandedResearchData.official_name && (
                                      <div><span style={{ fontSize: '11px', color: colors.textMuted, textTransform: 'uppercase', letterSpacing: '0.06em', fontWeight: 'var(--font-weight-semibold)' }}>Official Name</span><div style={{ fontSize: 'var(--font-size-sm)', color: colors.darkText, marginTop: '2px' }}>{expandedResearchData.official_name}</div></div>
                                    )}
                                    {expandedResearchData.headquarters && expandedResearchData.headquarters.city && (
                                      <div><span style={{ fontSize: '11px', color: colors.textMuted, textTransform: 'uppercase', letterSpacing: '0.06em', fontWeight: 'var(--font-weight-semibold)' }}>Headquarters</span><div style={{ fontSize: 'var(--font-size-sm)', color: colors.darkText, marginTop: '2px' }}>{[expandedResearchData.headquarters.city, expandedResearchData.headquarters.state, expandedResearchData.headquarters.country].filter(Boolean).join(', ')}</div></div>
                                    )}
                                    {expandedResearchData.ownership_type && (
                                      <div><span style={{ fontSize: '11px', color: colors.textMuted, textTransform: 'uppercase', letterSpacing: '0.06em', fontWeight: 'var(--font-weight-semibold)' }}>Ownership</span><div style={{ fontSize: 'var(--font-size-sm)', color: colors.darkText, marginTop: '2px' }}>{expandedResearchData.ownership_type}{expandedResearchData.parent_company ? ` (${expandedResearchData.parent_company})` : ''}</div></div>
                                    )}
                                    {expandedResearchData.revenue_range && expandedResearchData.revenue_range !== 'unknown' && (
                                      <div><span style={{ fontSize: '11px', color: colors.textMuted, textTransform: 'uppercase', letterSpacing: '0.06em', fontWeight: 'var(--font-weight-semibold)' }}>Revenue Range</span><div style={{ fontSize: 'var(--font-size-sm)', color: colors.darkText, marginTop: '2px' }}>{expandedResearchData.revenue_range.replace(/_/g, ' ')}</div></div>
                                    )}
                                    {expandedResearchData.employee_range && expandedResearchData.employee_range !== 'unknown' && (
                                      <div><span style={{ fontSize: '11px', color: colors.textMuted, textTransform: 'uppercase', letterSpacing: '0.06em', fontWeight: 'var(--font-weight-semibold)' }}>Employees</span><div style={{ fontSize: 'var(--font-size-sm)', color: colors.darkText, marginTop: '2px' }}>{expandedResearchData.employee_range.replace(/_/g, ' ')}</div></div>
                                    )}
                                    {expandedResearchData.year_established && (
                                      <div><span style={{ fontSize: '11px', color: colors.textMuted, textTransform: 'uppercase', letterSpacing: '0.06em', fontWeight: 'var(--font-weight-semibold)' }}>Established</span><div style={{ fontSize: 'var(--font-size-sm)', color: colors.darkText, marginTop: '2px' }}>{expandedResearchData.year_established}</div></div>
                                    )}
                                    {expandedResearchData.website && (
                                      <div><span style={{ fontSize: '11px', color: colors.textMuted, textTransform: 'uppercase', letterSpacing: '0.06em', fontWeight: 'var(--font-weight-semibold)' }}>Website</span><div style={{ fontSize: 'var(--font-size-sm)', color: colors.darkText, marginTop: '2px' }}>{expandedResearchData.website}</div></div>
                                    )}
                                    {expandedResearchData.competitive_position && (
                                      <div><span style={{ fontSize: '11px', color: colors.textMuted, textTransform: 'uppercase', letterSpacing: '0.06em', fontWeight: 'var(--font-weight-semibold)' }}>Competitive Position</span><div style={{ fontSize: 'var(--font-size-sm)', color: colors.darkText, marginTop: '2px' }}>{expandedResearchData.competitive_position}</div></div>
                                    )}
                                  </div>
                                  {/* Products & Capabilities */}
                                  {expandedResearchData.products_and_capabilities && (
                                    <div style={{ marginTop: '16px' }}>
                                      <span style={{ fontSize: '11px', color: colors.textMuted, textTransform: 'uppercase', letterSpacing: '0.06em', fontWeight: 'var(--font-weight-semibold)' }}>Products & Capabilities</span>
                                      <p style={{ fontSize: 'var(--font-size-sm)', color: colors.darkText, marginTop: '4px', lineHeight: '1.5' }}>{expandedResearchData.products_and_capabilities}</p>
                                    </div>
                                  )}
                                  {/* Certifications */}
                                  {expandedResearchData.certifications && expandedResearchData.certifications.length > 0 && (
                                    <div style={{ marginTop: '12px' }}>
                                      <span style={{ fontSize: '11px', color: colors.textMuted, textTransform: 'uppercase', letterSpacing: '0.06em', fontWeight: 'var(--font-weight-semibold)' }}>Certifications</span>
                                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginTop: '6px' }}>
                                        {expandedResearchData.certifications.map((cert, i) => (
                                          <span key={i} style={{
                                            display: 'inline-block', padding: '3px 10px', borderRadius: '12px', fontSize: '11px',
                                            fontWeight: 'var(--font-weight-semibold)', backgroundColor: `${colors.accent}12`, color: colors.accent,
                                          }}>{cert}</span>
                                        ))}
                                      </div>
                                    </div>
                                  )}
                                  {/* Industries Served */}
                                  {expandedResearchData.industries_served && expandedResearchData.industries_served.length > 0 && (
                                    <div style={{ marginTop: '12px' }}>
                                      <span style={{ fontSize: '11px', color: colors.textMuted, textTransform: 'uppercase', letterSpacing: '0.06em', fontWeight: 'var(--font-weight-semibold)' }}>Industries Served</span>
                                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginTop: '6px' }}>
                                        {expandedResearchData.industries_served.map((ind, i) => (
                                          <span key={i} style={{
                                            display: 'inline-block', padding: '3px 10px', borderRadius: '12px', fontSize: '11px',
                                            fontWeight: 'var(--font-weight-medium)', backgroundColor: `${colors.textMuted}12`, color: colors.darkText,
                                          }}>{ind}</span>
                                        ))}
                                      </div>
                                    </div>
                                  )}
                                  {/* Geographic Reach */}
                                  {expandedResearchData.geographic_reach && (
                                    <div style={{ marginTop: '12px' }}>
                                      <span style={{ fontSize: '11px', color: colors.textMuted, textTransform: 'uppercase', letterSpacing: '0.06em', fontWeight: 'var(--font-weight-semibold)' }}>Geographic Reach</span>
                                      <p style={{ fontSize: 'var(--font-size-sm)', color: colors.darkText, marginTop: '4px', lineHeight: '1.5' }}>{expandedResearchData.geographic_reach}</p>
                                    </div>
                                  )}
                                  {/* Recent News */}
                                  {expandedResearchData.recent_news && (
                                    <div style={{ marginTop: '12px' }}>
                                      <span style={{ fontSize: '11px', color: colors.textMuted, textTransform: 'uppercase', letterSpacing: '0.06em', fontWeight: 'var(--font-weight-semibold)' }}>Recent News</span>
                                      <p style={{ fontSize: 'var(--font-size-sm)', color: colors.darkText, marginTop: '4px', lineHeight: '1.5' }}>{expandedResearchData.recent_news}</p>
                                    </div>
                                  )}
                                  {/* Risk Factors */}
                                  {expandedResearchData.risk_factors && (
                                    <div style={{ marginTop: '12px' }}>
                                      <span style={{ fontSize: '11px', color: colors.textMuted, textTransform: 'uppercase', letterSpacing: '0.06em', fontWeight: 'var(--font-weight-semibold)' }}>Risk Factors</span>
                                      <p style={{ fontSize: 'var(--font-size-sm)', color: '#ef4444', marginTop: '4px', lineHeight: '1.5' }}>{expandedResearchData.risk_factors}</p>
                                    </div>
                                  )}
                                </div>
                              ) : (
                                <div style={{ padding: '20px', textAlign: 'center', color: colors.textMuted }}>No research data available.</div>
                              )}
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              {researchTotalPages > 1 && (
                <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '8px' }}>
                  <button
                    onClick={() => fetchResearchStatus(researchPage - 1)}
                    disabled={researchPage <= 1}
                    style={{
                      padding: '8px 16px', fontSize: 'var(--font-size-sm)', border: `1px solid ${colors.mediumGray}`,
                      borderRadius: '8px', backgroundColor: colors.white, cursor: researchPage <= 1 ? 'not-allowed' : 'pointer',
                      opacity: researchPage <= 1 ? 0.5 : 1, color: colors.darkText,
                    }}
                  >
                    Previous
                  </button>
                  <span style={{ fontSize: 'var(--font-size-sm)', color: colors.textMuted }}>
                    Page {researchPage} of {researchTotalPages}
                  </span>
                  <button
                    onClick={() => fetchResearchStatus(researchPage + 1)}
                    disabled={researchPage >= researchTotalPages}
                    style={{
                      padding: '8px 16px', fontSize: 'var(--font-size-sm)', border: `1px solid ${colors.mediumGray}`,
                      borderRadius: '8px', backgroundColor: colors.white, cursor: researchPage >= researchTotalPages ? 'not-allowed' : 'pointer',
                      opacity: researchPage >= researchTotalPages ? 0.5 : 1, color: colors.darkText,
                    }}
                  >
                    Next
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      )}

      {/* ── Demand Context Tab ── */}
      {activeTab === 'demand_context' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div>
              <h3 style={{ fontSize: 'var(--font-size-lg)', fontWeight: 'var(--font-weight-semibold)', color: colors.darkText, margin: '0 0 4px 0' }}>
                Demand Context
              </h3>
              <p style={{ fontSize: 'var(--font-size-sm)', color: colors.textMuted, margin: 0 }}>
                Map revenue drivers (customers, products, programs) to suppliers and materials for AI-powered analysis.
              </p>
            </div>
            <div style={{ display: 'flex', gap: '8px' }}>
              {pendingDcCount > 0 && (
                <button
                  onClick={handleApproveAllDc}
                  disabled={dcApprovingAll}
                  style={{
                    padding: '8px 16px', backgroundColor: '#059669', color: colors.white,
                    border: 'none', borderRadius: '8px', fontSize: 'var(--font-size-sm)',
                    fontWeight: 'var(--font-weight-semibold)', cursor: dcApprovingAll ? 'not-allowed' : 'pointer',
                    display: 'flex', alignItems: 'center', gap: '6px', whiteSpace: 'nowrap',
                    opacity: dcApprovingAll ? 0.7 : 1,
                  }}
                >
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
                  {dcApprovingAll ? 'Approving...' : `Approve All (${pendingDcCount})`}
                </button>
              )}
              <button
                onClick={() => setShowCreateDc(true)}
                style={{
                  padding: '8px 16px', backgroundColor: colors.accent, color: colors.white,
                  border: 'none', borderRadius: '8px', fontSize: 'var(--font-size-sm)',
                  fontWeight: 'var(--font-weight-semibold)', cursor: 'pointer',
                  display: 'flex', alignItems: 'center', gap: '6px', whiteSpace: 'nowrap',
                }}
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
                Add Context
              </button>
              {demandContexts.length > 0 && (
                <button
                  onClick={handleDeleteAllDc}
                  disabled={dcDeletingAll}
                  title="Delete all demand contexts and re-extract fresh"
                  style={{
                    padding: '8px 12px', backgroundColor: 'transparent', color: colors.textMuted,
                    border: `1px solid ${colors.mediumGray}`, borderRadius: '8px', fontSize: 'var(--font-size-sm)',
                    cursor: dcDeletingAll ? 'not-allowed' : 'pointer',
                    display: 'flex', alignItems: 'center', gap: '6px', whiteSpace: 'nowrap',
                    opacity: dcDeletingAll ? 0.7 : 1,
                  }}
                >
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round"><polyline points="3 6 5 6 21 6"/><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/></svg>
                  {dcDeletingAll ? 'Deleting...' : 'Delete All'}
                </button>
              )}
            </div>
          </div>

          {/* Summary Card */}
          {demandContextSummary.total_contexts > 0 && (
            <div style={{
              display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '16px',
            }}>
              {[
                { label: 'Demand Contexts', value: demandContextSummary.total_contexts },
                { label: 'Linked Suppliers', value: demandContextSummary.total_suppliers },
                { label: 'Linked Materials', value: demandContextSummary.total_materials },
                { label: 'Total Annual Value', value: `$${(demandContextSummary.total_annual_value || 0).toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}` },
              ].map((stat) => (
                <div key={stat.label} style={{
                  padding: '16px', backgroundColor: colors.white, border: `1px solid ${colors.mediumGray}`,
                  borderRadius: '12px', textAlign: 'center',
                }}>
                  <div style={{ fontSize: 'var(--font-size-lg)', fontWeight: 'var(--font-weight-semibold)', color: colors.darkText }}>
                    {stat.value}
                  </div>
                  <div style={{ fontSize: '11px', fontWeight: 'var(--font-weight-semibold)', color: colors.textMuted, textTransform: 'uppercase', letterSpacing: '0.06em', marginTop: '4px' }}>
                    {stat.label}
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Create Form (inline) */}
          {showCreateDc && (
            <div style={{
              padding: '20px', backgroundColor: colors.white, border: `1px solid ${colors.accent}40`,
              borderRadius: '12px', display: 'flex', flexDirection: 'column', gap: '12px',
            }}>
              <div style={{ fontSize: 'var(--font-size-base)', fontWeight: 'var(--font-weight-semibold)', color: colors.darkText }}>
                New Demand Context
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                <div>
                  <label style={{ display: 'block', marginBottom: '6px', fontSize: 'var(--font-size-sm)', fontWeight: 'var(--font-weight-medium)', color: colors.darkText }}>
                    Name *
                  </label>
                  <input
                    type="text"
                    value={dcForm.name}
                    onChange={(e) => setDcForm({ ...dcForm, name: e.target.value })}
                    placeholder="e.g., Halliburton, Cardiostat 20mg"
                    style={{ width: '100%', padding: '10px', border: `1px solid ${colors.mediumGray}`, borderRadius: '8px', fontSize: 'var(--font-size-sm)', boxSizing: 'border-box' }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: '6px', fontSize: 'var(--font-size-sm)', fontWeight: 'var(--font-weight-medium)', color: colors.darkText }}>
                    Type
                  </label>
                  <select
                    value={dcForm.context_type}
                    onChange={(e) => setDcForm({ ...dcForm, context_type: e.target.value })}
                    style={{ width: '100%', padding: '10px', border: `1px solid ${colors.mediumGray}`, borderRadius: '8px', fontSize: 'var(--font-size-sm)', boxSizing: 'border-box', backgroundColor: colors.white }}
                  >
                    <option value="">Select type...</option>
                    <option value="account">Account</option>
                    <option value="product">Product</option>
                    <option value="program">Program</option>
                    <option value="project">Project</option>
                  </select>
                </div>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '12px' }}>
                <div>
                  <label style={{ display: 'block', marginBottom: '6px', fontSize: 'var(--font-size-sm)', fontWeight: 'var(--font-weight-medium)', color: colors.darkText }}>
                    Annual Value
                  </label>
                  <input
                    type="number"
                    value={dcForm.annual_value}
                    onChange={(e) => setDcForm({ ...dcForm, annual_value: e.target.value })}
                    placeholder="e.g., 7300000"
                    style={{ width: '100%', padding: '10px', border: `1px solid ${colors.mediumGray}`, borderRadius: '8px', fontSize: 'var(--font-size-sm)', boxSizing: 'border-box' }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: '6px', fontSize: 'var(--font-size-sm)', fontWeight: 'var(--font-weight-medium)', color: colors.darkText }}>
                    Status
                  </label>
                  <select
                    value={dcForm.status}
                    onChange={(e) => setDcForm({ ...dcForm, status: e.target.value })}
                    style={{ width: '100%', padding: '10px', border: `1px solid ${colors.mediumGray}`, borderRadius: '8px', fontSize: 'var(--font-size-sm)', boxSizing: 'border-box', backgroundColor: colors.white }}
                  >
                    <option value="">Select status...</option>
                    <option value="growing">Growing</option>
                    <option value="stable">Stable</option>
                    <option value="declining">Declining</option>
                    <option value="at_risk">At Risk</option>
                    <option value="on_hold">On Hold</option>
                    <option value="new">New</option>
                  </select>
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: '6px', fontSize: 'var(--font-size-sm)', fontWeight: 'var(--font-weight-medium)', color: colors.darkText }}>
                    Strategic Tier
                  </label>
                  <select
                    value={dcForm.strategic_tier}
                    onChange={(e) => setDcForm({ ...dcForm, strategic_tier: e.target.value })}
                    style={{ width: '100%', padding: '10px', border: `1px solid ${colors.mediumGray}`, borderRadius: '8px', fontSize: 'var(--font-size-sm)', boxSizing: 'border-box', backgroundColor: colors.white }}
                  >
                    <option value="">Select tier...</option>
                    <option value="strategic">Strategic</option>
                    <option value="key">Key</option>
                    <option value="managed">Managed</option>
                    <option value="house">House</option>
                    <option value="other">Other</option>
                  </select>
                </div>
              </div>
              <div>
                <label style={{ display: 'block', marginBottom: '6px', fontSize: 'var(--font-size-sm)', fontWeight: 'var(--font-weight-medium)', color: colors.darkText }}>
                  Strategic Notes
                </label>
                <textarea
                  value={dcForm.strategic_notes}
                  onChange={(e) => setDcForm({ ...dcForm, strategic_notes: e.target.value })}
                  placeholder="Key strategic context about this account/product..."
                  rows={2}
                  style={{ width: '100%', padding: '10px', border: `1px solid ${colors.mediumGray}`, borderRadius: '8px', fontSize: 'var(--font-size-sm)', boxSizing: 'border-box', resize: 'vertical' }}
                />
              </div>
              <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
                <button
                  onClick={() => { setShowCreateDc(false); setDcForm({ name: '', context_type: '', annual_value: '', status: '', strategic_tier: '', margin_notes: '', strategic_notes: '', key_contacts: '' }); }}
                  style={{ padding: '8px 16px', backgroundColor: 'transparent', border: `1px solid ${colors.mediumGray}`, borderRadius: '8px', fontSize: 'var(--font-size-sm)', cursor: 'pointer' }}
                >
                  Cancel
                </button>
                <button
                  onClick={handleCreateDc}
                  disabled={creatingDc || !dcForm.name.trim()}
                  style={{
                    padding: '8px 16px', backgroundColor: colors.accent, color: colors.white,
                    border: 'none', borderRadius: '8px', fontSize: 'var(--font-size-sm)',
                    fontWeight: 'var(--font-weight-semibold)', cursor: creatingDc ? 'not-allowed' : 'pointer',
                    opacity: (creatingDc || !dcForm.name.trim()) ? 0.7 : 1,
                  }}
                >
                  {creatingDc ? 'Creating...' : 'Create'}
                </button>
              </div>
            </div>
          )}

          {/* Context List */}
          {/* Extraction loading indicator */}
          {dcExtracting && (
            <div style={{
              padding: '20px', backgroundColor: `${colors.accent}08`, border: `1px solid ${colors.accent}20`,
              borderRadius: '12px', textAlign: 'center', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '8px',
            }}>
              <div style={{ fontSize: 'var(--font-size-sm)', fontWeight: 'var(--font-weight-semibold)', color: colors.accent }}>
                Extracting demand contexts...
              </div>
              <div style={{ fontSize: 'var(--font-size-sm)', color: colors.textMuted }}>
                AI is analyzing the document. This may take up to 2 minutes.
              </div>
            </div>
          )}

          {/* Extract from Document button when contexts exist */}
          {demandContexts.length > 0 && dcKnowledgeDocs.length > 0 && !dcExtracting && (
            <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
              {dcKnowledgeDocs.map(doc => (
                <button
                  key={doc.document_id}
                  onClick={() => handleExtractDc(doc.document_id, doc.title)}
                  style={{
                    padding: '6px 12px', backgroundColor: 'transparent', border: `1px solid ${colors.mediumGray}`,
                    borderRadius: '6px', fontSize: 'var(--font-size-sm)', cursor: 'pointer',
                    display: 'flex', alignItems: 'center', gap: '6px', color: colors.textMuted,
                  }}
                >
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>
                  {extractedDocIds.has(doc.document_id) ? `Re-extract from "${doc.title}"` : `Extract from "${doc.title}"`}
                </button>
              ))}
            </div>
          )}

          {dcLoading ? (
            <div style={{ padding: '40px', textAlign: 'center', color: colors.textMuted }}>Loading demand contexts...</div>
          ) : demandContexts.length === 0 ? (
            <div style={{
              padding: '48px', textAlign: 'center', backgroundColor: colors.white,
              border: `1px solid ${colors.mediumGray}`, borderRadius: '12px',
            }}>
              <div style={{ fontSize: 'var(--font-size-base)', color: colors.textMuted, marginBottom: '8px' }}>
                No demand contexts yet
              </div>
              {dcKnowledgeDocs.length > 0 ? (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', alignItems: 'center' }}>
                  <div style={{ fontSize: 'var(--font-size-sm)', color: colors.textMuted, marginBottom: '4px' }}>
                    Extract demand contexts from your uploaded documents:
                  </div>
                  {dcKnowledgeDocs.map(doc => (
                    <button
                      key={doc.document_id}
                      onClick={() => handleExtractDc(doc.document_id, doc.title)}
                      disabled={dcExtracting}
                      style={{
                        padding: '8px 16px', backgroundColor: `${colors.accent}08`, border: `1px solid ${colors.accent}30`,
                        borderRadius: '8px', fontSize: 'var(--font-size-sm)', cursor: dcExtracting ? 'not-allowed' : 'pointer',
                        display: 'flex', alignItems: 'center', gap: '8px', color: colors.accent,
                        opacity: dcExtracting ? 0.6 : 1,
                      }}
                    >
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>
                      {extractedDocIds.has(doc.document_id) ? `Re-extract from "${doc.title}"` : `Extract from "${doc.title}"`}
                    </button>
                  ))}
                </div>
              ) : (
                <div style={{ fontSize: 'var(--font-size-sm)', color: colors.textMuted }}>
                  Upload a knowledge document first (Knowledge tab), then extract demand contexts from it. Or create one manually.
                </div>
              )}
            </div>
          ) : (
            <div style={{ backgroundColor: colors.white, border: `1px solid ${colors.mediumGray}`, borderRadius: '12px', overflow: 'hidden' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ borderBottom: `1px solid ${colors.mediumGray}` }}>
                    {['Name', 'Type', 'Annual Value', 'Status', 'Tier', 'Suppliers', 'Materials', 'Source', ''].map(h => (
                      <th key={h} style={{ textAlign: h === 'Annual Value' || h === 'Suppliers' || h === 'Materials' ? 'right' : 'left', padding: '12px 16px', fontSize: '11px', fontWeight: 'var(--font-weight-semibold)', color: colors.textMuted, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {demandContexts.map((dc) => {
                    const isExpanded = expandedDcId === dc.id;
                    const statusColors = { growing: '#059669', stable: '#2251FF', declining: '#DC2626', at_risk: '#F59E0B', on_hold: '#6B7280', new: '#8B5CF6' };
                    const recordStatusColor = dc.record_status === 'active' ? '#059669' : '#F59E0B';

                    return (
                      <React.Fragment key={dc.id}>
                        <tr
                          style={{ borderBottom: `1px solid ${colors.lightGray}`, cursor: 'pointer', backgroundColor: isExpanded ? `${colors.accent}05` : dc.record_status === 'pending_review' ? '#F59E0B08' : 'transparent' }}
                          onClick={() => {
                            if (isExpanded) {
                              setExpandedDcId(null);
                              setExpandedDcData(null);
                              setEditingDcId(null);
                            } else {
                              setExpandedDcId(dc.id);
                              fetchDemandContextDetail(dc.id);
                              setEditingDcId(null);
                            }
                          }}
                        >
                          <td style={{ padding: '12px 16px', fontSize: 'var(--font-size-sm)', color: colors.darkText, fontWeight: 'var(--font-weight-medium)' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                              <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke={colors.textMuted} strokeWidth={2}
                                style={{ transform: isExpanded ? 'rotate(90deg)' : 'rotate(0deg)', transition: 'transform 0.15s', flexShrink: 0 }}>
                                <polyline points="9 18 15 12 9 6"/>
                              </svg>
                              {dc.name}
                              {dc.record_status !== 'active' && (
                                <span style={{ fontSize: '10px', padding: '2px 6px', borderRadius: '4px', backgroundColor: `${recordStatusColor}15`, color: recordStatusColor, fontWeight: 'var(--font-weight-semibold)' }}>
                                  {dc.record_status}
                                </span>
                              )}
                            </div>
                          </td>
                          <td style={{ padding: '12px 16px', fontSize: 'var(--font-size-sm)', color: colors.textMuted, textTransform: 'capitalize' }}>
                            {dc.context_type || 'Not available'}
                          </td>
                          <td style={{ padding: '12px 16px', fontSize: 'var(--font-size-sm)', color: colors.darkText, textAlign: 'right' }}>
                            {dc.annual_value != null ? `$${dc.annual_value.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}` : 'Not available'}
                          </td>
                          <td style={{ padding: '12px 16px', fontSize: 'var(--font-size-sm)' }}>
                            {dc.status ? (
                              <span style={{ color: statusColors[dc.status] || colors.textMuted, textTransform: 'capitalize' }}>
                                {dc.status.replace('_', ' ')}
                              </span>
                            ) : 'Not available'}
                          </td>
                          <td style={{ padding: '12px 16px', fontSize: 'var(--font-size-sm)', color: colors.textMuted, textTransform: 'capitalize' }}>
                            {dc.strategic_tier || 'Not available'}
                          </td>
                          <td style={{ padding: '12px 16px', fontSize: 'var(--font-size-sm)', color: colors.darkText, textAlign: 'right' }}>
                            {dc.supplier_count}
                          </td>
                          <td style={{ padding: '12px 16px', fontSize: 'var(--font-size-sm)', color: colors.darkText, textAlign: 'right' }}>
                            {dc.material_count}
                          </td>
                          <td style={{ padding: '12px 16px', fontSize: 'var(--font-size-sm)', color: colors.textMuted, textTransform: 'capitalize' }}>
                            {dc.source || 'Not available'}
                          </td>
                          <td style={{ padding: '12px 16px', textAlign: 'right' }}>
                            <div style={{ display: 'flex', gap: '4px', justifyContent: 'flex-end' }}>
                              {dc.record_status === 'pending_review' && (
                                <>
                                  <button
                                    onClick={(e) => { e.stopPropagation(); handleApproveDc(dc.id, dc.name); }}
                                    disabled={dcApprovingId === dc.id}
                                    title="Approve"
                                    style={{ background: 'none', border: 'none', cursor: dcApprovingId === dc.id ? 'not-allowed' : 'pointer', padding: '4px', color: '#059669' }}
                                  >
                                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
                                  </button>
                                </>
                              )}
                              <button
                                onClick={(e) => { e.stopPropagation(); handleDeleteDc(dc.id, dc.name); }}
                                title={dc.record_status === 'pending_review' ? 'Reject' : 'Delete'}
                                style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '4px', color: colors.textMuted }}
                              >
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round">
                                  <polyline points="3 6 5 6 21 6"/><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/>
                                </svg>
                              </button>
                            </div>
                          </td>
                        </tr>

                        {/* Expanded Detail */}
                        {isExpanded && (
                          <tr>
                            <td colSpan={9} style={{ padding: 0 }}>
                              <div style={{ padding: '20px', borderBottom: `1px solid ${colors.mediumGray}`, backgroundColor: `${colors.accent}03` }}>
                                {expandedDcLoading ? (
                                  <div style={{ padding: '20px', textAlign: 'center', color: colors.textMuted }}>Loading details...</div>
                                ) : expandedDcData ? (
                                  <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
                                    {/* Edit / View toggle */}
                                    <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px' }}>
                                      {editingDcId === dc.id ? (
                                        <>
                                          <button onClick={() => setEditingDcId(null)} style={{ padding: '6px 12px', fontSize: 'var(--font-size-sm)', border: `1px solid ${colors.mediumGray}`, borderRadius: '6px', backgroundColor: 'transparent', cursor: 'pointer' }}>Cancel</button>
                                          <button onClick={() => handleUpdateDc(dc.id)} disabled={savingDc} style={{ padding: '6px 12px', fontSize: 'var(--font-size-sm)', border: 'none', borderRadius: '6px', backgroundColor: colors.accent, color: colors.white, cursor: savingDc ? 'not-allowed' : 'pointer', fontWeight: 'var(--font-weight-semibold)' }}>
                                            {savingDc ? 'Saving...' : 'Save'}
                                          </button>
                                        </>
                                      ) : (
                                        <button onClick={() => { setEditingDcId(dc.id); setEditDcForm({ name: expandedDcData.name || '', context_type: expandedDcData.context_type || '', annual_value: expandedDcData.annual_value != null ? expandedDcData.annual_value : '', status: expandedDcData.status || '', strategic_tier: expandedDcData.strategic_tier || '', margin_notes: expandedDcData.margin_notes || '', strategic_notes: expandedDcData.strategic_notes || '', key_contacts: expandedDcData.key_contacts || '' }); }} style={{ padding: '6px 12px', fontSize: 'var(--font-size-sm)', border: `1px solid ${colors.mediumGray}`, borderRadius: '6px', backgroundColor: 'transparent', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '4px' }}>
                                          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
                                          Edit
                                        </button>
                                      )}
                                    </div>

                                    {/* Context Fields */}
                                    {editingDcId === dc.id ? (
                                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '12px' }}>
                                        <div>
                                          <label style={{ display: 'block', marginBottom: '4px', fontSize: 'var(--font-size-sm)', fontWeight: 'var(--font-weight-medium)', color: colors.darkText }}>Name</label>
                                          <input type="text" value={editDcForm.name} onChange={(e) => setEditDcForm({ ...editDcForm, name: e.target.value })} style={{ width: '100%', padding: '8px', border: `1px solid ${colors.mediumGray}`, borderRadius: '6px', fontSize: 'var(--font-size-sm)', boxSizing: 'border-box' }} />
                                        </div>
                                        <div>
                                          <label style={{ display: 'block', marginBottom: '4px', fontSize: 'var(--font-size-sm)', fontWeight: 'var(--font-weight-medium)', color: colors.darkText }}>Type</label>
                                          <select value={editDcForm.context_type} onChange={(e) => setEditDcForm({ ...editDcForm, context_type: e.target.value })} style={{ width: '100%', padding: '8px', border: `1px solid ${colors.mediumGray}`, borderRadius: '6px', fontSize: 'var(--font-size-sm)', boxSizing: 'border-box', backgroundColor: colors.white }}>
                                            <option value="">Select...</option>
                                            <option value="account">Account</option>
                                            <option value="product">Product</option>
                                            <option value="program">Program</option>
                                            <option value="project">Project</option>
                                          </select>
                                        </div>
                                        <div>
                                          <label style={{ display: 'block', marginBottom: '4px', fontSize: 'var(--font-size-sm)', fontWeight: 'var(--font-weight-medium)', color: colors.darkText }}>Annual Value</label>
                                          <input type="number" value={editDcForm.annual_value} onChange={(e) => setEditDcForm({ ...editDcForm, annual_value: e.target.value })} style={{ width: '100%', padding: '8px', border: `1px solid ${colors.mediumGray}`, borderRadius: '6px', fontSize: 'var(--font-size-sm)', boxSizing: 'border-box' }} />
                                        </div>
                                        <div>
                                          <label style={{ display: 'block', marginBottom: '4px', fontSize: 'var(--font-size-sm)', fontWeight: 'var(--font-weight-medium)', color: colors.darkText }}>Status</label>
                                          <select value={editDcForm.status} onChange={(e) => setEditDcForm({ ...editDcForm, status: e.target.value })} style={{ width: '100%', padding: '8px', border: `1px solid ${colors.mediumGray}`, borderRadius: '6px', fontSize: 'var(--font-size-sm)', boxSizing: 'border-box', backgroundColor: colors.white }}>
                                            <option value="">Select...</option>
                                            <option value="growing">Growing</option>
                                            <option value="stable">Stable</option>
                                            <option value="declining">Declining</option>
                                            <option value="at_risk">At Risk</option>
                                            <option value="on_hold">On Hold</option>
                                            <option value="new">New</option>
                                          </select>
                                        </div>
                                        <div>
                                          <label style={{ display: 'block', marginBottom: '4px', fontSize: 'var(--font-size-sm)', fontWeight: 'var(--font-weight-medium)', color: colors.darkText }}>Strategic Tier</label>
                                          <select value={editDcForm.strategic_tier} onChange={(e) => setEditDcForm({ ...editDcForm, strategic_tier: e.target.value })} style={{ width: '100%', padding: '8px', border: `1px solid ${colors.mediumGray}`, borderRadius: '6px', fontSize: 'var(--font-size-sm)', boxSizing: 'border-box', backgroundColor: colors.white }}>
                                            <option value="">Select...</option>
                                            <option value="strategic">Strategic</option>
                                            <option value="key">Key</option>
                                            <option value="managed">Managed</option>
                                            <option value="house">House</option>
                                            <option value="other">Other</option>
                                          </select>
                                        </div>
                                        <div style={{ gridColumn: '1 / -1' }}>
                                          <label style={{ display: 'block', marginBottom: '4px', fontSize: 'var(--font-size-sm)', fontWeight: 'var(--font-weight-medium)', color: colors.darkText }}>Strategic Notes</label>
                                          <textarea value={editDcForm.strategic_notes} onChange={(e) => setEditDcForm({ ...editDcForm, strategic_notes: e.target.value })} rows={2} style={{ width: '100%', padding: '8px', border: `1px solid ${colors.mediumGray}`, borderRadius: '6px', fontSize: 'var(--font-size-sm)', boxSizing: 'border-box', resize: 'vertical' }} />
                                        </div>
                                        <div style={{ gridColumn: '1 / -1' }}>
                                          <label style={{ display: 'block', marginBottom: '4px', fontSize: 'var(--font-size-sm)', fontWeight: 'var(--font-weight-medium)', color: colors.darkText }}>Key Contacts</label>
                                          <textarea value={editDcForm.key_contacts} onChange={(e) => setEditDcForm({ ...editDcForm, key_contacts: e.target.value })} rows={2} style={{ width: '100%', padding: '8px', border: `1px solid ${colors.mediumGray}`, borderRadius: '6px', fontSize: 'var(--font-size-sm)', boxSizing: 'border-box', resize: 'vertical' }} />
                                        </div>
                                      </div>
                                    ) : (
                                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '12px' }}>
                                        {[
                                          { label: 'Type', value: expandedDcData.context_type },
                                          { label: 'Annual Value', value: expandedDcData.annual_value != null ? `$${expandedDcData.annual_value.toLocaleString()}` : null },
                                          { label: 'Status', value: expandedDcData.status?.replace('_', ' ') },
                                          { label: 'Strategic Tier', value: expandedDcData.strategic_tier },
                                          { label: 'Source', value: expandedDcData.source },
                                          { label: 'Record Status', value: expandedDcData.record_status },
                                        ].filter(f => f.value).map(f => (
                                          <div key={f.label}>
                                            <div style={{ fontSize: '11px', fontWeight: 'var(--font-weight-semibold)', color: colors.textMuted, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '4px' }}>{f.label}</div>
                                            <div style={{ fontSize: 'var(--font-size-sm)', color: colors.darkText, textTransform: 'capitalize' }}>{f.value}</div>
                                          </div>
                                        ))}
                                        {expandedDcData.strategic_notes && (
                                          <div style={{ gridColumn: '1 / -1' }}>
                                            <div style={{ fontSize: '11px', fontWeight: 'var(--font-weight-semibold)', color: colors.textMuted, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '4px' }}>Strategic Notes</div>
                                            <div style={{ fontSize: 'var(--font-size-sm)', color: colors.darkText, whiteSpace: 'pre-wrap' }}>{expandedDcData.strategic_notes}</div>
                                          </div>
                                        )}
                                        {expandedDcData.key_contacts && (
                                          <div style={{ gridColumn: '1 / -1' }}>
                                            <div style={{ fontSize: '11px', fontWeight: 'var(--font-weight-semibold)', color: colors.textMuted, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '4px' }}>Key Contacts</div>
                                            <div style={{ fontSize: 'var(--font-size-sm)', color: colors.darkText, whiteSpace: 'pre-wrap' }}>{expandedDcData.key_contacts}</div>
                                          </div>
                                        )}
                                      </div>
                                    )}

                                    {/* Linked Suppliers */}
                                    <div>
                                      <div style={{ fontSize: '11px', fontWeight: 'var(--font-weight-semibold)', color: colors.textMuted, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '8px' }}>
                                        Linked Suppliers ({(expandedDcData.suppliers || []).length})
                                      </div>
                                      {(expandedDcData.suppliers || []).length > 0 && (
                                        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', marginBottom: '8px' }}>
                                          {expandedDcData.suppliers.map(s => (
                                            <div key={s.supplier_id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '6px 10px', backgroundColor: colors.white, border: `1px solid ${colors.lightGray}`, borderRadius: '6px' }}>
                                              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                                <span style={{ fontSize: 'var(--font-size-sm)', color: colors.darkText }}>{s.supplier_name}</span>
                                                {s.criticality && (
                                                  <span style={{ fontSize: '10px', padding: '1px 6px', borderRadius: '4px', backgroundColor: s.criticality === 'critical' ? '#DC262615' : s.criticality === 'important' ? '#F59E0B15' : '#6B728015', color: s.criticality === 'critical' ? '#DC2626' : s.criticality === 'important' ? '#F59E0B' : '#6B7280', fontWeight: 'var(--font-weight-semibold)' }}>
                                                    {s.criticality}
                                                  </span>
                                                )}
                                              </div>
                                              <button onClick={() => handleUnlinkDcSupplier(dc.id, s.supplier_id)} title="Remove" style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '2px', color: colors.textMuted }}>
                                                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5}><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
                                              </button>
                                            </div>
                                          ))}
                                        </div>
                                      )}
                                      <div style={{ position: 'relative' }}>
                                        <input
                                          type="text"
                                          value={dcSupplierSearch}
                                          onChange={(e) => setDcSupplierSearch(e.target.value)}
                                          placeholder="Search suppliers to link..."
                                          style={{ width: '100%', padding: '8px 10px', border: `1px solid ${colors.mediumGray}`, borderRadius: '6px', fontSize: 'var(--font-size-sm)', boxSizing: 'border-box' }}
                                        />
                                        {dcFilteredSuppliers.length > 0 && (
                                          <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 10, backgroundColor: colors.white, border: `1px solid ${colors.mediumGray}`, borderRadius: '6px', marginTop: '2px', maxHeight: '200px', overflowY: 'auto', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }}>
                                            {dcFilteredSuppliers.map(s => (
                                              <div key={s.supplier_id} onClick={() => handleLinkDcSupplier(dc.id, s.supplier_id)} style={{ padding: '8px 10px', fontSize: 'var(--font-size-sm)', cursor: 'pointer', borderBottom: `1px solid ${colors.lightGray}` }}
                                                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = `${colors.accent}08`}
                                                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                                              >
                                                {s.supplier_name}
                                              </div>
                                            ))}
                                          </div>
                                        )}
                                      </div>
                                    </div>

                                    {/* Linked Materials */}
                                    <div>
                                      <div style={{ fontSize: '11px', fontWeight: 'var(--font-weight-semibold)', color: colors.textMuted, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '8px' }}>
                                        Linked Materials ({(expandedDcData.materials || []).length})
                                      </div>
                                      {(expandedDcData.materials || []).length > 0 && (
                                        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', marginBottom: '8px' }}>
                                          {expandedDcData.materials.map(m => (
                                            <div key={m.material_number} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '6px 10px', backgroundColor: colors.white, border: `1px solid ${colors.lightGray}`, borderRadius: '6px' }}>
                                              <div>
                                                <span style={{ fontSize: 'var(--font-size-sm)', color: colors.darkText }}>{m.material_number}</span>
                                                {m.material_name && <span style={{ fontSize: 'var(--font-size-sm)', color: colors.textMuted, marginLeft: '8px' }}>{m.material_name}</span>}
                                              </div>
                                              <button onClick={() => handleUnlinkDcMaterial(dc.id, m.material_number)} title="Remove" style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '2px', color: colors.textMuted }}>
                                                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5}><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
                                              </button>
                                            </div>
                                          ))}
                                        </div>
                                      )}
                                      <div style={{ display: 'flex', gap: '8px' }}>
                                        <input
                                          type="text"
                                          value={dcMaterialSearch}
                                          onChange={(e) => setDcMaterialSearch(e.target.value)}
                                          placeholder="Enter material number (e.g., MAT-100001)"
                                          style={{ flex: 1, padding: '8px 10px', border: `1px solid ${colors.mediumGray}`, borderRadius: '6px', fontSize: 'var(--font-size-sm)', boxSizing: 'border-box' }}
                                        />
                                        <button
                                          onClick={() => { if (dcMaterialSearch.trim()) handleLinkDcMaterial(dc.id, dcMaterialSearch.trim()); }}
                                          disabled={dcLinkingMaterial || !dcMaterialSearch.trim()}
                                          style={{ padding: '8px 12px', fontSize: 'var(--font-size-sm)', border: 'none', borderRadius: '6px', backgroundColor: colors.accent, color: colors.white, cursor: (!dcMaterialSearch.trim() || dcLinkingMaterial) ? 'not-allowed' : 'pointer', opacity: (!dcMaterialSearch.trim() || dcLinkingMaterial) ? 0.6 : 1, fontWeight: 'var(--font-weight-semibold)', whiteSpace: 'nowrap' }}
                                        >
                                          {dcLinkingMaterial ? 'Linking...' : 'Link'}
                                        </button>
                                      </div>
                                    </div>

                                    {/* Intelligence Entries */}
                                    <div style={{ marginTop: '16px', borderTop: `1px solid ${colors.lightGray}`, paddingTop: '16px' }}>
                                      <div style={{ fontSize: '11px', fontWeight: 'var(--font-weight-semibold)', color: colors.textMuted, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '8px' }}>
                                        Intelligence ({(expandedDcData.intelligence || []).length})
                                      </div>
                                      {(expandedDcData.intelligence || []).length > 0 && (
                                        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginBottom: '12px' }}>
                                          {expandedDcData.intelligence.map(entry => {
                                            const tagColors = {
                                              commercial: { bg: '#2251FF12', text: '#2251FF' },
                                              headwinds: { bg: '#DC262612', text: '#DC2626' },
                                              tailwinds: { bg: '#05966912', text: '#059669' },
                                              pipeline: { bg: '#7C3AED12', text: '#7C3AED' },
                                              risk: { bg: '#DC262612', text: '#DC2626' },
                                              relationship: { bg: '#0D948812', text: '#0D9488' },
                                              margin: { bg: '#D9770612', text: '#D97706' },
                                              competition: { bg: '#6B728012', text: '#6B7280' },
                                            };
                                            const entryTags = (entry.tags || '').split(',').map(t => t.trim()).filter(Boolean);
                                            return (
                                              <div key={entry.id} style={{ padding: '10px 12px', backgroundColor: colors.white, border: `1px solid ${colors.lightGray}`, borderRadius: '6px' }}>
                                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '6px' }}>
                                                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
                                                    {entryTags.map(tag => {
                                                      const tc = tagColors[tag] || { bg: '#6B728012', text: '#6B7280' };
                                                      return (
                                                        <span key={tag} style={{ fontSize: '10px', padding: '1px 6px', borderRadius: '4px', backgroundColor: tc.bg, color: tc.text, fontWeight: 'var(--font-weight-semibold)' }}>
                                                          {tag}
                                                        </span>
                                                      );
                                                    })}
                                                  </div>
                                                  <button onClick={() => handleDeleteDcIntelligence(dc.id, entry.id)} title="Delete" style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '2px', color: colors.textMuted, flexShrink: 0 }}>
                                                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5}><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
                                                  </button>
                                                </div>
                                                <div style={{ fontSize: 'var(--font-size-sm)', color: colors.darkText, lineHeight: '1.5', whiteSpace: 'pre-wrap' }}>{entry.content}</div>
                                                <div style={{ fontSize: '10px', color: colors.textMuted, marginTop: '6px' }}>
                                                  {entry.source_type === 'document_extraction' ? 'Extracted from document' : entry.source_type === 'manual_note' ? 'Manual note' : entry.source_type}
                                                  {entry.contributed_by && ` by ${entry.contributed_by}`}
                                                  {entry.created_at && ` \u00b7 ${new Date(entry.created_at).toLocaleDateString()}`}
                                                </div>
                                              </div>
                                            );
                                          })}
                                        </div>
                                      )}
                                      {/* Add intelligence note */}
                                      <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                                        <textarea
                                          value={dcIntelNote}
                                          onChange={(e) => setDcIntelNote(e.target.value)}
                                          placeholder="Add an intelligence note..."
                                          rows={2}
                                          style={{ width: '100%', padding: '8px 10px', border: `1px solid ${colors.mediumGray}`, borderRadius: '6px', fontSize: 'var(--font-size-sm)', boxSizing: 'border-box', resize: 'vertical', fontFamily: 'inherit' }}
                                        />
                                        <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                                          <select
                                            value={dcIntelTags}
                                            onChange={(e) => setDcIntelTags(e.target.value)}
                                            style={{ flex: 1, padding: '6px 10px', border: `1px solid ${colors.mediumGray}`, borderRadius: '6px', fontSize: 'var(--font-size-sm)' }}
                                          >
                                            <option value="">Select tag...</option>
                                            <option value="commercial">Commercial</option>
                                            <option value="headwinds">Headwinds</option>
                                            <option value="tailwinds">Tailwinds</option>
                                            <option value="pipeline">Pipeline</option>
                                            <option value="risk">Risk</option>
                                            <option value="relationship">Relationship</option>
                                            <option value="margin">Margin</option>
                                            <option value="competition">Competition</option>
                                          </select>
                                          <button
                                            onClick={() => handleAddDcIntelligence(dc.id)}
                                            disabled={dcAddingIntel || !dcIntelNote.trim()}
                                            style={{ padding: '6px 12px', fontSize: 'var(--font-size-sm)', border: 'none', borderRadius: '6px', backgroundColor: colors.accent, color: colors.white, cursor: (!dcIntelNote.trim() || dcAddingIntel) ? 'not-allowed' : 'pointer', opacity: (!dcIntelNote.trim() || dcAddingIntel) ? 0.6 : 1, fontWeight: 'var(--font-weight-semibold)', whiteSpace: 'nowrap' }}
                                          >
                                            {dcAddingIntel ? 'Adding...' : 'Add'}
                                          </button>
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                ) : null}
                              </div>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* ── PPI Profiles Tab ── */}
      {activeTab === 'ppi_profiles' && (
        <div>
          {/* Summary Bar */}
          <div style={{
            display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '20px',
            padding: '16px 20px', backgroundColor: colors.white, borderRadius: '12px',
            border: `1px solid ${colors.mediumGray}`,
          }}>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 'var(--font-size-sm)', fontWeight: 'var(--font-weight-semibold)', color: colors.darkText }}>
                {ppiSummary.mapped_count} of {ppiSummary.total_with_spend} suppliers mapped
              </div>
              <div style={{ fontSize: 'var(--font-size-xs)', color: colors.textMuted, marginTop: '2px' }}>
                Showing suppliers with 12-month spend, sorted by spend descending
              </div>
            </div>
            {ppiBulk ? (
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <div style={{ fontSize: 'var(--font-size-xs)', color: colors.darkText }}>
                  {ppiBulk.cancelled
                    ? `Cancelled. ${ppiBulk.completed} of ${ppiBulk.total} mapped.`
                    : ppiBulk.current
                      ? `Mapping ${ppiBulk.completed + 1} of ${ppiBulk.total}: ${ppiBulk.current}`
                      : `Done. ${ppiBulk.completed - ppiBulk.failed.length} of ${ppiBulk.total} mapped${ppiBulk.failed.length ? `, ${ppiBulk.failed.length} failed` : ''}.`
                  }
                </div>
                {ppiBulk.current && (
                  <button
                    onClick={handleCancelBulkPpi}
                    style={{
                      padding: '6px 14px', backgroundColor: colors.danger, color: colors.white,
                      border: 'none', borderRadius: '8px', fontSize: 'var(--font-size-xs)',
                      fontWeight: 'var(--font-weight-semibold)', cursor: 'pointer',
                    }}
                  >Stop</button>
                )}
                {!ppiBulk.current && (
                  <button
                    onClick={() => setPpiBulk(null)}
                    style={{
                      padding: '6px 14px', backgroundColor: colors.mediumGray, color: colors.darkText,
                      border: 'none', borderRadius: '8px', fontSize: 'var(--font-size-xs)',
                      fontWeight: 'var(--font-weight-semibold)', cursor: 'pointer',
                    }}
                  >Dismiss</button>
                )}
              </div>
            ) : (
              <div style={{ display: 'flex', gap: '8px' }}>
                <button
                  onClick={() => setShowPpiBulkConfirm('top20')}
                  disabled={ppiLoading}
                  style={{
                    padding: '8px 16px', backgroundColor: '#7C3AED', color: colors.white,
                    border: 'none', borderRadius: '8px', fontSize: 'var(--font-size-xs)',
                    fontWeight: 'var(--font-weight-semibold)', cursor: 'pointer',
                  }}
                >Map Top 20 by Spend</button>
                <button
                  onClick={() => setShowPpiBulkConfirm('all')}
                  disabled={ppiLoading}
                  style={{
                    padding: '8px 16px', backgroundColor: colors.white, color: '#7C3AED',
                    border: `1px solid #7C3AED`, borderRadius: '8px', fontSize: 'var(--font-size-xs)',
                    fontWeight: 'var(--font-weight-semibold)', cursor: 'pointer',
                  }}
                >Map All Unmapped</button>
              </div>
            )}
          </div>

          {/* Bulk Confirmation Modal */}
          {showPpiBulkConfirm && (() => {
            const unmapped = ppiSuppliers.filter(s => s.annual_spend > 0 && s.series_count === 0);
            const count = showPpiBulkConfirm === 'top20' ? Math.min(20, unmapped.length) : unmapped.length;
            const estMinutes = Math.ceil(count * 0.5); // ~30s per mapping
            return (
              <div style={{
                position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
                backgroundColor: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000,
              }}>
                <div style={{
                  backgroundColor: colors.white, borderRadius: '12px', padding: '24px',
                  maxWidth: '420px', width: '90%', boxShadow: '0 8px 32px rgba(0,0,0,0.15)',
                }}>
                  <div style={{ fontSize: 'var(--font-size-base)', fontWeight: 'var(--font-weight-semibold)', color: colors.darkText, marginBottom: '12px' }}>
                    Confirm Bulk PPI Mapping
                  </div>
                  <div style={{ fontSize: 'var(--font-size-sm)', color: colors.textMuted, marginBottom: '16px', lineHeight: 1.5 }}>
                    This will map <strong>{count} unmapped supplier{count !== 1 ? 's' : ''}</strong> to PPI series using AI.
                    Each mapping calls the Claude API and takes ~30 seconds.
                    <br /><br />
                    Estimated time: <strong>~{estMinutes} minute{estMinutes !== 1 ? 's' : ''}</strong>. You can stop at any time.
                  </div>
                  <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
                    <button
                      onClick={() => setShowPpiBulkConfirm(null)}
                      style={{
                        padding: '8px 16px', backgroundColor: colors.mediumGray, color: colors.darkText,
                        border: 'none', borderRadius: '8px', fontSize: 'var(--font-size-xs)',
                        fontWeight: 'var(--font-weight-semibold)', cursor: 'pointer',
                      }}
                    >Cancel</button>
                    <button
                      onClick={() => handleBulkMapPpi(showPpiBulkConfirm)}
                      style={{
                        padding: '8px 16px', backgroundColor: '#7C3AED', color: colors.white,
                        border: 'none', borderRadius: '8px', fontSize: 'var(--font-size-xs)',
                        fontWeight: 'var(--font-weight-semibold)', cursor: 'pointer',
                      }}
                    >Start Mapping</button>
                  </div>
                </div>
              </div>
            );
          })()}

          {/* Search */}
          <div style={{ marginBottom: '16px' }}>
            <input
              type="text"
              placeholder="Search suppliers..."
              value={ppiSearch}
              onChange={e => setPpiSearch(e.target.value)}
              style={{
                width: '300px', padding: '8px 12px', border: `1px solid ${colors.mediumGray}`,
                borderRadius: '8px', fontSize: 'var(--font-size-sm)', outline: 'none',
              }}
            />
          </div>

          {/* Supplier Table */}
          {(() => {
            const ppiFiltered = ppiSuppliers.filter(s =>
              !ppiSearch || s.supplier_name.toLowerCase().includes(ppiSearch.toLowerCase())
            );
            const ppiDisplayed = ppiShowAll ? ppiFiltered : ppiFiltered.slice(0, 50);
            return ppiLoading ? (
            <div style={{ padding: '40px', textAlign: 'center', color: colors.textMuted }}>Loading suppliers...</div>
          ) : (
            <>
              <div style={{
                backgroundColor: colors.white, borderRadius: '12px', border: `1px solid ${colors.mediumGray}`, overflow: 'hidden',
              }}>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr style={{ backgroundColor: '#F9FAFB' }}>
                      {['Supplier Name', '12mo Spend', 'PPI Status', 'Series', 'Last Mapped', ''].map((h, i) => (
                        <th key={i} style={{
                          padding: '10px 16px', fontSize: '11px', fontWeight: 'var(--font-weight-semibold)',
                          color: colors.textMuted, textTransform: 'uppercase', letterSpacing: '0.06em',
                          textAlign: i === 1 ? 'right' : 'left', borderBottom: `1px solid ${colors.mediumGray}`,
                        }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {ppiDisplayed.map(s => {
                        const status = ppiMappingStatus[s.supplier_name] || {};
                        const isMapped = s.series_count > 0;
                        const isExpanded = ppiExpandedSupplier === s.supplier_name;
                        return (
                          <React.Fragment key={s.supplier_name}>
                            <tr
                              onClick={() => isMapped ? fetchPpiProfile(s.supplier_name) : null}
                              style={{
                                cursor: isMapped ? 'pointer' : 'default',
                                backgroundColor: isExpanded ? '#F5F3FF' : ppiBulk?.current === s.supplier_name ? '#FFFBEB' : 'transparent',
                                transition: 'background-color 0.15s',
                              }}
                            >
                              <td style={{ padding: '10px 16px', fontSize: 'var(--font-size-sm)', color: colors.darkText, borderBottom: `1px solid ${colors.lightGray}` }}>
                                {s.supplier_name}
                              </td>
                              <td style={{ padding: '10px 16px', fontSize: 'var(--font-size-sm)', color: colors.darkText, textAlign: 'right', borderBottom: `1px solid ${colors.lightGray}`, fontVariantNumeric: 'tabular-nums' }}>
                                {s.annual_spend > 0 ? `$${s.annual_spend.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}` : 'Not available'}
                              </td>
                              <td style={{ padding: '10px 16px', borderBottom: `1px solid ${colors.lightGray}` }}>
                                <span style={{
                                  display: 'inline-block', padding: '2px 8px', borderRadius: '10px',
                                  fontSize: '11px', fontWeight: 'var(--font-weight-semibold)',
                                  backgroundColor: isMapped ? `${colors.success}15` : `${colors.textMuted}10`,
                                  color: isMapped ? colors.success : colors.textMuted,
                                }}>
                                  {status.loading ? 'Mapping...' : status.error ? 'Error' : isMapped ? 'Mapped' : 'Unmapped'}
                                </span>
                                {status.error && (
                                  <span style={{ marginLeft: '6px', fontSize: '10px', color: colors.danger }}>{status.error}</span>
                                )}
                              </td>
                              <td style={{ padding: '10px 16px', fontSize: 'var(--font-size-sm)', color: colors.textMuted, borderBottom: `1px solid ${colors.lightGray}` }}>
                                {isMapped ? `${s.series_count} series` : 'Not mapped'}
                              </td>
                              <td style={{ padding: '10px 16px', fontSize: 'var(--font-size-sm)', color: colors.textMuted, borderBottom: `1px solid ${colors.lightGray}` }}>
                                {s.mapped_at ? new Date(s.mapped_at).toLocaleDateString() : 'Not mapped'}
                              </td>
                              <td style={{ padding: '10px 16px', borderBottom: `1px solid ${colors.lightGray}`, textAlign: 'right' }}>
                                <button
                                  onClick={(e) => { e.stopPropagation(); handleMapPpi(s.supplier_name); }}
                                  disabled={status.loading || (ppiBulk && ppiBulk.current)}
                                  style={{
                                    padding: '5px 14px', backgroundColor: status.loading ? colors.mediumGray : '#7C3AED',
                                    color: colors.white, border: 'none', borderRadius: '6px',
                                    fontSize: '11px', fontWeight: 'var(--font-weight-semibold)',
                                    cursor: status.loading ? 'not-allowed' : 'pointer',
                                    opacity: status.loading ? 0.7 : 1,
                                  }}
                                >
                                  {status.loading ? 'Mapping...' : isMapped ? 'Remap' : 'Map PPI'}
                                </button>
                              </td>
                            </tr>
                            {/* Expanded profile detail */}
                            {isExpanded && (
                              <tr>
                                <td colSpan={6} style={{ padding: '0 16px 12px 16px', backgroundColor: '#F5F3FF', borderBottom: `1px solid ${colors.lightGray}` }}>
                                  {ppiExpandedLoading ? (
                                    <div style={{ padding: '12px', fontSize: 'var(--font-size-xs)', color: colors.textMuted }}>Loading profile...</div>
                                  ) : ppiExpandedProfile?.exists ? (
                                    <div style={{ padding: '12px 0' }}>
                                      <div style={{ fontSize: '11px', fontWeight: 'var(--font-weight-semibold)', color: colors.darkText, marginBottom: '8px', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                                        PPI Series Assignments
                                      </div>
                                      <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                                        {ppiExpandedProfile.profile.map((p, i) => (
                                          <div key={i} style={{
                                            display: 'flex', alignItems: 'flex-start', gap: '12px',
                                            padding: '8px 12px', backgroundColor: colors.white, borderRadius: '8px',
                                            border: `1px solid ${colors.mediumGray}`,
                                          }}>
                                            <div style={{
                                              minWidth: '42px', padding: '2px 8px', borderRadius: '6px',
                                              backgroundColor: '#7C3AED15', color: '#7C3AED',
                                              fontSize: '11px', fontWeight: 'var(--font-weight-semibold)', textAlign: 'center',
                                            }}>{p.weight_pct}%</div>
                                            <div style={{ flex: 1 }}>
                                              <div style={{ fontSize: 'var(--font-size-xs)', fontWeight: 'var(--font-weight-semibold)', color: colors.darkText }}>
                                                {p.series_name}
                                              </div>
                                              <div style={{ fontSize: '10px', color: colors.textMuted, marginTop: '2px' }}>
                                                {p.series_id} ({p.series_type})
                                              </div>
                                              {p.rationale && (
                                                <div style={{ fontSize: '10px', color: colors.textMuted, marginTop: '4px', fontStyle: 'italic' }}>
                                                  {p.rationale}
                                                </div>
                                              )}
                                            </div>
                                          </div>
                                        ))}
                                      </div>
                                      {ppiExpandedProfile.mapped_at && (
                                        <div style={{ fontSize: '10px', color: colors.textMuted, marginTop: '8px' }}>
                                          Mapped {new Date(ppiExpandedProfile.mapped_at).toLocaleString()}
                                          {ppiExpandedProfile.mapped_by && ` by ${ppiExpandedProfile.mapped_by}`}
                                        </div>
                                      )}
                                    </div>
                                  ) : (
                                    <div style={{ padding: '12px', fontSize: 'var(--font-size-xs)', color: colors.textMuted }}>No profile data found.</div>
                                  )}
                                </td>
                              </tr>
                            )}
                          </React.Fragment>
                        );
                      })}
                  </tbody>
                </table>
              </div>
              {/* Show All toggle */}
              {!ppiShowAll && ppiFiltered.length > 50 && (
                <div style={{ textAlign: 'center', marginTop: '12px' }}>
                  <button
                    onClick={() => setPpiShowAll(true)}
                    style={{
                      padding: '8px 20px', backgroundColor: 'transparent', color: '#7C3AED',
                      border: `1px solid #7C3AED`, borderRadius: '8px', fontSize: 'var(--font-size-xs)',
                      fontWeight: 'var(--font-weight-semibold)', cursor: 'pointer',
                    }}
                  >
                    Show All ({ppiFiltered.length} suppliers)
                  </button>
                </div>
              )}
            </>
          );
          })()}
        </div>
      )}

      {/* ── Intelligence Audit Tab ── */}
      {activeTab === 'intelligence' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
          {/* Audit Controls */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div>
              <h3 style={{ fontSize: 'var(--font-size-lg)', fontWeight: 'var(--font-weight-semibold)', color: colors.darkText, margin: '0 0 4px 0' }}>Intelligence Audit</h3>
              <p style={{ fontSize: 'var(--font-size-sm)', color: colors.textMuted, margin: 0 }}>Automated gap analysis across suppliers, accounts, and categories</p>
            </div>
            {auditStarting || (auditStatus && auditStatus.status === 'running') ? (
              <span style={{ padding: '10px 20px', backgroundColor: colors.mediumGray, color: '#fff', borderRadius: '8px', fontSize: 'var(--font-size-sm)', fontWeight: 'var(--font-weight-semibold)', display: 'inline-block' }}>
                {auditStarting ? 'Starting...' : `Running... (${auditStatus.completed || 0}/${auditStatus.total || '?'})`}
              </span>
            ) : (
              <div style={{ display: 'flex', gap: '8px' }}>
                <button
                  onClick={() => startAudit('test')}
                  style={{ padding: '10px 16px', backgroundColor: '#F0F4FF', color: colors.accent, border: `1px solid ${colors.accent}`, borderRadius: '8px', cursor: 'pointer', fontSize: 'var(--font-size-sm)', fontWeight: 'var(--font-weight-semibold)' }}
                >
                  Quick Test (8 entities)
                </button>
                <button
                  onClick={() => startAudit('full')}
                  style={{ padding: '10px 16px', backgroundColor: colors.accent, color: '#fff', border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: 'var(--font-size-sm)', fontWeight: 'var(--font-weight-semibold)' }}
                >
                  Full Audit (all entities)
                </button>
              </div>
            )}
          </div>

          {/* Running Progress */}
          {auditStatus && auditStatus.status === 'running' && (
            <div style={{ backgroundColor: '#F0F4FF', borderRadius: '8px', padding: '16px', border: '1px solid #D4DEFF' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                <span style={{ fontSize: 'var(--font-size-sm)', fontWeight: 'var(--font-weight-medium)', color: colors.darkText }}>
                  Analyzing: {auditStatus.current_entity || 'preparing...'} ({(auditStatus.phase || '').replace('_', ' ')})
                </span>
                <span style={{ fontSize: 'var(--font-size-sm)', color: colors.textMuted }}>
                  {auditStatus.completed || 0} / {auditStatus.total || '?'} entities
                </span>
              </div>
              <div style={{ height: '6px', backgroundColor: '#D4DEFF', borderRadius: '3px', overflow: 'hidden' }}>
                <div style={{ height: '100%', width: `${auditStatus.total ? (auditStatus.completed / auditStatus.total * 100) : 0}%`, backgroundColor: colors.accent, borderRadius: '3px', transition: 'width 0.3s' }} />
              </div>
            </div>
          )}

          {auditError && (
            <div style={{ padding: '12px 16px', backgroundColor: '#FEF2F2', border: '1px solid #FECACA', borderRadius: '8px', color: '#DC2626', fontSize: 'var(--font-size-sm)' }}>{auditError}</div>
          )}

          {/* Stats Cards */}
          {auditSummary && auditSummary.total_gaps > 0 && (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '16px' }}>
              {[
                { label: 'Total Gaps', value: auditSummary.total_gaps, color: colors.accent },
                { label: 'Critical Open', value: (auditSummary.by_priority || {}).critical || 0, color: '#DC2626' },
                { label: 'Closure Rate', value: `${auditSummary.closure_rate || 0}%`, color: '#059669' },
                { label: 'Last Audit', value: auditSummary.last_audit?.completed_at ? new Date(auditSummary.last_audit.completed_at).toLocaleDateString() : 'Never', color: colors.textMuted },
              ].map((stat, i) => (
                <div key={i} style={{ backgroundColor: '#fff', borderRadius: '10px', padding: '16px 20px', border: `1px solid ${colors.mediumGray}` }}>
                  <div style={{ fontSize: '11px', textTransform: 'uppercase', fontWeight: 600, letterSpacing: '0.06em', color: colors.textMuted, marginBottom: '4px' }}>{stat.label}</div>
                  <div style={{ fontSize: 'var(--font-size-xl)', fontWeight: 'var(--font-weight-bold)', color: colors.darkText }}>{stat.value}</div>
                </div>
              ))}
            </div>
          )}

          {/* Heat Map */}
          {allGapsForHeatMap.length > 0 && (
            <IntelligenceHeatMap
              gaps={allGapsForHeatMap}
              visible={heatMapVisible}
              setVisible={setHeatMapVisible}
              selectedCell={heatMapSelectedCell}
              setSelectedCell={setHeatMapSelectedCell}
              onStatusUpdate={(gapId) => { updateGapStatus(gapId, 'closed'); setAllGapsForHeatMap(prev => prev.map(g => g.id === gapId ? { ...g, status: 'closed' } : g)); }}
              updatingGapId={updatingGapId}
              formatGapCategory={formatGapCategory}
            />
          )}

          {/* No audit yet */}
          {!auditSummary && !auditLoading && !(auditStatus && auditStatus.status === 'running') && (
            <div style={{ padding: '40px', textAlign: 'center', backgroundColor: '#F9FAFB', borderRadius: '10px', border: `1px dashed ${colors.mediumGray}` }}>
              <p style={{ color: colors.textMuted, fontSize: 'var(--font-size-base)', margin: 0 }}>No audit has been run yet. Use "Quick Test" to audit 8 key entities, or "Full Audit" to analyze all suppliers, accounts, and categories.</p>
            </div>
          )}

          {/* Filter Bar */}
          {auditSummary && auditSummary.total_gaps > 0 && (
            <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', alignItems: 'center' }}>
              {[
                { key: 'priority', label: 'Priority', options: ['', 'critical', 'high', 'medium', 'low'] },
                { key: 'status', label: 'Status', options: ['', 'open', 'partially_answered', 'closed'] },
                { key: 'entity_type', label: 'Type', options: ['', 'supplier', 'account', 'category'] },
              ].map(f => (
                <select key={f.key} value={gapFilters[f.key]} onChange={e => { setGapFilters(prev => ({ ...prev, [f.key]: e.target.value })); setGapPage(1); }} style={{ padding: '8px 12px', borderRadius: '6px', border: `1px solid ${colors.mediumGray}`, fontSize: 'var(--font-size-sm)', color: colors.darkText, backgroundColor: '#fff' }}>
                  <option value="">{f.label}: All</option>
                  {f.options.filter(Boolean).map(o => <option key={o} value={o}>{formatGapCategory(o)}</option>)}
                </select>
              ))}
              <input type="text" placeholder="Search entity name..." value={gapFilters.search} onChange={e => { setGapFilters(prev => ({ ...prev, search: e.target.value })); setGapPage(1); }} style={{ padding: '8px 12px', borderRadius: '6px', border: `1px solid ${colors.mediumGray}`, fontSize: 'var(--font-size-sm)', flex: '1', minWidth: '180px' }} />
            </div>
          )}

          {/* Gap Table */}
          {auditGaps.gaps && auditGaps.gaps.length > 0 && (
            <div style={{ backgroundColor: '#fff', borderRadius: '10px', border: `1px solid ${colors.mediumGray}`, overflow: 'hidden' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ backgroundColor: '#F9FAFB' }}>
                    {['Entity', 'Type', 'Gap Category', 'Priority', 'Status', 'Evidence'].map(h => (
                      <th key={h} style={{ padding: '10px 14px', textAlign: 'left', fontSize: '11px', textTransform: 'uppercase', fontWeight: 600, letterSpacing: '0.06em', color: colors.textMuted, borderBottom: `1px solid ${colors.mediumGray}` }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {auditGaps.gaps.map(gap => (
                    <React.Fragment key={gap.id}>
                      <tr onClick={() => setExpandedGapId(expandedGapId === gap.id ? null : gap.id)} style={{ cursor: 'pointer', borderBottom: `1px solid ${colors.lightGray}` }}>
                        <td style={{ padding: '10px 14px', fontSize: 'var(--font-size-sm)', fontWeight: 'var(--font-weight-medium)', color: colors.darkText }}>{gap.entity_name}</td>
                        <td style={{ padding: '10px 14px', fontSize: 'var(--font-size-sm)', color: colors.textMuted }}>{gap.entity_type}</td>
                        <td style={{ padding: '10px 14px', fontSize: 'var(--font-size-sm)', color: colors.darkText }}>{formatGapCategory(gap.gap_category)}</td>
                        <td style={{ padding: '10px 14px' }}><span style={{ padding: '2px 8px', borderRadius: '4px', fontSize: '11px', fontWeight: 600, backgroundColor: `${priorityColors[gap.priority]}15`, color: priorityColors[gap.priority] }}>{gap.priority}</span></td>
                        <td style={{ padding: '10px 14px' }}><span style={{ padding: '2px 8px', borderRadius: '4px', fontSize: '11px', fontWeight: 600, backgroundColor: `${statusColors[gap.status] || '#8A94A6'}15`, color: statusColors[gap.status] || '#8A94A6' }}>{formatGapCategory(gap.status)}</span></td>
                        <td style={{ padding: '10px 14px', fontSize: 'var(--font-size-sm)', color: colors.textMuted, maxWidth: '300px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{(gap.evidence || '').slice(0, 120)}{(gap.evidence || '').length > 120 ? '...' : ''}</td>
                      </tr>
                      {expandedGapId === gap.id && (
                        <tr>
                          <td colSpan={6} style={{ padding: '16px 20px', backgroundColor: '#F9FAFB', borderBottom: `1px solid ${colors.mediumGray}` }}>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                              <div>
                                <div style={{ fontSize: '11px', textTransform: 'uppercase', fontWeight: 600, color: colors.textMuted, marginBottom: '4px' }}>Evidence</div>
                                <div style={{ fontSize: 'var(--font-size-sm)', color: colors.darkText, lineHeight: 1.5 }}>{gap.evidence || 'No evidence recorded'}</div>
                              </div>
                              {gap.suggested_questions && gap.suggested_questions.length > 0 && (
                                <div>
                                  <div style={{ fontSize: '11px', textTransform: 'uppercase', fontWeight: 600, color: colors.textMuted, marginBottom: '4px' }}>Suggested Questions</div>
                                  <ol style={{ margin: 0, paddingLeft: '20px', fontSize: 'var(--font-size-sm)', color: colors.darkText, lineHeight: 1.6 }}>
                                    {gap.suggested_questions.map((q, i) => <li key={i}>{q}</li>)}
                                  </ol>
                                </div>
                              )}
                              {gap.best_role && <div style={{ fontSize: 'var(--font-size-sm)', color: colors.textMuted }}>Best role to answer: <strong>{formatGapCategory(gap.best_role)}</strong></div>}
                              {gap.closure_date && <div style={{ fontSize: 'var(--font-size-sm)', color: colors.textMuted }}>Closed: {new Date(gap.closure_date).toLocaleDateString()} via {gap.closure_source_type || 'unknown'}</div>}
                              <div style={{ display: 'flex', gap: '8px' }}>
                                {gap.status !== 'closed' && (
                                  <button onClick={(e) => { e.stopPropagation(); updateGapStatus(gap.id, 'closed'); }} disabled={updatingGapId === gap.id} style={{ padding: '6px 14px', fontSize: 'var(--font-size-sm)', backgroundColor: '#059669', color: '#fff', border: 'none', borderRadius: '6px', cursor: 'pointer' }}>
                                    {updatingGapId === gap.id ? 'Updating...' : 'Mark as Closed'}
                                  </button>
                                )}
                                {gap.status === 'closed' && (
                                  <button onClick={(e) => { e.stopPropagation(); updateGapStatus(gap.id, 'open'); }} disabled={updatingGapId === gap.id} style={{ padding: '6px 14px', fontSize: 'var(--font-size-sm)', backgroundColor: '#D97706', color: '#fff', border: 'none', borderRadius: '6px', cursor: 'pointer' }}>
                                    {updatingGapId === gap.id ? 'Updating...' : 'Reopen'}
                                  </button>
                                )}
                              </div>
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  ))}
                </tbody>
              </table>
              {/* Pagination */}
              {auditGaps.total_pages > 1 && (
                <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '12px', padding: '12px' }}>
                  <button onClick={() => setGapPage(Math.max(1, gapPage - 1))} disabled={gapPage <= 1} style={{ padding: '6px 12px', border: `1px solid ${colors.mediumGray}`, borderRadius: '6px', backgroundColor: '#fff', cursor: gapPage <= 1 ? 'not-allowed' : 'pointer', fontSize: 'var(--font-size-sm)' }}>Previous</button>
                  <span style={{ fontSize: 'var(--font-size-sm)', color: colors.textMuted }}>Page {gapPage} of {auditGaps.total_pages}</span>
                  <button onClick={() => setGapPage(Math.min(auditGaps.total_pages, gapPage + 1))} disabled={gapPage >= auditGaps.total_pages} style={{ padding: '6px 12px', border: `1px solid ${colors.mediumGray}`, borderRadius: '6px', backgroundColor: '#fff', cursor: gapPage >= auditGaps.total_pages ? 'not-allowed' : 'pointer', fontSize: 'var(--font-size-sm)' }}>Next</button>
                </div>
              )}
            </div>
          )}

          {/* Empty state after audit with no results matching filter */}
          {auditSummary && auditSummary.total_gaps > 0 && auditGaps.gaps && auditGaps.gaps.length === 0 && (
            <div style={{ padding: '24px', textAlign: 'center', color: colors.textMuted, fontSize: 'var(--font-size-sm)' }}>No gaps match the current filters.</div>
          )}

          {auditLoading && <div style={{ padding: '40px', textAlign: 'center', color: colors.textMuted }}>Loading audit data...</div>}
        </div>
      )}

      {/* ── Chat Capture Tab (D.6) ── */}
      {activeTab === 'chat_capture' && (() => {
        const isOn = Boolean(company?.chat_capture_enabled);
        const isSaving = chatCaptureAction?.status === 'saving';
        return (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '20px', maxWidth: '720px' }}>
            <div style={{
              backgroundColor: colors.white, borderRadius: '12px', padding: '24px',
              boxShadow: 'var(--shadow-sm)',
            }}>
              <h2 style={{ fontSize: 'var(--font-size-md)', fontWeight: 'var(--font-weight-semibold)', color: colors.darkText, margin: '0 0 6px' }}>
                Chat Capture
              </h2>
              <p style={{ fontSize: 'var(--font-size-sm)', color: colors.textMuted, margin: '0 0 24px', lineHeight: 1.5 }}>
                When enabled, AI chat messages from users at this company are evaluated for saveable intelligence
                (supplier prices, lead times, capacity constraints, etc.). Captured facts write to the assertions
                table and surface across the platform.
              </p>

              <div style={{
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                padding: '16px 20px', border: `1px solid ${colors.mediumGray}`, borderRadius: '8px',
                backgroundColor: colors.lightGray,
              }}>
                <div>
                  <div style={{ fontSize: 'var(--font-size-base)', fontWeight: 'var(--font-weight-semibold)', color: colors.darkText }}>
                    Chat capture is currently <span style={{ color: isOn ? colors.success : colors.textMuted }}>{isOn ? 'ON' : 'OFF'}</span> for {company?.company_name || 'this company'}
                  </div>
                  <div style={{ fontSize: 'var(--font-size-sm)', color: colors.textMuted, marginTop: '4px' }}>
                    {isOn
                      ? 'Users see a one-time consent banner on their first chat use; users who previously consented won\u2019t be re-prompted.'
                      : 'Toggle on to start evaluating chat messages for this company.'}
                  </div>
                </div>

                <button
                  ref={pillRef}
                  type="button"
                  role="switch"
                  aria-checked={isOn}
                  aria-label={`Chat capture for ${company?.company_name || 'company'}`}
                  disabled={isSaving}
                  onClick={() => openChatCaptureModal(!isOn)}
                  style={{
                    position: 'relative',
                    width: '44px', height: '24px',
                    borderRadius: '12px',
                    border: 'none',
                    padding: 0,
                    backgroundColor: isOn ? colors.accent : colors.mediumGray,
                    cursor: isSaving ? 'not-allowed' : 'pointer',
                    opacity: isSaving ? 0.5 : 1,
                    transition: 'background-color 150ms ease, opacity 150ms ease',
                    flexShrink: 0,
                  }}
                >
                  <span style={{
                    position: 'absolute',
                    top: '3px',
                    left: isOn ? '23px' : '3px',
                    width: '18px', height: '18px',
                    borderRadius: '50%',
                    backgroundColor: colors.white,
                    boxShadow: '0 1px 2px rgba(0,0,0,0.2)',
                    transition: 'left 150ms ease',
                  }} />
                </button>
              </div>
            </div>
          </div>
        );
      })()}

      {/* ── Knowledge Upload Modal ── */}
      <Modal isOpen={showKnowledgeUpload} title="Upload Company Document" size="large" onClose={() => { setShowKnowledgeUpload(false); setKnowledgeForm({ file: null, title: '', source: '', document_date: '', summary: '', relevance_tags: '' }); }}>
          <form onSubmit={handleKnowledgeUpload}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              <div style={{
                padding: '8px 12px', backgroundColor: `${colors.adminPurple}08`, border: `1px solid ${colors.adminPurple}20`,
                borderRadius: '6px', fontSize: 'var(--font-size-sm)', color: colors.darkText, display: 'flex', alignItems: 'center', gap: '8px',
              }}>
                <span style={{display: 'flex', alignItems: 'center'}}><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round"><rect x="4" y="2" width="16" height="20" rx="2" ry="2"/><path d="M9 22V12h6v10"/><line x1="9" y1="6" x2="9.01" y2="6"/><line x1="15" y1="6" x2="15.01" y2="6"/></svg></span>
                <span>This document is specific to <strong>{company.company_name}</strong>.</span>
              </div>

              <div>
                <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'var(--font-weight-medium)', fontSize: 'var(--font-size-sm)' }}>Document File *</label>
                <input type="file" accept=".pdf,.docx,.doc,.txt,.md"
                  onChange={(e) => setKnowledgeForm({ ...knowledgeForm, file: e.target.files[0] })}
                  style={{ width: '100%', padding: '10px', border: `2px dashed ${colors.mediumGray}`, borderRadius: '6px', backgroundColor: colors.lightGray, boxSizing: 'border-box', fontSize: 'var(--font-size-sm)' }}
                />
              </div>

              <FormField label="Title" value={knowledgeForm.title} onChange={(v) => setKnowledgeForm({ ...knowledgeForm, title: v })} placeholder="e.g., 2026 Procurement Strategy" required />

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                <FormField label="Source" value={knowledgeForm.source} onChange={(v) => setKnowledgeForm({ ...knowledgeForm, source: v })} placeholder="e.g., CPO Office" />
                <div>
                  <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'var(--font-weight-medium)', fontSize: 'var(--font-size-base)' }}>Document Date</label>
                  <input type="date" value={knowledgeForm.document_date}
                    onChange={(e) => setKnowledgeForm({ ...knowledgeForm, document_date: e.target.value })}
                    style={{ width: '100%', padding: '12px', border: `1px solid ${colors.mediumGray}`, borderRadius: '8px', fontSize: 'var(--font-size-base)', boxSizing: 'border-box' }}
                  />
                </div>
              </div>

              <FormField label="Key Takeaway / Summary" value={knowledgeForm.summary} onChange={(v) => setKnowledgeForm({ ...knowledgeForm, summary: v })} placeholder="Main insight for the AI" textarea />
              <FormField label="Relevance Tags" value={knowledgeForm.relevance_tags} onChange={(v) => setKnowledgeForm({ ...knowledgeForm, relevance_tags: v })} placeholder="e.g., strategy, cost reduction (comma-separated)" />

              <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end', marginTop: '4px' }}>
                <button type="button" onClick={() => { setShowKnowledgeUpload(false); setKnowledgeForm({ file: null, title: '', source: '', document_date: '', summary: '', relevance_tags: '' }); }}
                  style={{ padding: '10px 20px', backgroundColor: 'transparent', border: `1px solid ${colors.mediumGray}`, borderRadius: '8px', fontSize: 'var(--font-size-base)', cursor: 'pointer' }}>
                  Cancel
                </button>
                <button type="submit" disabled={knowledgeUploading}
                  style={{ padding: '10px 20px', backgroundColor: colors.adminPurple, color: colors.white, border: 'none', borderRadius: '8px', fontSize: 'var(--font-size-base)', fontWeight: 'var(--font-weight-semibold)', cursor: knowledgeUploading ? 'not-allowed' : 'pointer', opacity: knowledgeUploading ? 0.7 : 1 }}>
                  {knowledgeUploading ? 'Uploading...' : 'Upload Document'}
                </button>
              </div>
            </div>
          </form>
      </Modal>

      {/* Edit Company Modal */}
      <Modal
          isOpen={showEditModal}
          title="Edit Company"
          size="medium"
          onClose={() => setShowEditModal(false)}
        >
          <form onSubmit={handleUpdateCompany}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
              <FormField
                label="Company Name"
                value={editForm.company_name}
                onChange={(v) => setEditForm({ ...editForm, company_name: v })}
                required
              />
              <FormField
                label="Industry"
                value={editForm.industry}
                onChange={(v) => setEditForm({ ...editForm, industry: v })}
              />
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                <FormField
                  label="Contact Email"
                  type="email"
                  value={editForm.contact_email}
                  onChange={(v) => setEditForm({ ...editForm, contact_email: v })}
                />
                <FormField
                  label="Contact Phone"
                  value={editForm.contact_phone}
                  onChange={(v) => setEditForm({ ...editForm, contact_phone: v })}
                />
              </div>
              <FormField
                label="Address"
                value={editForm.address}
                onChange={(v) => setEditForm({ ...editForm, address: v })}
                textarea
              />
              <div>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'var(--font-weight-medium)', fontSize: 'var(--font-size-base)' }}>
                  Status
                </label>
                <select
                  value={editForm.status}
                  onChange={(e) => setEditForm({ ...editForm, status: e.target.value })}
                  style={{
                    width: '100%',
                    padding: '12px',
                    border: `1px solid ${colors.mediumGray}`,
                    borderRadius: '8px',
                    fontSize: 'var(--font-size-base)',
                  }}
                >
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                </select>
              </div>
              <div style={{ marginTop: '16px' }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={!!(editForm.settings && editForm.settings.interview_v2_enabled)}
                    onChange={(e) => setEditForm({
                      ...editForm,
                      settings: { ...(editForm.settings || {}), interview_v2_enabled: e.target.checked }
                    })}
                    style={{ width: '18px', height: '18px', accentColor: colors.accent }}
                  />
                  <span style={{ fontSize: 'var(--font-size-base)', fontWeight: 'var(--font-weight-medium)' }}>
                    Enable Interview v2
                  </span>
                </label>
                <p style={{ fontSize: '12px', color: colors.textMuted, marginTop: '4px', marginLeft: '28px' }}>
                  Role-based interview flow with AI-generated questions
                </p>
              </div>
            </div>

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px', marginTop: '24px' }}>
              <button
                type="button"
                onClick={() => setShowEditModal(false)}
                style={{
                  padding: '10px 20px',
                  backgroundColor: 'transparent',
                  border: `1px solid ${colors.mediumGray}`,
                  borderRadius: '8px',
                  fontSize: 'var(--font-size-base)',
                  cursor: 'pointer',
                }}
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={saving}
                style={{
                  padding: '10px 20px',
                  backgroundColor: colors.accent,
                  color: colors.white,
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: 'var(--font-size-base)',
                  fontWeight: 'var(--font-weight-semibold)',
                  cursor: saving ? 'not-allowed' : 'pointer',
                  opacity: saving ? 0.7 : 1,
                }}
              >
                {saving ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          </form>
      </Modal>

      {/* Add User Modal */}
      <Modal
          isOpen={showUserModal}
          title="Add User"
          size="medium"
          onClose={() => { setShowUserModal(false); resetUserForm(); }}
        >
          <form onSubmit={handleCreateUser}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
              <FormField
                label="Full Name"
                value={userForm.full_name}
                onChange={(v) => setUserForm({ ...userForm, full_name: v })}
                placeholder="e.g., John Smith"
                required
              />
              <FormField
                label="Email"
                type="email"
                value={userForm.email}
                onChange={(v) => setUserForm({ ...userForm, email: v })}
                placeholder="user@company.com"
                required
              />
              <FormField
                label="Password"
                type="password"
                value={userForm.password}
                onChange={(v) => setUserForm({ ...userForm, password: v })}
                placeholder="Minimum 8 characters"
                required
              />
              <div>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'var(--font-weight-medium)', fontSize: 'var(--font-size-base)' }}>
                  Role
                </label>
                <select
                  value={userForm.role}
                  onChange={(e) => setUserForm({ ...userForm, role: e.target.value })}
                  style={{
                    width: '100%',
                    padding: '12px',
                    border: `1px solid ${colors.mediumGray}`,
                    borderRadius: '8px',
                    fontSize: 'var(--font-size-base)',
                  }}
                >
                  <option value="company_admin">Company Admin</option>
                  <option value="manager">Manager</option>
                  <option value="user">User</option>
                  <option value="viewer">Viewer</option>
                </select>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                <FormField
                  label="Department"
                  value={userForm.department}
                  onChange={(v) => setUserForm({ ...userForm, department: v })}
                  placeholder="e.g., Procurement"
                />
                <FormField
                  label="Job Title"
                  value={userForm.job_title}
                  onChange={(v) => setUserForm({ ...userForm, job_title: v })}
                  placeholder="e.g., Buyer"
                />
              </div>
            </div>
            
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px', marginTop: '24px' }}>
              <button
                type="button"
                onClick={() => { setShowUserModal(false); resetUserForm(); }}
                style={{
                  padding: '10px 20px',
                  backgroundColor: 'transparent',
                  border: `1px solid ${colors.mediumGray}`,
                  borderRadius: '8px',
                  fontSize: 'var(--font-size-base)',
                  cursor: 'pointer',
                }}
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={creatingUser}
                style={{
                  padding: '10px 20px',
                  backgroundColor: colors.accent,
                  color: colors.white,
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: 'var(--font-size-base)',
                  fontWeight: 'var(--font-weight-semibold)',
                  cursor: creatingUser ? 'not-allowed' : 'pointer',
                  opacity: creatingUser ? 0.7 : 1,
                }}
              >
                {creatingUser ? 'Creating...' : 'Create User'}
              </button>
            </div>
          </form>
      </Modal>

      {/* ===== CREDENTIAL RESULT MODAL (post-create / post-reset) ===== */}
      <Modal isOpen={!!confirmDeleteUser} onClose={() => setConfirmDeleteUser(null)} title="Permanently Delete User" size="small">
        <div>
          <p style={{ color: colors.danger, fontSize: 'var(--font-size-base)', fontWeight: 'var(--font-weight-semibold)', marginBottom: '8px' }}>
            This action cannot be undone.
          </p>
          <p style={{ color: colors.darkText, fontSize: 'var(--font-size-base)', marginBottom: '8px' }}>
            Permanently delete <strong>{confirmDeleteUser?.full_name || confirmDeleteUser?.email}</strong>?
          </p>
          <p style={{ color: colors.textMuted, fontSize: 'var(--font-size-sm)', marginBottom: '24px' }}>
            This will also delete their interview sessions, intelligence entries, and coverage claims. Created records (RFPs, documents) will be preserved with authorship cleared.
          </p>
          <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
            <button onClick={() => setConfirmDeleteUser(null)} style={{
              padding: '8px 16px', backgroundColor: colors.lightGray, border: `1px solid ${colors.mediumGray}`,
              borderRadius: '6px', cursor: 'pointer', fontSize: 'var(--font-size-base)',
            }}>Cancel</button>
            <button onClick={() => handleDeleteUser(confirmDeleteUser?.user_id)} style={{
              padding: '8px 16px', backgroundColor: colors.danger, color: '#FFFFFF',
              border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: 'var(--font-size-base)', fontWeight: 'var(--font-weight-semibold)',
            }}>Delete Permanently</button>
          </div>
        </div>
      </Modal>

      <Modal isOpen={!!credentialResult} onClose={() => setCredentialResult(null)} title="Login Credentials" size="small">
        <div style={{ textAlign: 'center' }}>
          <div style={{
            width: '48px', height: '48px', borderRadius: '12px', backgroundColor: colors.lightGray,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            margin: '0 auto 16px',
          }}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke={colors.adminPurple} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M21 2l-2 2m-7.61 7.61a5.5 5.5 0 1 1-7.778 7.778 5.5 5.5 0 0 1 7.777-7.777zm0 0L15.5 7.5m0 0l3 3L22 7l-3-3m-3.5 3.5L19 4" />
            </svg>
          </div>
          <p style={{ color: colors.textMuted, fontSize: 'var(--font-size-base)', marginBottom: '20px' }}>
            Credentials for <strong>{credentialResult?.email}</strong>
          </p>
          <div style={{
            backgroundColor: colors.lightGray, borderRadius: '8px', padding: '16px',
            fontFamily: 'monospace', fontSize: 'var(--font-size-lg)', fontWeight: 'var(--font-weight-bold)', color: colors.darkText,
            letterSpacing: '1px', marginBottom: '20px', userSelect: 'all',
          }}>
            {credentialResult?.password}
          </div>
          <p style={{ fontSize: 'var(--font-size-sm)', color: colors.warning || '#F59E0B', marginBottom: '20px' }}>
            Save this password. It will not be shown again
          </p>
          <button onClick={() => {
            navigator.clipboard?.writeText(credentialResult?.password);
            setCredentialResult(null);
            setMessage({ type: 'success', text: 'Password copied to clipboard' });
          }} style={{
            padding: '8px 16px', backgroundColor: colors.adminPurple, color: colors.white || '#FFFFFF',
            border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: 'var(--font-size-base)', fontWeight: 'var(--font-weight-semibold)',
          }}>Copy & Close</button>
        </div>
      </Modal>

      {/* ── Chat Capture confirmation modal (D.6) ── */}
      {chatCaptureAction && (() => {
        const isEnable = chatCaptureAction.direction === 'enable';
        const isSaving = chatCaptureAction.status === 'saving';
        const companyName = company?.company_name || 'this company';
        const titlePrefix = isEnable ? 'Enable chat capture for ' : 'Disable chat capture for ';
        const body = isEnable
          ? `Users at ${companyName} will have their AI chat messages evaluated for saveable intelligence (supplier prices, lead times, capacity constraints, etc.). Captured facts write to the assertions table and surface across the platform. Users will see a one-time consent banner the next time they open the chat.`
          : `New messages at ${companyName} will stop being evaluated. Already-captured assertions are preserved. Disabling does not retract them. Users won't see any change to the chat; if you re-enable later, users who previously granted consent won't be re-prompted.`;
        const primaryLabel = isSaving ? 'Saving…' : (isEnable ? 'Enable capture' : 'Disable capture');
        return (
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="chat-capture-modal-title"
            onClick={closeChatCaptureModal}
            style={{
              position: 'fixed', inset: 0, zIndex: 1000,
              backgroundColor: 'rgba(0,0,0,0.4)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              padding: '24px',
            }}
          >
            <div
              onClick={(e) => e.stopPropagation()}
              style={{
                backgroundColor: colors.white, borderRadius: '12px', padding: '24px',
                width: '100%', maxWidth: '480px',
                boxShadow: '0 20px 40px rgba(0,0,0,0.15)',
              }}
            >
              <h2
                id="chat-capture-modal-title"
                style={{
                  fontSize: 'var(--font-size-md)', fontWeight: 'var(--font-weight-semibold)',
                  color: colors.darkText, margin: '0 0 12px', lineHeight: 1.4,
                }}
              >
                {titlePrefix}
                <strong>{companyName}</strong>?
              </h2>
              <p style={{
                fontSize: 'var(--font-size-sm)', color: colors.darkText,
                margin: '0 0 24px', lineHeight: 1.6,
              }}>
                {body}
              </p>
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px' }}>
                <button
                  type="button"
                  autoFocus
                  disabled={isSaving}
                  onClick={closeChatCaptureModal}
                  style={{
                    padding: '8px 16px',
                    backgroundColor: colors.lightGray,
                    border: `1px solid ${colors.mediumGray}`,
                    borderRadius: '6px',
                    cursor: isSaving ? 'not-allowed' : 'pointer',
                    opacity: isSaving ? 0.6 : 1,
                    fontSize: 'var(--font-size-base)',
                    color: colors.darkText,
                  }}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  disabled={isSaving}
                  onClick={confirmChatCaptureToggle}
                  style={{
                    padding: '8px 16px',
                    backgroundColor: isEnable ? colors.accent : '#6B7280',
                    color: colors.white,
                    border: 'none',
                    borderRadius: '6px',
                    cursor: isSaving ? 'not-allowed' : 'pointer',
                    opacity: isSaving ? 0.6 : 1,
                    fontSize: 'var(--font-size-base)',
                    fontWeight: 'var(--font-weight-semibold)',
                  }}
                >
                  {primaryLabel}
                </button>
              </div>
            </div>
          </div>
        );
      })()}
    </div>
  );
}

// Helper Components
function InfoItem({ label, value }) {
  return (
    <div>
      <div style={{ fontSize: 'var(--font-size-sm)', color: colors.textMuted, marginBottom: '4px', textTransform: 'uppercase' }}>
        {label}
      </div>
      <div style={{ fontSize: 'var(--font-size-base)', color: colors.darkText }}>
        {value}
      </div>
    </div>
  );
}

function FormField({ label, value, onChange, type = 'text', placeholder, required, textarea }) {
  const inputStyles = {
    width: '100%',
    padding: '12px',
    border: `1px solid ${colors.mediumGray}`,
    borderRadius: '8px',
    fontSize: 'var(--font-size-base)',
    outline: 'none',
    boxSizing: 'border-box',
  };

  return (
    <div>
      <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'var(--font-weight-medium)', fontSize: 'var(--font-size-base)' }}>
        {label} {required && '*'}
      </label>
      {textarea ? (
        <textarea
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          rows={3}
          style={{ ...inputStyles, resize: 'vertical' }}
        />
      ) : (
        <input
          type={type}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          required={required}
          style={inputStyles}
        />
      )}
    </div>
  );
}

// ═══════ INTELLIGENCE HEAT MAP COMPONENT ═══════

const SUPPLIER_COLUMNS = ['pricing', 'performance', 'sole_source_risk', 'customer_links', 'competition', 'relationship', 'contracts', 'recent_changes'];
const ACCOUNT_COLUMNS = ['supplier_mapping', 'ground_reality', 'competitive_threats', 'growth', 'relationships'];
const CATEGORY_COLUMNS = ['category_strategy', 'spec_flexibility', 'market_intelligence'];

const HEATMAP_TABS = [
  { key: 'supplier', label: 'Suppliers', columns: SUPPLIER_COLUMNS, maxRows: 15 },
  { key: 'account', label: 'Customer Accounts', columns: ACCOUNT_COLUMNS, maxRows: 15 },
  { key: 'category', label: 'Categories', columns: CATEGORY_COLUMNS, maxRows: 50 },
];

// Cell color: green = intelligence exists, red = critical/high open gaps, amber = medium/low open gaps, white = no gap
const getCellColor = (gap) => {
  if (!gap) return { bg: '#FFFFFF', border: '#E5E7EB', text: '#9CA3AF' }; // White — no gap
  if (gap.status === 'closed') return { bg: '#166534', border: '#166534', text: '#fff' }; // Dark green
  if (gap.status === 'partially_answered') return { bg: '#86EFAC', border: '#4ADE80', text: '#166534' }; // Light green
  // Open gaps
  if (gap.priority === 'critical') return { bg: '#991B1B', border: '#991B1B', text: '#fff' }; // Dark red
  if (gap.priority === 'high') return { bg: '#F87171', border: '#EF4444', text: '#fff' }; // Medium red
  if (gap.priority === 'medium') return { bg: '#B45309', border: '#B45309', text: '#fff' }; // Dark amber
  return { bg: '#FDE68A', border: '#FCD34D', text: '#92400E' }; // Light amber (low)
};

const PRIORITY_BADGE_COLORS = { critical: '#991B1B', high: '#F87171', medium: '#B45309', low: '#FDE68A' };

function IntelligenceHeatMap({ gaps, visible, setVisible, selectedCell, setSelectedCell, onStatusUpdate, updatingGapId, formatGapCategory }) {
  const colors = { textMuted: '#767B85', darkText: '#16181D', mediumGray: '#E5E7EB', accent: '#7C3AED' }; // R6: Meridian values + purple admin accent (was #8A94A6/#111827/#E2E5EA/#2251FF)
  const [activeEntityTab, setActiveEntityTab] = React.useState('supplier');

  // Group gaps by entity type
  const gapsByType = {
    supplier: gaps.filter(g => g.entity_type === 'supplier'),
    account: gaps.filter(g => g.entity_type === 'account'),
    category: gaps.filter(g => g.entity_type === 'category'),
  };

  // Priority summary (all gaps)
  const byCritical = gaps.filter(g => g.priority === 'critical' && g.status === 'open').length;
  const byHigh = gaps.filter(g => g.priority === 'high' && g.status === 'open').length;
  const byMedium = gaps.filter(g => g.priority === 'medium' && g.status === 'open').length;
  const byLow = gaps.filter(g => g.priority === 'low' && g.status === 'open').length;
  const byClosed = gaps.filter(g => g.status === 'closed').length;
  const byPartial = gaps.filter(g => g.status === 'partially_answered').length;
  const total = gaps.length;

  // Unique entity counts per type
  const entityCounts = {};
  HEATMAP_TABS.forEach(t => {
    entityCounts[t.key] = new Set(gapsByType[t.key].map(g => g.entity_name)).size;
  });
  const totalEntities = Object.values(entityCounts).reduce((a, b) => a + b, 0);

  // Build grid data
  const buildGrid = (entityGaps, maxRows = 15) => {
    const entityMap = {};
    entityGaps.forEach(g => {
      if (!entityMap[g.entity_name]) entityMap[g.entity_name] = {};
      entityMap[g.entity_name][g.gap_category] = g;
    });
    const sorted = Object.entries(entityMap)
      .map(([name, gapMap]) => ({ name, gapMap, count: Object.keys(gapMap).length }))
      .sort((a, b) => b.count - a.count);
    return { rows: sorted.slice(0, maxRows), totalEntities: sorted.length };
  };

  const fmtCol = (col) => col.split('_').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');

  const cellSize = 40;
  const cellGap = 2;
  const labelWidth = 200;

  // Active tab config
  const activeTabConfig = HEATMAP_TABS.find(t => t.key === activeEntityTab);
  const activeGaps = gapsByType[activeEntityTab] || [];
  const { rows, totalEntities: totalForType } = buildGrid(activeGaps, activeTabConfig.maxRows);

  return (
    <div style={{ backgroundColor: '#fff', borderRadius: '10px', border: `1px solid ${colors.mediumGray}`, padding: '20px', overflow: 'hidden' }}>
      {/* Header + Toggle */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: visible ? '16px' : '0' }}>
        <div style={{ fontSize: '11px', textTransform: 'uppercase', fontWeight: 600, letterSpacing: '0.06em', color: colors.textMuted }}>Intelligence Heat Map</div>
        <button onClick={() => { setVisible(!visible); setSelectedCell(null); }} style={{ fontSize: 'var(--font-size-xs)', color: colors.accent, background: 'none', border: `1px solid ${colors.mediumGray}`, borderRadius: '6px', padding: '4px 10px', cursor: 'pointer' }}>
          {visible ? 'Hide Heat Map' : 'Show Heat Map'}
        </button>
      </div>

      {visible && (
        <>
          {/* Priority Summary Bar */}
          <div style={{ marginBottom: '20px' }}>
            <div style={{ display: 'flex', height: '40px', borderRadius: '8px', overflow: 'hidden', backgroundColor: '#F3F4F6' }}>
              {[
                { label: 'Critical', count: byCritical, color: '#991B1B' },
                { label: 'High', count: byHigh, color: '#F87171' },
                { label: 'Medium', count: byMedium, color: '#B45309' },
                { label: 'Low', count: byLow, color: '#FDE68A', textColor: '#92400E' },
                { label: 'Partial', count: byPartial, color: '#86EFAC', textColor: '#166534' },
                { label: 'Closed', count: byClosed, color: '#166534' },
              ].filter(s => s.count > 0).map(seg => (
                <div key={seg.label} style={{ flex: seg.count, backgroundColor: seg.color, display: 'flex', alignItems: 'center', justifyContent: 'center', minWidth: seg.count > 0 ? '40px' : '0' }}>
                  <span style={{ fontSize: '11px', fontWeight: 600, color: seg.textColor || '#fff' }}>{seg.count} {seg.label}</span>
                </div>
              ))}
            </div>
            <div style={{ fontSize: 'var(--font-size-xs)', color: colors.textMuted, marginTop: '6px' }}>
              {total} gaps across {totalEntities} entities
            </div>
          </div>

          {/* Legend */}
          <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap', marginBottom: '16px', fontSize: '11px', color: colors.textMuted }}>
            {[
              { color: '#166534', label: 'Closed' },
              { color: '#86EFAC', label: 'Partial', border: '#4ADE80' },
              { color: '#991B1B', label: 'Critical' },
              { color: '#F87171', label: 'High' },
              { color: '#B45309', label: 'Medium' },
              { color: '#FDE68A', label: 'Low', border: '#FCD34D' },
              { color: '#FFFFFF', label: 'No gap', border: '#E5E7EB' },
            ].map(item => (
              <div key={item.label} style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                <div style={{ width: '14px', height: '14px', borderRadius: '3px', backgroundColor: item.color, border: `1px solid ${item.border || item.color}` }} />
                <span>{item.label}</span>
              </div>
            ))}
          </div>

          {/* Entity Type Tabs */}
          <div style={{ display: 'flex', borderBottom: `1px solid ${colors.mediumGray}`, marginBottom: '16px' }}>
            {HEATMAP_TABS.map((tab, i) => (
              <button
                key={tab.key}
                onClick={() => { setActiveEntityTab(tab.key); setSelectedCell(null); }}
                style={{
                  padding: '8px 16px',
                  fontSize: 'var(--font-size-sm)',
                  fontWeight: activeEntityTab === tab.key ? 600 : 400,
                  color: activeEntityTab === tab.key ? colors.accent : colors.textMuted,
                  background: 'none',
                  border: 'none',
                  borderBottom: activeEntityTab === tab.key ? `2px solid ${colors.accent}` : '2px solid transparent',
                  cursor: 'pointer',
                  borderRight: i < HEATMAP_TABS.length - 1 ? `1px solid #e5e7eb` : 'none',
                }}
              >
                {tab.label} ({entityCounts[tab.key]})
              </button>
            ))}
          </div>

          {/* Grid */}
          {rows.length > 0 ? (
            <div style={{ overflowX: 'auto' }}>
              <div style={{ display: 'grid', gridTemplateColumns: `${labelWidth}px repeat(${activeTabConfig.columns.length}, 1fr)`, gap: `${cellGap}px`, alignItems: 'center', minWidth: `${labelWidth + activeTabConfig.columns.length * (cellSize + cellGap)}px` }}>
                {/* Column headers */}
                <div />
                {activeTabConfig.columns.map(col => (
                  <div key={col} style={{ fontSize: '10px', fontWeight: 600, color: colors.textMuted, textTransform: 'uppercase', textAlign: 'center', padding: '4px 2px', lineHeight: '1.2' }}>
                    {fmtCol(col)}
                  </div>
                ))}
                {/* Data rows */}
                {rows.map((row) => (
                  <React.Fragment key={row.name}>
                    <div style={{ fontSize: 'var(--font-size-xs)', color: colors.darkText, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', paddingRight: '8px' }} title={row.name}>
                      {row.name.length > 30 ? row.name.substring(0, 30) + '...' : row.name}
                    </div>
                    {activeTabConfig.columns.map(col => {
                      const gap = row.gapMap[col];
                      const cellKey = `${row.name}|${col}`;
                      const isSelected = selectedCell?.key === cellKey;
                      const cellColor = getCellColor(gap);
                      const statusLabel = gap ? (gap.status === 'open' ? gap.priority : gap.status.replace('_', ' ')) : 'No gap';
                      return (
                        <div
                          key={col}
                          onClick={() => gap && setSelectedCell(isSelected ? null : { key: cellKey, gap })}
                          title={`${row.name}: ${fmtCol(col)}, ${gap ? gap.priority : 'N/A'} (${statusLabel})`}
                          style={{
                            height: cellSize,
                            borderRadius: '4px',
                            backgroundColor: cellColor.bg,
                            cursor: gap ? 'pointer' : 'default',
                            border: isSelected ? '2px solid #111827' : `1px solid ${cellColor.border}`,
                            transition: 'border-color 100ms',
                          }}
                        />
                      );
                    })}
                    {/* Detail panel — spans full width */}
                    {selectedCell && selectedCell.gap && selectedCell.key.startsWith(row.name + '|') && (
                      <div style={{ gridColumn: `1 / -1`, padding: '12px 16px', backgroundColor: '#F9FAFB', borderRadius: '8px', border: `1px solid ${colors.mediumGray}`, marginTop: '2px', marginBottom: '4px' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '8px' }}>
                          <div>
                            <span style={{ fontWeight: 600, fontSize: 'var(--font-size-sm)', color: colors.darkText }}>{selectedCell.gap.entity_name}</span>
                            <span style={{ margin: '0 8px', color: colors.textMuted }}>·</span>
                            <span style={{ fontSize: 'var(--font-size-sm)', color: colors.textMuted }}>{formatGapCategory(selectedCell.gap.gap_category)}</span>
                          </div>
                          <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
                            <span style={{ padding: '2px 8px', borderRadius: '4px', fontSize: '11px', fontWeight: 600, backgroundColor: `${PRIORITY_BADGE_COLORS[selectedCell.gap.priority] || '#8A94A6'}20`, color: PRIORITY_BADGE_COLORS[selectedCell.gap.priority] || '#8A94A6' }}>{selectedCell.gap.priority}</span>
                            <span style={{ padding: '2px 8px', borderRadius: '4px', fontSize: '11px', fontWeight: 600, backgroundColor: '#F3F4F6', color: colors.textMuted }}>{formatGapCategory(selectedCell.gap.status)}</span>
                          </div>
                        </div>
                        <div style={{ fontSize: 'var(--font-size-sm)', color: colors.darkText, lineHeight: 1.5, marginBottom: '8px' }}>{selectedCell.gap.evidence || 'No evidence recorded'}</div>
                        {selectedCell.gap.suggested_questions?.length > 0 && (
                          <div style={{ marginBottom: '8px' }}>
                            <div style={{ fontSize: '11px', textTransform: 'uppercase', fontWeight: 600, color: colors.textMuted, marginBottom: '4px' }}>Suggested Questions</div>
                            <ol style={{ margin: 0, paddingLeft: '20px', fontSize: 'var(--font-size-sm)', color: colors.darkText, lineHeight: 1.6 }}>
                              {selectedCell.gap.suggested_questions.map((q, i) => <li key={i}>{q}</li>)}
                            </ol>
                          </div>
                        )}
                        {selectedCell.gap.best_role && <div style={{ fontSize: 'var(--font-size-sm)', color: colors.textMuted, marginBottom: '4px' }}>Best role: <strong>{formatGapCategory(selectedCell.gap.best_role)}</strong></div>}
                        {selectedCell.gap.status !== 'closed' && (
                          <button onClick={(e) => { e.stopPropagation(); onStatusUpdate(selectedCell.gap.id); }} disabled={updatingGapId === selectedCell.gap.id} style={{ padding: '6px 14px', fontSize: 'var(--font-size-sm)', backgroundColor: '#059669', color: '#fff', border: 'none', borderRadius: '6px', cursor: 'pointer', marginTop: '4px' }}>
                            {updatingGapId === selectedCell.gap.id ? 'Updating...' : 'Mark as Closed'}
                          </button>
                        )}
                      </div>
                    )}
                  </React.Fragment>
                ))}
              </div>
            </div>
          ) : (
            <div style={{ padding: '20px', textAlign: 'center', color: colors.textMuted, fontSize: 'var(--font-size-sm)' }}>
              No gaps found for {activeTabConfig.label.toLowerCase()}.
            </div>
          )}
          {totalForType > activeTabConfig.maxRows && (
            <div style={{ fontSize: 'var(--font-size-xs)', color: colors.textMuted, marginTop: '6px' }}>
              Showing top {activeTabConfig.maxRows} of {totalForType}. Full list in table below.
            </div>
          )}
        </>
      )}
    </div>
  );
}

export default CompanyDetail;
